import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";

const ORIGIN_KEY =
  (typeof process !== "undefined" && process.env && (process.env.REACT_APP_ORIGIN_KEY || process.env.NEXT_PUBLIC_ORIGIN_KEY)) || "";

const RANK = { unknown: 0, green: 1, light_green: 2, amber: 3, red: 4, critical_red: 5 };
const RETURN_POST_ARRIVAL_GRACE_HOURS = 2;
const SOURCE_KEYS = ["road", "rail", "tfl", "weather", "flight", "terminal"];
const SOURCE_LABELS = {
  road: "Road",
  rail: "Rail",
  tfl: "TfL",
  weather: "Weather",
  flight: "Flight",
  terminal: "Terminal",
};

const apiRequest = async (url, opts = {}) => {
  const headers = { ...(opts.headers || {}) };
  if (ORIGIN_KEY && !headers["X-Origin-Key"]) headers["X-Origin-Key"] = ORIGIN_KEY;
  try {
    const res = await api.request({
      url,
      method: opts.method || "GET",
      data: opts.body ? JSON.parse(opts.body) : undefined,
      headers,
    });
    return res?.data;
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    const fallback = err?.message || "Request failed";
    const message =
      (typeof data === "object" && (data?.error || data?.message)) ||
      (typeof data === "string" ? data.slice(0, 180) : null) ||
      fallback;
    throw new Error(status ? `${message} (HTTP ${status})` : message);
  }
};

const fmtLevel = (v) => String(v || "unknown").toLowerCase();

const pillClass = (level) => {
  const v = fmtLevel(level);
  if (v === "green") return "pill ok";
  if (v === "light_green") return "pill light";
  if (v === "amber") return "pill warn";
  if (v === "red" || v === "critical_red") return "pill bad";
  return "pill na";
};

const railRealtimeLevel = (value) => {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return "unknown";
  if (v.includes("cancel")) return "red";
  if (v.includes("delay")) return "amber";
  if (v.includes("on time") || v === "ontime" || v === "ok" || v === "normal" || v === "planned" || v === "unknown") {
    return "green";
  }
  return "unknown";
};

const formatDt = (iso) => {
  if (!iso) return "N/A";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleString("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatDurationMins = (secs) => {
  const n = Number(secs);
  if (!Number.isFinite(n)) return "N/A";
  const mins = Math.round(n / 60);
  return `${mins}m`;
};

const formatDeltaMins = (secs) => {
  const n = Number(secs);
  if (!Number.isFinite(n)) return "N/A";
  const mins = Math.round(n / 60);
  return `${mins > 0 ? "+" : ""}${mins}m`;
};

const formatMiles = (meters) => {
  const n = Number(meters);
  if (!Number.isFinite(n)) return "N/A";
  const miles = n / 1609.344;
  return `${Math.round(miles)}mi`;
};

const segmentEndpointsFromRows = (segments = []) => {
  const first = segments[0];
  const last = segments[segments.length - 1];
  const parseEdge = (label, side) => {
    if (!label || !String(label).includes("→")) return null;
    const [from, to] = String(label).split("→").map((v) => v.trim());
    return side === "start" ? from : to;
  };
  return {
    start: parseEdge(first?.display_label, "start") || "Start",
    end: parseEdge(last?.display_label, "end") || "End",
  };
};

const railBoardDisplayTime = (service, mode = "departure") => {
  if (mode === "arrival") {
    return service?.arrival_expected_local || service?.arrival_scheduled_local || service?.expected_time_local || "Live";
  }
  return service?.departure_expected_local || service?.departure_scheduled_local || service?.expected_time_local || service?.std_local || "Live";
};

const railBoardScheduledTime = (service, mode = "departure") => {
  if (mode === "arrival") return service?.arrival_scheduled_local || service?.std_local || null;
  return service?.departure_scheduled_local || service?.std_local || null;
};

const stationDisplayWithCode = (name, code) => {
  const cleanName = String(name || "").trim();
  const cleanCode = String(code || "").trim();
  if (cleanName && cleanCode) return `${cleanName} (${cleanCode})`;
  return cleanName || cleanCode || "Unknown station";
};

const railReasonDetail = (item) => item?.detail_reason || item?.target?.affected_entities?.find?.((entry) => entry?.reason)?.reason || null;

const railAlertExactReason = (item) => {
  const evidence = item?.last_evidence_json || item?.evidence_json || {};
  const incidents = Array.isArray(evidence?.incidents) ? evidence.incidents : [];
  if (incidents[0]?.title) return incidents[0].title;
  const services = Array.isArray(evidence?.services) ? evidence.services : [];
  const topService =
    services
      .filter((service) => service?.is_cancelled || Number.isFinite(Number(service?.delay_mins)))
      .sort((a, b) => {
        const aCancel = a?.is_cancelled ? 1 : 0;
        const bCancel = b?.is_cancelled ? 1 : 0;
        if (bCancel !== aCancel) return bCancel - aCancel;
        return Number(b?.delay_mins || 0) - Number(a?.delay_mins || 0);
      })[0] || null;
  if (topService) {
    const when =
      topService.departure_scheduled_local ||
      topService.arrival_scheduled_local ||
      topService.std_local ||
      topService.expected_time_local ||
      "Live";
    const route = `${topService.origin_crs || topService.origin_name || "?"} → ${topService.destination_crs || topService.destination_name || "?"}`;
    if (topService.is_cancelled) return `${when} ${route} cancelled`;
    if (Number.isFinite(Number(topService.delay_mins))) return `${when} ${route} delayed +${Number(topService.delay_mins)}m`;
  }
  return evidence?.reason_text || null;
};

const airportOpsAvailabilityText = (summary, error) => {
  if (error) return error;
  if (!summary?.last_evaluated_at) return "Airport ops alert feed unavailable.";
  return null;
};

const airportOpsIsAvailable = (summary) => Boolean(summary?.last_evaluated_at);

const addMinutesIso = (iso, minutes) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  return new Date(ms + minutes * 60000).toISOString();
};

const parseTs = (value) => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const alertSortTs = (item) =>
  parseTs(item?.last_observed_at_utc) ||
  parseTs(item?.last_seen_at) ||
  parseTs(item?.last_evaluated_at) ||
  parseTs(item?.opened_at) ||
  0;

const sortAlertsNewestFirst = (items = []) =>
  [...items].sort((a, b) => {
    const sev = (RANK[fmtLevel(b?.current_severity || b?.severity_after)] || 0) - (RANK[fmtLevel(a?.current_severity || a?.severity_after)] || 0);
    if (sev !== 0) return sev;
    return alertSortTs(b) - alertSortTs(a);
  });

const roadAlertExactReason = (item) => {
  const mins = Number(item?.last_metric_value_mins);
  const base = Number.isFinite(mins) ? `Journey time +${Math.round(mins)}m versus baseline` : null;
  const evidence = item?.last_evidence_json || {};
  const nhMatches = Array.isArray(evidence?.nh_matches) ? evidence.nh_matches : [];
  if (nhMatches[0]?.title) return `${base ? `${base}. ` : ""}${nhMatches[0].title}`;
  return base || "Road delay detected against baseline";
};

const nhAlertExactReason = (item) => {
  const evidence = item?.last_evidence_json || {};
  const nhMatches = Array.isArray(evidence?.nh_matches) ? evidence.nh_matches : [];
  const topMatch = nhMatches[0] || {};
  return topMatch?.title || "National Highways incident matched to corridor";
};

const formatHm = (iso) => {
  if (!iso) return "Live";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Live";
  return d.toLocaleTimeString("en-GB", {
    timeZone: "Europe/London",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const flightTimeSummary = (flight) => {
  const parts = [];
  if (flight?.scheduled_utc) parts.push(`sch ${formatHm(flight.scheduled_utc)}`);
  if (flight?.estimated_utc && flight.estimated_utc !== flight.scheduled_utc) parts.push(`est ${formatHm(flight.estimated_utc)}`);
  if (flight?.actual_utc) parts.push(`act ${formatHm(flight.actual_utc)}`);
  return parts.join(" • ");
};

const lhrFlightTimeSummary = (flight) => {
  const parts = [];
  if (flight?.scheduled_local) parts.push(`sch ${formatHm(flight.scheduled_local)}`);
  if (flight?.checkin_start_utc) parts.push(`check-in ${formatHm(flight.checkin_start_utc)}`);
  return parts.join(" • ");
};

const queueTrendText = (trend) => {
  const key = String(trend?.trend || "flat").toLowerCase();
  const delta = Number(trend?.delta_mins);
  if (key === "up") return `up${Number.isFinite(delta) ? ` +${delta}m` : ""}`;
  if (key === "down") return `down${Number.isFinite(delta) ? ` ${delta}m` : ""}`;
  return "flat";
};

const weatherSummaryText = (weather) => {
  if (!weather?.current) return "No airport weather snapshot.";
  const parts = [];
  if (weather.current.temp_c != null) parts.push(`${weather.current.temp_c}C`);
  if (weather.current.weather_text) parts.push(weather.current.weather_text);
  if (weather.current.wind_mps != null) parts.push(`wind ${weather.current.wind_mps}m/s`);
  return parts.join(" • ");
};

const humanizeToken = (value) =>
  String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

const shortSourceLabel = (value) => {
  const key = String(value || "").toLowerCase();
  if (key === "google_routes") return "google";
  if (key === "tomtom_routes") return "tomtom";
  return key || "source";
};

const alertNextCheckAt = (item, fallbackEvaluatedAt = null) =>
  addMinutesIso(item?.last_evaluated_at || item?.last_observed_at_utc || fallbackEvaluatedAt, 5);

const airportOpsExactReason = (item) => {
  const category = String(item?.category || "").toLowerCase();
  const feature = item?.feature_json || {};
  const evidence = item?.evidence_json || {};
  const examples = Array.isArray(evidence?.examples) ? evidence.examples : [];
  const topExample = examples[0] || null;

  if (category === "queue") {
    return feature?.wait_message || `Queue wait ${item?.metric_value || "n/a"} minutes`;
  }

  if (category === "terminal_pressure") {
    const arrivals = feature?.next_hour?.arrivals || 0;
    const departures = feature?.next_hour?.departures || 0;
    const severe = feature?.ops?.severe_60_plus || 0;
    const major = feature?.ops?.major_120_plus || 0;
    return `${arrivals} arrivals and ${departures} departures due in next hour${severe ? `, with ${severe} flights delayed 60m+` : ""}${major ? ` and ${major} delayed 120m+` : ""}`;
  }

  if (category === "arrivals_pressure") {
    const nextHour = feature?.next_hour_flights ?? item?.metric_value;
    return `${nextHour} arrivals due in the next hour`;
  }

  if (category === "departures_pressure") {
    const nextHour = feature?.next_hour_flights ?? item?.metric_value;
    return `${nextHour} departures due in the next hour`;
  }

  if (category === "terminal_ops") {
    const delayed = feature?.delayed_total ?? item?.metric_value;
    const major = feature?.major_120_plus || 0;
    const cancelled = feature?.cancelled || 0;
    const exampleText =
      topExample && Number.isFinite(Number(topExample?.delay_mins))
        ? ` Example: ${topExample.flight_number || topExample.airline_name || "flight"} ${topExample.route_label || ""} +${Number(topExample.delay_mins)}m`.trim()
        : "";
    return `${delayed} flights delayed 60m+${major ? `, including ${major} delayed 120m+` : ""}${cancelled ? `, ${cancelled} cancelled` : ""}.${exampleText}`;
  }

  if (category === "airline_hotspot") {
    const airline = feature?.airline_name || item?.entity_label || "Airline";
    const delayed = feature?.delayed_total ?? item?.metric_value;
    const flights = feature?.flights || 0;
    const major = feature?.major_120_plus || 0;
    const cancelled = feature?.cancelled || 0;
    return `${airline} has ${delayed} flights delayed 60m+ out of ${flights}${major ? `, with ${major} delayed 120m+` : ""}${cancelled ? ` and ${cancelled} cancelled` : ""}`;
  }

  if (category === "origin_hotspot") {
    const origin = feature?.origin_key || item?.entity_label || "Origin";
    const delayed = feature?.delayed_total ?? item?.metric_value;
    const major = feature?.major_120_plus || 0;
    const cancelled = feature?.cancelled || 0;
    return `${origin} has ${delayed} flights delayed 60m+${major ? `, with ${major} delayed 120m+` : ""}${cancelled ? ` and ${cancelled} cancelled` : ""}`;
  }

  if (category === "airport_flights") {
    const delayed = feature?.delayed_total ?? item?.metric_value;
    const major = feature?.major_120_plus || 0;
    const extreme = feature?.extreme_180_plus || 0;
    const cancelled = feature?.cancelled || 0;
    return `${delayed} flights delayed 60m+ across ${item?.airport_code || "airport"}${major ? `, including ${major} delayed 120m+` : ""}${extreme ? ` and ${extreme} delayed 180m+` : ""}${cancelled ? `, ${cancelled} cancelled` : ""}`;
  }

  return feature?.wait_message || feature?.airline_name || feature?.origin_key || `Metric ${item?.metric_value ?? "n/a"}`;
};

const airportOpsMetricText = (item) => {
  const category = String(item?.category || "").toLowerCase();
  const metric = item?.metric_value ?? "n/a";
  if (category === "queue") return `Measured wait ${metric}m`;
  if (category === "terminal_ops" || category === "airport_flights" || category === "airline_hotspot" || category === "origin_hotspot") {
    return `Flights delayed 60m+: ${metric}`;
  }
  if (category === "arrivals_pressure") return `Arrivals in next hour: ${metric}`;
  if (category === "departures_pressure") return `Departures in next hour: ${metric}`;
  if (category === "terminal_pressure") return `Pressure score: ${metric}`;
  return `Measured value ${metric}`;
};

const weatherAlertExactReason = (item) =>
  item?.feature_json?.reason_text || item?.evidence_json?.reason_text || item?.display_name || "Weather condition alert";

const weatherAlertMetricText = (item) => {
  const feature = item?.feature_json || {};
  if (feature?.wind_mps != null) return `Wind ${Number(feature.wind_mps).toFixed(1)}m/s`;
  return `Measured value ${item?.metric_value ?? "n/a"}`;
};

const manualAlertReasonText = (item) => String(item?.reason_text || "").trim() || "Manual incident";

const manualAlertLinksText = (item) => {
  const links = Array.isArray(item?.links_json) ? item.links_json : [];
  if (!links.length) return null;
  return links
    .slice(0, 3)
    .map((link) => `${link.link_type || "related"}: ${link.linked_system || "system"} / ${link.linked_target_key || "target"}`)
    .join(" • ");
};

const roadAlertDescription = () => "Journey time above normal baseline";
const nhAlertDescription = () => "National Highways incident affecting monitored corridor";
const railAlertDescription = () => "Rail disruption or delay on monitored airport path";

const airportOpsDescription = (item) => {
  const category = String(item?.category || "").toLowerCase();
  if (category === "queue") return "Queue wait above normal threshold";
  if (category === "terminal_ops") return "Delayed flights concentrated at terminal";
  if (category === "terminal_pressure") return "Combined terminal flow and delay pressure";
  if (category === "arrivals_pressure") return "Inbound flight wave building into terminal";
  if (category === "departures_pressure") return "Outbound flight wave building from terminal";
  if (category === "airline_hotspot") return "Airline disruption cluster";
  if (category === "origin_hotspot") return "Origin city disruption cluster";
  if (category === "airport_flights") return "Airport-wide flight disruption above baseline";
  return "Operational airport alert";
};

const airportOpsCauseText = (item, linkedManual) => {
  if (linkedManual?.title) return linkedManual.title;
  const category = String(item?.category || "").toLowerCase();
  if (category === "queue") {
    if (item?.evidence_json?.is_data_stale === true) return "Stale queue feed";
    return "Live queue snapshot";
  }
  if (category === "terminal_pressure" || category === "arrivals_pressure" || category === "departures_pressure") return "Flight schedule and live ops mix";
  if (category === "terminal_ops" || category === "airport_flights" || category === "airline_hotspot" || category === "origin_hotspot") return "Live flight disruption";
  return "Operational conditions";
};

const weatherCauseText = (item, linkedManual) => {
  if (linkedManual?.title) return linkedManual.title;
  return item?.feature_json?.weather_text || "Observed weather";
};

const manualCauseText = (item) => {
  if (item?.source_url) {
    try {
      return new URL(item.source_url.startsWith("http") ? item.source_url : `https://${item.source_url}`).hostname;
    } catch {
      return item.source_url;
    }
  }
  return "Manual entry";
};

const AlertGridRow = ({ openedAt, system, specific, description, reason, cause, severity, footer = [], stale = false }) => (
  <div className={`alert-grid-row ${fmtLevel(severity)}${stale ? " stale" : ""}`}>
    <div className="alert-grid-col">
      <div className="alert-grid-label">Time-start</div>
      <div className="alert-grid-value">{formatDt(openedAt)}</div>
    </div>
    <div className="alert-grid-col">
      <div className="alert-grid-label">System</div>
      <div className="alert-grid-value">{system}</div>
    </div>
    <div className="alert-grid-col">
      <div className="alert-grid-label">Specific</div>
      <div className="alert-grid-value">{specific}</div>
    </div>
    <div className="alert-grid-col">
      <div className="alert-grid-label">Description</div>
      <div className="alert-grid-value">{description}</div>
    </div>
    <div className="alert-grid-col">
      <div className="alert-grid-label">Reason</div>
      <div className="alert-grid-value">{reason}</div>
    </div>
    <div className="alert-grid-col">
      <div className="alert-grid-label">Cause</div>
      <div className="alert-grid-value">{cause}</div>
    </div>
    <div className="alert-grid-col alert-grid-col-severity">
      <div className="alert-grid-label">Severity</div>
      <span className={pillClass(severity)}>{String(severity || "unknown").toUpperCase()}</span>
    </div>
    {footer.length ? (
      <div className="alert-grid-footer">
        {footer.filter(Boolean).map((value, idx) => (
          <span key={`${openedAt || "alert"}-meta-${idx}`}>{value}</span>
        ))}
      </div>
    ) : null}
  </div>
);

const trackingSignalPillClass = (value) => {
  const key = String(value || "unknown").toLowerCase();
  if (key === "delayed_signal") return "pill bad";
  if (key === "on_time_signal") return "pill ok";
  if (key === "early_signal") return "pill light";
  return "pill na";
};

const trackingConsistencyPillClass = (value) => {
  const key = String(value || "unknown").toLowerCase();
  if (key === "consistent") return "pill ok";
  if (key === "insufficient_tracking") return "pill na";
  return "pill warn";
};

const trackingDirectionBucket = (item = {}) => {
  const phase = String(item?.flight_phase || "").toLowerCase();
  const basis = String(item?.timing_signal_basis || "").toLowerCase();
  const route = String(item?.route_label || "").toLowerCase();
  if (phase.includes("desc") || phase.includes("approach") || basis.includes("arrival") || route.includes("lhr")) return "toward";
  if (phase.includes("climb") || phase.includes("depart") || phase.includes("cruise")) return "away";
  return "unknown";
};

const formatTrackingDelta = (mins) => {
  const n = Number(mins);
  if (!Number.isFinite(n)) return "N/A";
  return `${n > 0 ? "+" : ""}${Math.round(n)}m`;
};

const minutesUntil = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.round((t - Date.now()) / 60000);
};

const relativeWithDot = (iso, kind = "generic") => {
  if (!iso) return { text: "N/A", cls: "na" };
  const diffMins = minutesUntil(iso);
  if (!Number.isFinite(diffMins)) return { text: "N/A", cls: "na" };
  const abs = formatDt(iso);
  if (diffMins < 0) {
    if (kind === "check") return { text: `Check overdue (${Math.abs(diffMins)}m) (${abs})`, cls: "bad" };
    return { text: `overdue by ${Math.abs(diffMins)}m (${abs})`, cls: "bad" };
  }
  if (diffMins <= 30) return { text: `in ${diffMins}m (${abs})`, cls: "warn" };
  const h = Math.floor(diffMins / 60);
  const m = diffMins % 60;
  const rel = h > 0 ? `in ${h}h ${m}m` : `in ${m}m`;
  return { text: `${rel} (${abs})`, cls: "ok" };
};

const sinceText = (iso) => {
  if (!iso) return "N/A";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "N/A";
  const diffMins = Math.round((Date.now() - t) / 60000);
  if (diffMins <= 0) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const h = Math.floor(diffMins / 60);
  const m = diffMins % 60;
  return m > 0 ? `${h}h ${m}m ago` : `${h}h ago`;
};

const checkpointStats = (row) => {
  const checkpoints = row?.checkpoint_status || {};
  const slots = Object.keys(checkpoints);
  const total = slots.length;
  let done = 0;
  let latestTs = null;
  for (const slot of slots) {
    const c = checkpoints[slot] || {};
    const ts = c.check_done_at || c.trigger_done_at || c.notify_done_at || null;
    if (c.check_done_at || c.trigger_done_at) done += 1;
    if (ts && (!latestTs || new Date(ts).getTime() > new Date(latestTs).getTime())) latestTs = ts;
  }
  return { done, total, latestTs };
};

const phaseLabel = (row) => {
  const h = Number(row?.hours_to_flight);
  const flow = String(row?.flow || "").toLowerCase();
  const skipped = String(row?.skipped_reason || "").toLowerCase();
  if (skipped === "outside_watch_window") return "Outside watch window";
  if (skipped === "past_stop_window") return flow === "return" ? "Past return window" : "Post flight";
  if (skipped === "post_departure_stop") return "Post flight";
  if (skipped === "no_flight_anchor") return "Anchor unresolved";
  if (skipped === "schedule_missing") return "Schedule missing";
  if (flow === "return" && Number.isFinite(h) && h < 0 && h >= -RETURN_POST_ARRIVAL_GRACE_HOURS) {
    const postHours = Math.abs(h);
    return `Return grace (+${postHours.toFixed(1)}h)`;
  }
  if (Number.isFinite(h)) {
    if (h < 0) return "Post flight";
    if (h <= 7) return "Hyper (T-7..T-0)";
    if (h <= 12) return "T-12 to T-8";
    if (h <= 24) return "T-24 to T-12";
    if (h <= 48) return "T-48 to T-24";
    return ">T-48";
  }
  const bucket = String(row?.phase_bucket || "").toLowerCase();
  if (bucket === "post_flight") return "Post flight";
  if (bucket === ">48") return ">T-48";
  if (bucket === "24-48") return "T-48 to T-24";
  if (bucket === "12-24") return "T-24 to T-12";
  if (bucket === "8-12") return "T-12 to T-8";
  if (bucket === "0-8") return "Hyper (T-7..T-0)";
  return "Out of window";
};

const monitoringPhaseLabel = (v) => {
  const x = String(v || "unknown").toLowerCase();
  if (x === "pre_boarding") return "PRE (before leave)";
  if (x === "in_transit") return "DURING (in journey)";
  if (x === "post_rail_window" || x === "post_road_window") return "POST (advisory)";
  return x.toUpperCase();
};

const isReturnGraceWindow = (row) => {
  const flow = String(row?.flow || "").toLowerCase();
  const h = Number(row?.hours_to_flight);
  return flow === "return" && Number.isFinite(h) && h < 0 && h >= -RETURN_POST_ARRIVAL_GRACE_HOURS;
};

const sourceState = (row, key) => {
  const fallback = key === "flight" ? row?.anchor_status : null;
  const v = fmtLevel(row?.source_levels?.[key] || fallback);
  if (v && v !== "unknown") return { text: v.toUpperCase(), className: pillClass(v), level: v };
  if (key === "tfl" && !row?.monitor_targets?.tfl_relevant) return { text: "N/A", className: "pill na", level: "unknown" };
  const skipped = String(row?.skipped_reason || "").toLowerCase();
  if (skipped === "outside_watch_window") return { text: "OUTSIDE WINDOW", className: "pill na", level: "unknown" };
  if (skipped === "past_stop_window" || skipped === "post_departure_stop") {
    return { text: "POST WINDOW", className: "pill na", level: "unknown" };
  }
  if (skipped === "no_flight_anchor") return { text: "ANCHOR N/A", className: "pill na", level: "unknown" };
  if ((row?.phase_bucket === "post_flight" || !row?.next_check_due_at) && !isReturnGraceWindow(row)) {
    return { text: "OUT OF WINDOW", className: "pill na", level: "unknown" };
  }
  if (!row?.last_monitor_at) return { text: "PENDING", className: "pill na", level: "unknown" };
  return { text: "PENDING", className: "pill na", level: "unknown" };
};

const overallFromRow = (row) => {
  const levels = SOURCE_KEYS.map((k) => sourceState(row, k).level).filter((x) => x && x !== "unknown");
  if (!levels.length) return "unknown";
  return levels.sort((a, b) => RANK[b] - RANK[a])[0];
};

const overallFromExecution = (exec, row) => {
  const direct = fmtLevel(exec?.overall_level);
  if (direct !== "unknown") return direct;
  const summary = fmtLevel(exec?.summary?.overall_level);
  if (summary !== "unknown") return summary;
  const bySources = [
    fmtLevel(exec?.road_level),
    fmtLevel(exec?.rail_level),
    fmtLevel(exec?.tfl_level),
    fmtLevel(exec?.weather_level),
  ].filter((v) => v !== "unknown");
  if (bySources.length) return bySources.sort((a, b) => (RANK[b] || 0) - (RANK[a] || 0))[0];
  return overallFromRow(row);
};

const overallWhy = (row) => {
  const ranked = SOURCE_KEYS.map((k) => ({ key: k, ...sourceState(row, k) })).filter((x) => x.level !== "unknown");
  if (!ranked.length) return "No live source levels yet.";
  ranked.sort((a, b) => RANK[b.level] - RANK[a.level]);
  const top = ranked[0];
  const peers = ranked.filter((x) => x.level === top.level);
  const left = peers.map((x) => `${SOURCE_LABELS[x.key]}: ${x.text}`).join(", ");
  return `Overall ${top.text} because ${left}.`;
};

const deriveAction = (row) => {
  const overall = overallFromRow(row);
  const nextCheck = relativeWithDot(row?.next_check_due_at, "check");
  if (overall === "critical_red" || overall === "red") return "Manual review";
  if (overall === "amber") return "Notify user (issue detected)";
  if (nextCheck.cls === "bad") return "Recompute plan (check overdue)";
  if (overall === "light_green") return "Monitor closely";
  return "No action";
};

const actionReason = (row) => {
  const overall = overallFromRow(row);
  const overdue = relativeWithDot(row?.next_check_due_at, "check").cls === "bad";
  if (overall === "critical_red" || overall === "red") return "Critical/high risk severity detected.";
  if (overall === "amber") return overallWhy(row);
  if (overdue) return "Next scheduled check is overdue.";
  if (overall === "light_green") return "Minor variation detected; monitoring continues.";
  return "All monitored sources currently stable.";
};

const actionRank = (action) => {
  const s = String(action || "").toLowerCase();
  if (s.includes("manual review")) return 4;
  if (s.includes("notify user")) return 3;
  if (s.includes("recompute")) return 2;
  if (s.includes("monitor")) return 1;
  return 0;
};

const shortTargets = (arr, max = 3) => {
  const items = Array.isArray(arr) ? arr : [];
  if (items.length <= max) return items.join(", ") || "N/A";
  return `${items.slice(0, max).join(", ")} +${items.length - max} more`;
};

const flightAnchorTs = (row) => {
  const t = new Date(row?.anchor_utc || "").getTime();
  if (Number.isFinite(t)) return t;
  const fallback = new Date(row?.last_monitor_at || "").getTime();
  return Number.isFinite(fallback) ? fallback : -Infinity;
};

const notificationDisplayDecision = (notification) => {
  const n = notification || {};
  const decision = String(n?.decision || "").toLowerCase();
  const notifyOutcome = String(n?.notify_outcome || "").toLowerCase();
  const notifyReason = String(n?.notify_decision_reason || "").toLowerCase();
  if (
    decision === "suppressed" &&
    (notifyOutcome === "pending_review" || notifyReason === "draft_pending_review")
  ) {
    return "pending";
  }
  return decision;
};

const notificationPillClass = (notification) => {
  const d = notificationDisplayDecision(notification);
  if (d === "sent") return "ok";
  if (d === "pending") return "warn";
  if (d === "cancelled" || d === "superseded") return "na";
  if (d === "failed") return "bad";
  if (d === "skipped" || d === "suppressed") return "warn";
  return "na";
};

const notificationDisplayAt = (notification) => {
  const n = notification || {};
  const decision = notificationDisplayDecision(n);
  if (decision === "sent" && n?.sent_at) return n.sent_at;
  if (decision === "cancelled" && n?.cancelled_at) return n.cancelled_at;
  if ((decision === "pending" || decision === "superseded") && n?.reviewed_at) return n.reviewed_at;
  return n?.created_at || null;
};

const describeNotificationLifecycle = (notification) => {
  const n = notification || {};
  const draft = n?.payload?.email_draft || {};
  const amendments = Array.isArray(draft?.amendments) ? draft.amendments : [];
  const amended = amendments.length > 0;
  const decision = notificationDisplayDecision(n);
  const sentFrom = String(draft?.sent_from || "").toLowerCase();
  const lastAction = String(draft?.last_action || "").toLowerCase();
  const parts = [];

  if (n?.notify_rule) parts.push(n.notify_rule);
  if (Number.isFinite(Number(n?.slot_hours))) parts.push(`T-${Number(n.slot_hours)}`);

  if (decision === "pending") {
    parts.push(amended ? "Draft amended" : "Draft ready");
    if (draft?.auto_send_at || n?.send_after_at) parts.push(`Auto-send ${formatDt(draft?.auto_send_at || n?.send_after_at)}`);
    return parts;
  }
  if (decision === "sent") {
    parts.push(sentFrom === "manual_send" ? "Manually sent" : sentFrom === "auto_window_expiry" ? "Auto-sent" : "Sent");
    if (amended || lastAction === "draft_amended") parts.push("Amended before send");
    return parts;
  }
  if (decision === "cancelled") {
    parts.push(amended ? "Draft amended then cancelled" : "Draft cancelled");
    return parts;
  }
  if (decision === "superseded") {
    parts.push("Draft superseded");
    return parts;
  }
  if (n?.notify_outcome) parts.push(String(n.notify_outcome).replace(/_/g, " "));
  if (n?.notify_decision_reason) parts.push(String(n.notify_decision_reason).replace(/_/g, " "));
  if (n?.notify_error_class) {
    const errorMessage = String(n?.notify_error_message || "").trim();
    parts.push(errorMessage ? `err:${n.notify_error_class} (${errorMessage})` : `err:${n.notify_error_class}`);
  }
  return parts.filter(Boolean);
};

const describeSentEmailSnapshot = (notification, fallbackSummary) => {
  const n = notification || null;
  const draft = n?.payload?.email_draft || {};
  const amendments = Array.isArray(draft?.amendments) ? draft.amendments : [];
  const amended = amendments.length > 0;
  if (n?.notification_id) {
    return {
      source: "last sent email snapshot",
      delivery: String(draft?.sent_from || "").toLowerCase() === "manual_send" ? "manually sent" : "auto-sent after hold",
      lifecycle: amended ? "amended before send" : "sent as drafted",
      notifyOutcome: "sent",
      notifyReason: n?.reason || "email_sent",
    };
  }
  if (fallbackSummary) {
    return {
      source: "current summary (not yet sent)",
      delivery: "not sent",
      lifecycle: "summary only",
      notifyOutcome: fallbackSummary?.notify_outcome || "N/A",
      notifyReason: fallbackSummary?.notify_decision_reason || "N/A",
    };
  }
  return {
    source: "N/A",
    delivery: "N/A",
    lifecycle: "N/A",
    notifyOutcome: "N/A",
    notifyReason: "N/A",
  };
};

const flightStatusTitle = (row) => {
  const status = String(row?.source_levels?.flight || row?.anchor_status || "").trim();
  if (!status) return "Flight status unavailable.";
  if (row?.last_monitor_at) {
    return `Flight status: ${status}. Last monitor run: ${formatDt(row.last_monitor_at)} (${sinceText(row.last_monitor_at)}).`;
  }
  return `Flight status: ${status}. Source fallback: schedule/anchor snapshot.`;
};

const sourceShort = (s) => {
  const v = String(s || "").toLowerCase();
  if (v === "google") return "G";
  if (v === "nr") return "NR";
  if (v === "national_highways") return "NH";
  if (v === "raildata") return "RD";
  if (v === "open_meteo") return "WX";
  if (v === "tfl") return "TfL";
  if (v === "osrm") return "OSRM";
  if (v.includes("flight")) return "FLT";
  if (v === "monitor_runner") return "RUN";
  return String(s || "").slice(0, 4).toUpperCase();
};

const matrixCellClass = (cell = {}) => {
  const level = fmtLevel(cell?.level);
  if (level && level !== "unknown") return pillClass(level);
  const state = String(cell?.state || "").toLowerCase();
  if (state === "done") return "pill ok";
  if (state === "due") return "pill warn";
  if (state === "failed") return "pill bad";
  if (state === "skipped" || state === "suppressed") return "pill na";
  return "pill na";
};

const matrixCellTitle = (cell = {}) => {
  const parts = [];
  if (cell?.state) parts.push(`state=${cell.state}`);
  if (cell?.level && cell.level !== "unknown") parts.push(`level=${cell.level}`);
  if (cell?.decision) parts.push(`decision=${cell.decision}`);
  if (cell?.reason_code) parts.push(`reason=${cell.reason_code}`);
  return parts.join(" • ") || "No detail";
};

const projectionCardStatus = (modeKey, card = {}) => {
  if (modeKey === "train") return String(card?.display_level || card?.status || card?.raw_level || "unknown").toLowerCase();
  return String(card?.road_status_level || card?.display_level || card?.status || "unknown").toLowerCase();
};

const compactProjectionSummary = (hero = {}, projection = {}) => {
  const flight = hero?.flight_line || projection?.flight_number || "Journey";
  const status = String(hero?.journey_status || projection?.overall_level || "unknown").replace(/_/g, " ");
  const rec = projection?.recommended_mode ? `Recommended: ${String(projection.recommended_mode).replace(/_/g, " ")}` : null;
  return [flight, status, rec].filter(Boolean).join(" • ");
};

function renderProjectionTimeline(timeline = {}) {
  const steps = asArray(timeline?.steps);
  if (!steps.length) return <div className="muted" style={{ marginTop: 6 }}>No timeline steps.</div>;
  return (
    <div style={{ overflowX: "auto", paddingBottom: 4 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "stretch", minWidth: Math.max(steps.length * 180, 720) }}>
        {steps.map((step, idx) => {
          const statusReason = String(step?.status_reason || "").trim();
          const notes = [];
          for (const rawNote of asArray(step?.notes)) {
            const note = String(rawNote || "").trim();
            if (!note) continue;
            if (statusReason && note === statusReason) continue;
            if (!notes.includes(note)) notes.push(note);
          }
          return (
          <React.Fragment key={`${timeline.mode || "mode"}-${step.id || idx}-${step.at || idx}`}>
            <div
              style={{
                minWidth: 170,
                border: "1px solid #203761",
                borderRadius: 10,
                padding: 10,
                background: "#0a1a37",
              }}
            >
              <div style={{ fontSize: 12, color: "#9db0d3", marginBottom: 6 }}>{formatDt(step?.at)}</div>
              <div style={{ marginBottom: 6, fontWeight: 700 }}>{step?.title || "Step"}</div>
              <div style={{ marginBottom: 6 }}>
                <span className={pillClass(step?.status || "unknown")}>{String(step?.status || "unknown").toUpperCase()}</span>
              </div>
              {Number.isFinite(Number(step?.duration_mins)) && (
                <div className="muted" style={{ marginBottom: 4 }}>{`${Number(step.duration_mins)} mins`}</div>
              )}
              {statusReason ? <div className="muted" style={{ marginBottom: 4 }}>{statusReason}</div> : null}
              {notes.slice(0, 3).map((note, noteIdx) => (
                <div key={`${step.id || idx}-note-${noteIdx}`} className="muted">{String(note || "")}</div>
              ))}
            </div>
            {idx < steps.length - 1 ? (
              <div style={{ alignSelf: "center", color: "#4f6ea4", fontSize: 22, paddingTop: 8 }}>→</div>
            ) : null}
          </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function renderCustomerViewPanel({ row, detail, onDraftAction, draftActionBusy = false }) {
  const projection = detail?.projection;
  if (!projection) {
    return <div className="muted" style={{ marginTop: 8 }}>Customer projection not available yet.</div>;
  }
  const hero = projection?.hero || {};
  const cards = projection?.cards || {};
  const timelines = projection?.timelines || {};
  const cache = detail?.projection_cache || {};
  const searchNowRefresh = detail?.search_now_refresh || {};
  const latestSentSummary = detail?.last_sent_notification?.payload?.summary || null;
  const currentSummary = asArray(detail?.executions)[0]?.summary || null;
  const emailSummary = latestSentSummary || currentSummary || null;
  const emailSnapshotMeta = describeSentEmailSnapshot(detail?.last_sent_notification || null, currentSummary || null);
  const pendingNotification = detail?.pending_notification || null;
  const pendingDraft = pendingNotification?.payload?.email_draft || null;
  const pendingFields = pendingDraft?.current?.fields || pendingDraft?.generated?.fields || null;
  const modeOrder = ["train", "self_drive", "taxi"];

  return (
    <details className="detail-collapsible" style={{ marginTop: 10 }}>
      <summary>Customer view</summary>
      <div style={{ marginTop: 10, display: "grid", gap: 12 }}>
        <div style={{ border: "1px solid #203761", borderRadius: 12, padding: 12, background: "#0a1a37" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 800 }}>{hero?.title || "Live Travel Recommendation"}</div>
              <div className="muted" style={{ marginTop: 4 }}>{compactProjectionSummary(hero, projection)}</div>
            </div>
            <span className={pillClass(hero?.journey_status || projection?.overall_level || "unknown")}>
              {String(hero?.journey_status || projection?.overall_level || "unknown").toUpperCase()}
            </span>
          </div>
          {hero?.recommendation_summary ? <div style={{ marginTop: 8 }}>{hero.recommendation_summary}</div> : null}
          <div className="muted" style={{ marginTop: 8 }}>
            {[
              hero?.flight_status ? `Flight: ${hero.flight_status}` : null,
              hero?.weather_line ? `Weather: ${hero.weather_line}` : null,
              row?.email_verified ? "Verified journey" : "Unverified journey",
            ].filter(Boolean).join(" • ")}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
          {modeOrder.map((modeKey) => {
            const card = cards?.[modeKey];
            if (!card) return null;
            const badgeLevel = projectionCardStatus(modeKey, card);
            return (
              <div key={modeKey} style={{ border: "1px solid #203761", borderRadius: 12, padding: 12, background: "#0a1a37" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                  <div style={{ fontWeight: 700 }}>{card?.title || modeKey}</div>
                  <span className={pillClass(badgeLevel)}>{String(badgeLevel).toUpperCase()}</span>
                </div>
                {card?.recommended ? <div className="muted" style={{ marginTop: 4 }}>Recommended</div> : null}
                {card?.cost_text ? <div style={{ marginTop: 8 }}>{card.cost_text}</div> : null}
                {card?.duration_text ? <div>{card.duration_text}</div> : null}
                {card?.terminal_margin_note ? <div className="muted" style={{ marginTop: 6 }}>{card.terminal_margin_note}</div> : null}
                {card?.insight ? <div style={{ marginTop: 8 }}>{card.insight}</div> : null}
                {card?.secondary_insight ? <div className="muted" style={{ marginTop: 4 }}>{card.secondary_insight}</div> : null}
              </div>
            );
          })}
        </div>

        <div style={{ border: "1px solid #203761", borderRadius: 12, padding: 12, background: "#0a1a37" }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Timelines</div>
          {modeOrder.map((modeKey) => {
            const timeline = timelines?.[modeKey];
            if (!timeline?.available) return null;
            return (
              <div key={`timeline-${modeKey}`} style={{ marginTop: 10 }}>
                <div style={{ marginBottom: 6, fontWeight: 700 }}>{cards?.[modeKey]?.title || modeKey}</div>
                {renderProjectionTimeline(timeline)}
              </div>
            );
          })}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
          <div style={{ border: "1px solid #203761", borderRadius: 12, padding: 12, background: "#0a1a37" }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Cache status</div>
            <div>{`Search now: ${searchNowRefresh?.response_mode === "cached_projection" ? "cached projection" : "fresh rebuild likely"}`}</div>
            <div className="muted">{`Serving layer: ${searchNowRefresh?.cache_source_of_truth || cache?.cache_source_of_truth || "N/A"}`}</div>
            <div className="muted">{`Policy bucket: ${searchNowRefresh?.cache_policy_bucket || cache?.cache_policy_bucket || "N/A"}`}</div>
            <div className="muted">{`Verified policy applied: ${(searchNowRefresh?.verified_policy_applied || cache?.verified_policy_applied) ? "yes" : "no"}`}</div>
            <div className="muted">{`Fresh source calls on search now: ${searchNowRefresh?.fresh_source_calls_on_search_now ? "likely yes" : "no"}`}</div>
            <div className="muted">{`Reason: ${searchNowRefresh?.fresh_source_calls_reason || "N/A"}`}</div>
            <div className="muted">{`Projection built: ${formatDt(cache?.projection_updated_at)}`}</div>
            <div className="muted">{`Source evaluated: ${formatDt(cache?.source_evaluated_at)}`}</div>
            <div className="muted">{`Search cache serves until: ${formatDt(searchNowRefresh?.cache_serves_until || cache?.cache_serves_until)}`}</div>
            <div className="muted">{`Stored evaluation TTL until: ${formatDt(cache?.ttl_expires_at)}`}</div>
            <div className="muted">
              {searchNowRefresh?.fresh_source_calls_on_search_now
                ? `NR / Google / live-source checks would run fresh now (search cache expired or missing).`
                : `NR / Google / live-source checks should stay cached until ${formatDt(searchNowRefresh?.cache_serves_until || cache?.cache_serves_until)}.`}
            </div>
            <div className="muted">{`Last full source refresh: ${formatDt(searchNowRefresh?.last_full_source_refresh_at)}`}</div>
            <div className="muted">{`Cache key: ${cache?.source_eval_cache_key || "N/A"}`}</div>
            <div className="muted" style={{ marginTop: 8 }}>
              {`If rebuild now: NR ${searchNowRefresh?.sources?.nr_free?.likely_refresh_on_rebuild ? "yes" : "no"} • Google ${searchNowRefresh?.sources?.google_road?.likely_refresh_on_rebuild ? "yes" : "no"} • NH ${searchNowRefresh?.sources?.nh_free?.likely_refresh_on_rebuild ? "yes" : "no"} • TfL ${searchNowRefresh?.sources?.tfl_free?.likely_refresh_on_rebuild ? "yes" : "no"} • TomTom ${searchNowRefresh?.sources?.tomtom_debug?.likely_refresh_on_rebuild ? "yes" : "no"} • Live board ${searchNowRefresh?.sources?.live_board?.likely_refresh_on_rebuild ? "yes" : "no"}`}
            </div>
          </div>
          <div style={{ border: "1px solid #203761", borderRadius: 12, padding: 12, background: "#0a1a37" }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Email snapshot</div>
            {emailSummary ? (
              <div style={{ display: "grid", gap: 4 }}>
                <div>{`Source: ${emailSnapshotMeta.source}`}</div>
                <div className="muted">{`Overall: ${String(emailSummary?.overall_level || "unknown").toUpperCase()}`}</div>
                <div className="muted">{`Recommended mode: ${emailSummary?.recommended_mode || "N/A"}`}</div>
                <div className="muted">{`Flight: ${emailSummary?.flight_status || "N/A"}`}</div>
                <div className="muted">{`Road: ${emailSummary?.road_monitoring_level || "N/A"}`}</div>
                <div className="muted">{`Rail: ${emailSummary?.rail_monitoring_level || "N/A"}`}</div>
                <div className="muted">{`Weather: ${emailSummary?.weather_level || "N/A"}`}</div>
                <div className="muted">{`Delivery: ${emailSnapshotMeta.delivery}`}</div>
                <div className="muted">{`Lifecycle: ${emailSnapshotMeta.lifecycle}`}</div>
                <div className="muted">{`Notify outcome: ${emailSnapshotMeta.notifyOutcome}`}</div>
                <div className="muted">{`Notify reason: ${emailSnapshotMeta.notifyReason}`}</div>
                <div className="muted">{`Next notify: ${formatDt(emailSummary?.next_notify_due_at || row?.next_notification_due_at)}`}</div>
              </div>
            ) : (
              <div className="muted">No email summary values available.</div>
            )}
          </div>
        </div>

        {pendingNotification && pendingFields ? (
          <div style={{ border: "1px solid #203761", borderRadius: 12, padding: 12, background: "#0a1a37" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ fontWeight: 700 }}>Pending email draft</div>
              <span className="muted">{`Auto-send at ${formatDt(pendingDraft?.auto_send_at || pendingNotification?.send_after_at)}`}</span>
            </div>
            <div className="muted" style={{ marginTop: 6 }}>{`Created ${formatDt(pendingDraft?.created_at || pendingNotification?.created_at)} • Hold window ${pendingDraft?.hold_window_mins || 5} mins`}</div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
              <div>
                <div>{`Overall: ${String(pendingFields?.overall_status || "unknown").toUpperCase()}`}</div>
                <div className="muted">{`Flight: ${pendingFields?.flight_status || "N/A"}`}</div>
                <div className="muted">{`Road: ${pendingFields?.road_status || "N/A"}`}</div>
                <div className="muted">{`Rail: ${pendingFields?.rail_status || "N/A"}`}</div>
                <div className="muted">{`TfL: ${pendingFields?.tfl_status || "N/A"}`}</div>
                <div className="muted">{`Weather: ${pendingFields?.weather_status || "N/A"}`}</div>
              </div>
              <div>
                {pendingFields?.road_detail ? <div className="muted">{`Road detail: ${pendingFields.road_detail}`}</div> : null}
                {pendingFields?.road_alternate ? <div className="muted">{`Road alternate: ${pendingFields.road_alternate}`}</div> : null}
                {pendingFields?.rail_detail ? <div className="muted">{`Rail detail: ${pendingFields.rail_detail}`}</div> : null}
                {pendingFields?.rail_alternate ? <div className="muted">{`Rail alternate: ${pendingFields.rail_alternate}`}</div> : null}
                <div className="muted">{`Issue summary: ${pendingFields?.issue_summary || "N/A"}`}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              <button
                type="button"
                disabled={draftActionBusy}
                onClick={() => onDraftAction?.(pendingNotification.notification_id, "send")}
              >
                Send now
              </button>
              <button
                type="button"
                disabled={draftActionBusy}
                onClick={() => onDraftAction?.(pendingNotification.notification_id, "cancel")}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={draftActionBusy}
                onClick={() => {
                  const nextIssueSummary = window.prompt("Issue summary", pendingFields?.issue_summary || "");
                  if (nextIssueSummary == null) return;
                  const nextRoadDetail = window.prompt("Road detail", pendingFields?.road_detail || "");
                  if (nextRoadDetail == null) return;
                  const nextRailDetail = window.prompt("Rail detail", pendingFields?.rail_detail || "");
                  if (nextRailDetail == null) return;
                  onDraftAction?.(pendingNotification.notification_id, "amend", {
                    issue_summary: nextIssueSummary,
                    road_detail: nextRoadDetail,
                    rail_detail: nextRailDetail,
                  });
                }}
              >
                Amend
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </details>
  );
}

const renderCheckMatrix = (detail) => {
  const matrix = detail?.matrix;
  const columns = asArray(matrix?.columns);
  const rows = asArray(matrix?.rows);
  if (!columns.length || !rows.length) {
    return <div className="muted" style={{ marginTop: 6 }}>Matrix unavailable.</div>;
  }
  return (
    <div style={{ marginTop: 10 }}>
      <div className="muted" style={{ marginBottom: 6 }}>
        Full check matrix: scheduled slots and attached runs, using observations/source calls/notifications as the source of truth.
      </div>
      <div style={{ overflowX: "auto", border: "1px solid #203761", borderRadius: 10 }}>
        <table style={{ width: "100%", minWidth: 1200, borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ position: "sticky", left: 0, background: "#0f2348", zIndex: 2, minWidth: 160, textAlign: "left", padding: 8, borderBottom: "1px solid #203761" }}>
                Slot / Run
              </th>
              {columns.map((column) => (
                <th key={column.column_key} style={{ background: "#0f2348", textAlign: "center", padding: 8, borderBottom: "1px solid #203761", minWidth: 88 }}>
                  <div>{column.label}</div>
                  <div className="muted" style={{ fontSize: 10 }}>{String(column.group || "").replace(/_/g, " ")}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.row_key}>
                <td style={{ position: "sticky", left: 0, background: "#09162f", zIndex: 1, padding: 8, borderBottom: "1px solid #203761", verticalAlign: "top" }}>
                  <div><strong>{row.label || "Run"}</strong></div>
                  <div className="muted">{row.row_type === "scheduled_slot" ? formatDt(row.scheduled_at) : formatDt(row.run_at)}</div>
                  <div className="muted">{row.run_at ? `run ${formatDt(row.run_at)}` : String(row.row_status || "").toUpperCase()}</div>
                  <div className="muted">{String(row.run_profile || "full").toLowerCase() === "lightweight" ? "Lightweight" : "Full"}</div>
                  {row.overall_level && row.overall_level !== "unknown" && (
                    <div style={{ marginTop: 4 }}><span className={pillClass(row.overall_level)}>{String(row.overall_level).toUpperCase()}</span></div>
                  )}
                </td>
                {columns.map((column) => {
                  const cell = row?.cells?.[column.column_key] || {};
                  return (
                    <td key={`${row.row_key}-${column.column_key}`} style={{ padding: 8, borderBottom: "1px solid #203761", textAlign: "center" }} title={matrixCellTitle(cell)}>
                      <span className={matrixCellClass(cell)}>{cell?.display_value || "—"}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const asArray = (v) => (Array.isArray(v) ? v : []);

const parseRoadSegments = (segmentKeys = []) => {
  const dirs = new Set();
  const junctions = new Set();
  for (const key of asArray(segmentKeys)) {
    const parts = String(key || "").split("|");
    const dir = String(parts[1] || "ANY").toUpperCase();
    const from = String(parts[2] || "*").toUpperCase();
    const to = String(parts[3] || "*").toUpperCase();
    if (dir && dir !== "ANY") dirs.add(dir);
    if (from && from !== "*") junctions.add(from);
    if (to && to !== "*") junctions.add(to);
  }
  return { dirs: [...dirs], junctions: [...junctions] };
};

const ynna = (v) => (v === true ? "yes" : v === false ? "no" : "n/a");
const NH_EVENT_SCORE_MIN = 55;
const isNhEventRelevant = (m = {}) => {
  if (m?.directionMatch === true) return true;
  if (m?.junctionOverlap === true) return true;
  const overlapMins = Number(m?.timeOverlapMins);
  const overlapScore = Number(m?.overlapScore);
  return (
    m?.timeRelevant === true &&
    Number.isFinite(overlapMins) &&
    overlapMins > 0 &&
    Number.isFinite(overlapScore) &&
    overlapScore >= NH_EVENT_SCORE_MIN
  );
};
const junctionSpanText = (junctions = []) => {
  const vals = asArray(junctions)
    .map((x) => String(x || "").toUpperCase())
    .filter(Boolean);
  if (!vals.length) return "N/A";
  if (vals.length === 1) return vals[0];
  return `${vals[0]} -> ${vals[vals.length - 1]}`;
};

const extractRoadTokensFromText = (text = "") => {
  const matches = String(text || "").toUpperCase().match(/\b(?:M\d{1,3}[A-Z]?|A\d{1,4}[A-Z]?(?:\(M\))?)\b/g) || [];
  return [...new Set(matches.map((x) => String(x || "").replace(/\(M\)/g, "").trim()).filter(Boolean))];
};

const extractJunctionTokensFromText = (text = "") => {
  const out = new Set();
  const re = /\bJ(?:UNCTION)?\s*(\d{1,3}[A-Z]?)\b/gi;
  let m = null;
  while ((m = re.exec(String(text || "")))) {
    const token = String(m[1] || "").toUpperCase();
    if (token) out.add(token);
  }
  return [...out];
};

const inferStepDirections = (step = {}) => {
  const text = `${step?.instruction || ""} ${step?.name || ""}`.toLowerCase();
  const out = [];
  if (text.includes("northbound")) out.push("NORTHBOUND");
  if (text.includes("southbound")) out.push("SOUTHBOUND");
  if (text.includes("eastbound")) out.push("EASTBOUND");
  if (text.includes("westbound")) out.push("WESTBOUND");
  if (out.length) return out;
  const sLat = Number(step?.start?.lat);
  const sLon = Number(step?.start?.lon);
  const eLat = Number(step?.end?.lat);
  const eLon = Number(step?.end?.lon);
  if ([sLat, sLon, eLat, eLon].every((n) => Number.isFinite(n))) {
    const dLat = eLat - sLat;
    const dLon = eLon - sLon;
    if (Math.abs(dLat) >= Math.abs(dLon)) {
      if (dLat > 0.0001) return ["NORTHBOUND"];
      if (dLat < -0.0001) return ["SOUTHBOUND"];
    } else {
      if (dLon > 0.0001) return ["EASTBOUND"];
      if (dLon < -0.0001) return ["WESTBOUND"];
    }
  }
  return [];
};

const buildRoadTimelineByRoad = ({ routeDetail = null, departAt = null }) => {
  const steps = asArray(routeDetail?.steps);
  if (!steps.length) return new Map();
  const departTs = new Date(departAt || "").getTime();
  let cursorTs = Number.isFinite(departTs) ? departTs : null;
  const map = new Map();
  const stepJunctionMap = new Map();

  for (let stepIdx = 0; stepIdx < steps.length; stepIdx += 1) {
    const step = steps[stepIdx];
    const text = `${step?.instruction || ""} ${step?.name || ""}`;
    const roads = extractRoadTokensFromText(text);
    const stepJunctions = extractJunctionTokensFromText(text);
    stepJunctionMap.set(stepIdx, stepJunctions);
    if (!roads.length) {
      const dur = Number(step?.duration_sec);
      if (cursorTs != null && Number.isFinite(dur) && dur > 0) cursorTs += dur * 1000;
      continue;
    }
    const stepDurationSecRaw = Number(step?.duration_sec);
    const stepDurationSec = Number.isFinite(stepDurationSecRaw) && stepDurationSecRaw > 0 ? stepDurationSecRaw : 0;
    const stepDistanceRaw = Number(step?.distance_m);
    const stepDistanceM = Number.isFinite(stepDistanceRaw) && stepDistanceRaw > 0 ? stepDistanceRaw : 0;
    const stepDirs = inferStepDirections(step);
    const perRoadDurSec = roads.length ? stepDurationSec / roads.length : stepDurationSec;
    const perRoadDistM = roads.length ? stepDistanceM / roads.length : stepDistanceM;

    for (const road of roads) {
      const existing = map.get(road) || {
        road,
        enterAt: null,
        exitAt: null,
        durationSec: 0,
        distanceM: 0,
        directions: new Set(),
        junctions: new Set(),
        firstStepIdx: null,
        lastStepIdx: null,
      };
      if (!Number.isFinite(existing.firstStepIdx)) existing.firstStepIdx = stepIdx;
      existing.lastStepIdx = stepIdx;
      if (existing.enterAt == null && cursorTs != null) existing.enterAt = cursorTs;
      if (cursorTs != null) existing.exitAt = cursorTs + perRoadDurSec * 1000;
      existing.durationSec += perRoadDurSec;
      existing.distanceM += perRoadDistM;
      for (const d of stepDirs) existing.directions.add(d);
      for (const j of stepJunctions) existing.junctions.add(j);
      map.set(road, existing);
    }

    if (cursorTs != null) cursorTs += stepDurationSec * 1000;
  }

  return new Map(
    Array.from(map.entries()).map(([road, v]) => [
      road,
      (() => {
        const explicitJunctions = [...v.junctions].sort();
        let inferredJunctions = [];
        if (!explicitJunctions.length && Number.isFinite(v.firstStepIdx) && Number.isFinite(v.lastStepIdx)) {
          const near = new Set();
          const from = Math.max(0, Number(v.firstStepIdx) - 2);
          const to = Math.min(steps.length - 1, Number(v.lastStepIdx) + 2);
          for (let i = from; i <= to; i += 1) {
            for (const j of asArray(stepJunctionMap.get(i))) near.add(String(j || "").toUpperCase());
          }
          inferredJunctions = [...near].filter(Boolean).sort();
        }
        return {
        road,
        enterAt: v.enterAt ? new Date(v.enterAt).toISOString() : null,
        exitAt: v.exitAt ? new Date(v.exitAt).toISOString() : null,
        durationMins: v.durationSec > 0 ? Math.round(v.durationSec / 60) : null,
        distanceKm: v.distanceM > 0 ? Number((v.distanceM / 1000).toFixed(1)) : null,
        directions: [...v.directions].sort(),
          junctions: explicitJunctions,
          inferredJunctions,
        };
      })(),
    ])
  );
};

const roadChecksByExecution = (detail) => {
  if (!detail || typeof detail !== "object") return [];
  const executions = asArray(detail.executions);
  const evaluations = asArray(detail.evaluations);
  const observations = asArray(detail.observations);
  const sourceCalls = asArray(detail.source_calls);

  const exById = new Map(
    executions
      .filter((e) => Number.isFinite(Number(e?.execution_id)))
      .map((e) => [Number(e.execution_id), e])
  );

  const nhCallsByExec = new Map();
  for (const c of sourceCalls) {
    const exId = Number(c?.execution_id);
    if (!Number.isFinite(exId)) continue;
    const src = String(c?.source || "").toLowerCase();
    if (!(src.includes("national_highways") || src === "nh" || src.includes("road_nh"))) continue;
    const prev = nhCallsByExec.get(exId) || { calls: 0, rows: 0, errors: 0 };
    prev.calls += Number(c?.call_count || 0);
    prev.rows += 1;
    if (String(c?.status || "").toLowerCase() === "error" || c?.error_code) prev.errors += 1;
    nhCallsByExec.set(exId, prev);
  }

  const evalCandidates = evaluations
    .map((e) => ({ ...e, evaluatedMs: new Date(e?.evaluated_at || 0).getTime() }))
    .filter((e) => Number.isFinite(e.evaluatedMs));

  const evalForRunAt = (runAt) => {
    const runMs = new Date(runAt || 0).getTime();
    if (!Number.isFinite(runMs) || !evalCandidates.length) return null;
    let best = null;
    let bestDiff = Infinity;
    for (const e of evalCandidates) {
      const d = Math.abs(e.evaluatedMs - runMs);
      if (d < bestDiff) {
        best = e;
        bestDiff = d;
      }
    }
    if (bestDiff > 20 * 60 * 1000) return null;
    return best;
  };

  const groups = new Map();
  for (const o of observations) {
    if (String(o?.source || "").toLowerCase() !== "road") continue;
    if (String(o?.target_type || "").toLowerCase() !== "road_segment") continue;
    const exId = Number(o?.execution_id);
    const hasEx = Number.isFinite(exId);
    const key = hasEx ? `ex-${exId}` : `obs-${o?.observation_id || o?.created_at || Math.random()}`;
    const existing = groups.get(key) || {
      executionId: hasEx ? exId : null,
      runAt: hasEx ? exById.get(exId)?.run_at || o?.created_at || null : o?.created_at || null,
      roads: new Map(),
      nhCalls: hasEx ? nhCallsByExec.get(exId) || { calls: 0, rows: 0, errors: 0 } : { calls: 0, rows: 0, errors: 0 },
      runMeta: (() => {
        const ex = hasEx ? exById.get(exId) : null;
        const evalSnap = evalForRunAt(ex?.run_at || o?.created_at || null);
        const evalPlan = evalSnap?.plan || {};
        const s = ex?.summary || {};
        const resultPlan = s?.result?.plan || evalPlan || {};
        const driveRoad = resultPlan?.drive?.road || resultPlan?.modes?.self_drive?.road || {};
        const taxiRoad = resultPlan?.taxi?.road || resultPlan?.modes?.taxi?.road || {};
        const nhOverlay = driveRoad?.nh_overlay || taxiRoad?.nh_overlay || {};
        const routeDetail = driveRoad?.route_detail || taxiRoad?.route_detail || null;
        const departAt = driveRoad?.depart_at || taxiRoad?.depart_at || s?.anchor_estimated_utc || s?.anchor_scheduled_utc || null;
        const roadTimelineByRoad = buildRoadTimelineByRoad({ routeDetail, departAt });
        const nhMatched = asArray(nhOverlay?.matched);
        const nhMatchedByRoad = new Map();
        for (const m of nhMatched) {
          for (const rkey of asArray(m?.matched_roads).map((x) => String(x || "").toUpperCase()).filter(Boolean)) {
            const arr = nhMatchedByRoad.get(rkey) || [];
            arr.push({
              id: m?.id || null,
              road: m?.road || null,
              status: m?.status || null,
              impact: m?.impact || null,
              direction: m?.direction || null,
              from: m?.from || null,
              to: m?.to || null,
              minDistanceKm: Number.isFinite(Number(m?.min_distance_km)) ? Number(m.min_distance_km) : null,
              overlapScore: Number.isFinite(Number(m?.overlap_score)) ? Number(m.overlap_score) : null,
              timeOverlapMins: Number.isFinite(Number(m?.time_overlap_mins)) ? Number(m.time_overlap_mins) : null,
              strongRouteEvidence: m?.strong_route_evidence === true,
              timeRelevant: m?.time_relevant === true,
              directionMatch: m?.direction_match,
              junctionOverlap: m?.junction_overlap,
              incidentStartTime: m?.incident_start_time || null,
              incidentEndTime: m?.incident_end_time || null,
            });
            nhMatchedByRoad.set(rkey, arr);
          }
        }
        const nhImpactCounts = nhMatched.reduce(
          (acc, m) => {
            const impact = String(m?.impact || "fyi").toLowerCase();
            if (impact === "major") acc.major += 1;
            else if (impact === "advisory") acc.advisory += 1;
            else acc.fyi += 1;
            if (m?.strong_route_evidence === true) acc.strong += 1;
            if (m?.time_relevant === true) acc.timeRelevant += 1;
            return acc;
          },
          { major: 0, advisory: 0, fyi: 0, strong: 0, timeRelevant: 0 }
        );
        return {
          roadNhDue: s?.road_nh_due,
          roadGoogleDue: s?.road_google_due,
          paidRoadRan: s?.paid_road_ran,
          roadGoogleUsed: s?.road_google_used,
          nhChecked: nhOverlay?.checked,
          nhError: nhOverlay?.error || null,
          nhMatchedCount: nhMatched.length,
          nhImpactCounts,
          nhMatchedByRoad,
          roadTimelineByRoad,
        };
      })(),
    };
    const n = o?.normalized_json || {};
    const observed = o?.observed_json || {};
    const roadKey = String(o?.target_key || n?.road || "").toUpperCase() || "UNKNOWN_ROAD";
    const timeline = existing.runMeta?.roadTimelineByRoad?.get?.(roadKey) || null;
    const nhMatches = asArray(existing.runMeta?.nhMatchedByRoad?.get?.(roadKey));
    const nhEffectiveMatches = nhMatches.filter((m) => isNhEventRelevant(m));
    const parsedSeg = parseRoadSegments(n?.route_segment_keys);
    const routeDirections = asArray(n?.route_directions).map((x) => String(x || "").toUpperCase()).filter(Boolean);
    const routeJunctions = asArray(n?.route_junctions).map((x) => String(x || "").toUpperCase()).filter(Boolean);
    const timelineJunctions = asArray(timeline?.junctions).map((x) => String(x || "").toUpperCase()).filter(Boolean);
    const timelineInferredJunctions = asArray(timeline?.inferredJunctions)
      .map((x) => String(x || "").toUpperCase())
      .filter(Boolean);
    const junctionSource = routeJunctions.length
      ? "route"
      : parsedSeg.junctions.length
      ? "segment"
      : timelineJunctions.length
      ? "timeline"
      : timelineInferredJunctions.length
      ? "inferred"
      : "none";
    const mergedJunctions = routeJunctions.length
      ? routeJunctions
      : parsedSeg.junctions.length
      ? parsedSeg.junctions
      : timelineJunctions.length
      ? timelineJunctions
      : timelineInferredJunctions;
    existing.roads.set(roadKey, {
      road: roadKey,
      level: String(n?.level || o?.severity || "unknown").toLowerCase(),
      durationMins: Number.isFinite(Number(n?.road_duration_mins)) ? Number(n.road_duration_mins) : null,
      routeDirections,
      routeJunctions,
      routeJunctionSpan: junctionSpanText(mergedJunctions),
      routeJunctionSource: junctionSource,
      routeDirectionsParsed: parsedSeg.dirs,
      routeJunctionsParsed: parsedSeg.junctions,
      nhEventCount: nhMatches.length ? nhEffectiveMatches.length : Number(n?.nh_event_count || 0),
      nhEventCountRaw: nhMatches.length ? nhMatches.length : Number(n?.nh_event_count || 0),
      nhDirections:
        nhEffectiveMatches.length > 0
          ? [...new Set(nhEffectiveMatches.map((x) => String(x?.direction || "").toUpperCase()).filter(Boolean))]
          : asArray(n?.nh_directions).map((x) => String(x || "").toUpperCase()).filter(Boolean),
      nhSegmentOverlapRatio:
        Number.isFinite(Number(o?.delta_json?.nh_segment_overlap_ratio)) ? Number(o.delta_json.nh_segment_overlap_ratio) : null,
      nhOverlapScore: Number.isFinite(Number(o?.delta_json?.nh_overlap_score)) ? Number(o.delta_json.nh_overlap_score) : null,
      nhOverlapConfidence: String(o?.delta_json?.nh_overlap_confidence || ""),
      nhDirectionMatch: o?.delta_json?.nh_direction_overlap,
      nhSegmentMatch: o?.delta_json?.nh_segment_overlap,
      nhMatches,
      timeline,
      decision: o?.decision || null,
      reasonCode: o?.reason_code || null,
      nhDue: observed?.nh_due,
      googleDue: observed?.google_due,
      paidRoadRan: observed?.paid_road_ran,
      createdAt: o?.created_at || null,
    });
    groups.set(key, existing);
  }

  return Array.from(groups.values())
    .map((g) => ({
      executionId: g.executionId,
      runAt: g.runAt,
      nhCalls: g.nhCalls,
      runMeta: g.runMeta,
      roads: Array.from(g.roads.values()).sort((a, b) => a.road.localeCompare(b.road)),
    }))
    .sort((a, b) => new Date(b.runAt || 0).getTime() - new Date(a.runAt || 0).getTime());
};

const railChecksByExecution = (detail) => {
  if (!detail || typeof detail !== "object") return [];
  const executions = asArray(detail.executions);
  const evaluations = asArray(detail.evaluations);
  const observations = asArray(detail.rail_observations).length ? asArray(detail.rail_observations) : asArray(detail.observations);
  const sourceCalls = asArray(detail.source_calls);

  const exById = new Map(
    executions
      .filter((e) => Number.isFinite(Number(e?.execution_id)))
      .map((e) => [Number(e.execution_id), e])
  );

  const railCallsByExec = new Map();
  for (const c of sourceCalls) {
    const exId = Number(c?.execution_id);
    if (!Number.isFinite(exId)) continue;
    const src = String(c?.source || "").toLowerCase();
    if (!(src === "nr" || src === "raildata" || src.includes("rail"))) continue;
    const prev = railCallsByExec.get(exId) || {
      total: { calls: 0, rows: 0, errors: 0 },
      nr: { calls: 0, rows: 0, errors: 0, endpoints: {} },
      raildata: { calls: 0, rows: 0, errors: 0 },
    };
    const calls = Number(c?.call_count || 0);
    const isErr = String(c?.status || "").toLowerCase() === "error" || !!c?.error_code;
    prev.total.calls += calls;
    prev.total.rows += 1;
    if (isErr) prev.total.errors += 1;
    if (src === "nr") {
      prev.nr.calls += calls;
      prev.nr.rows += 1;
      if (isErr) prev.nr.errors += 1;
      const ep = String(c?.endpoint || "unknown").toLowerCase();
      prev.nr.endpoints[ep] = (prev.nr.endpoints[ep] || 0) + calls;
    } else {
      prev.raildata.calls += calls;
      prev.raildata.rows += 1;
      if (isErr) prev.raildata.errors += 1;
    }
    railCallsByExec.set(exId, prev);
  }

  const evalCandidates = evaluations
    .map((e) => ({ ...e, evaluatedMs: new Date(e?.evaluated_at || 0).getTime() }))
    .filter((e) => Number.isFinite(e.evaluatedMs));
  const evalForRunAt = (runAt) => {
    const runMs = new Date(runAt || 0).getTime();
    if (!Number.isFinite(runMs) || !evalCandidates.length) return null;
    let best = null;
    let bestDiff = Infinity;
    for (const e of evalCandidates) {
      const d = Math.abs(e.evaluatedMs - runMs);
      if (d < bestDiff) {
        best = e;
        bestDiff = d;
      }
    }
    if (bestDiff > 20 * 60 * 1000) return null;
    return best;
  };

  const buildRunMeta = (execution, fallbackCreatedAt = null) => {
    const exSummary = execution?.summary || {};
    const evalSnap = evalForRunAt(execution?.run_at || fallbackCreatedAt || null);
    const evalSources = evalSnap?.sources || {};
    const evalSummary = evalSnap?.summary || {};
    const evalPlan = evalSnap?.plan || {};
    const s = { ...evalSummary, ...exSummary };
    const freeRail = evalSources?.free_rail || {};
    const railPlan = evalPlan?.rail || evalPlan?.modes?.rail || {};
    const nrBest = railPlan?.best_candidate || {};
    const nrJourney =
      (Array.isArray(nrBest?.journeys) && nrBest.journeys.length ? nrBest.journeys[0] : null) ||
      nrBest?.journey ||
      null;
    const nrLastChecked = nrBest?.last_checked || null;
    const nrLegs = asArray(nrJourney?.legs).map((leg) => ({
      from: String(leg?.from || leg?.from_crs || "").toUpperCase() || "N/A",
      to: String(leg?.to || leg?.to_crs || "").toUpperCase() || "N/A",
      depart: leg?.depart || leg?.sched_dep || null,
      arrive: leg?.arrive || leg?.sched_arr || null,
      realtime: String(leg?.realtime || leg?.status || "").toUpperCase() || "N/A",
      operator: leg?.operator || null,
    }));
    return {
      freeRailLevel: s?.free_rail_level || freeRail?.level || "unknown",
      railMonitoringLevel: s?.rail_monitoring_level || "unknown",
      railMonitoringReason: s?.rail_monitoring_reason || "N/A",
      paidRailRan: s?.paid_rail_ran === true,
      railDue: s?.free_rail_checked_crs?.length ? true : null,
      checked: freeRail?.checked === true,
      stale: freeRail?.stale === true,
      error: freeRail?.error || null,
      coverageStatus: s?.free_rail_coverage_status || freeRail?.coverage_status || null,
      checkedOkCount: Number(s?.free_rail_checked_ok_count ?? freeRail?.checked_ok_count ?? 0),
      checkedErrorCount: Number(s?.free_rail_checked_error_count ?? freeRail?.checked_error_count ?? 0),
      checkedErrorCrs: asArray(s?.free_rail_checked_error_crs || freeRail?.checked_error_crs)
        .map((x) => String(x || "").toUpperCase())
        .filter(Boolean),
      timeRelevantCount: Number(s?.rail_time_relevant_count ?? freeRail?.rail_time_relevant_count ?? 0),
      pathOverlapCount: Number(s?.rail_path_overlap_count ?? freeRail?.rail_path_overlap_count ?? 0),
      majorOverlapCount: Number(s?.rail_major_overlap_count ?? freeRail?.rail_major_overlap_count ?? 0),
      reasonCodes: asArray(s?.rail_impact_reason_codes || freeRail?.rail_impact_reason_codes).filter(Boolean),
      paidIncidentCount: Number(s?.rail_paid_incident_count ?? 0),
      paidIncidentSignalUsed: s?.rail_paid_incident_signal_used === true,
      paidIncidentSignalIgnored: s?.rail_paid_incident_signal_ignored === true,
      timeline: freeRail?.rail_timeline || null,
      nrCheckedSources: asArray(nrBest?.checked_sources || []).map((x) => String(x || "").toLowerCase()),
      nrDisruptionLevel: String(nrBest?.disruption?.level || "unknown").toLowerCase(),
      nrJourney: nrJourney
        ? {
            depart: nrJourney?.depart || null,
            arrive: nrJourney?.arrive || null,
            durationMins: Number.isFinite(Number(nrJourney?.duration_mins)) ? Number(nrJourney.duration_mins) : null,
            changes: Number.isFinite(Number(nrJourney?.changes)) ? Number(nrJourney.changes) : null,
            realtime: String(nrJourney?.realtime || nrJourney?.status || "").toUpperCase() || "N/A",
            targetDeltaMins: Number.isFinite(Number(nrJourney?.target_delta_mins))
              ? Number(nrJourney.target_delta_mins)
              : Number.isFinite(Number(nrBest?.target_best_delta_mins))
              ? Number(nrBest.target_best_delta_mins)
              : null,
            legs: nrLegs.slice(0, 6),
          }
        : null,
      nrLastChecked,
      railBaselineOutcome: {
        status: s?.rail_baseline_outcome_status || null,
        seenAt: s?.rail_baseline_outcome_seen_at || null,
        source: s?.rail_baseline_outcome_source || null,
        final: s?.rail_baseline_outcome_final === true,
      },
      railMonitoringMode: s?.rail_monitoring_mode || null,
      railActiveUntil: s?.rail_active_until_utc || null,
      railPostWindowContextOnly: s?.rail_post_window_context_only === true,
      railLiveBoard: {
        enabled: s?.rail_live_board_enabled === true,
        checked: s?.rail_live_board_checked === true,
        checkedAt: s?.rail_live_board_checked_at || null,
        reason: s?.rail_live_board_reason || null,
        summary: s?.rail_live_board_summary || null,
        queries: asArray(s?.rail_live_board_queries),
      },
      assurance: {
        level: String(s?.rail_assurance_level || "unknown").toUpperCase(),
        source: s?.rail_assurance_source || null,
        reasonCode: s?.rail_assurance_reason_code || null,
        reasonText: s?.rail_assurance_reason_text || null,
        thresholdCrossed: s?.rail_assurance_threshold_crossed || null,
        confidence: s?.rail_assurance_confidence || null,
        usedInDecision: s?.rail_assurance_used_in_decision === true,
        decisionBias: s?.rail_assurance_decision_bias || null,
        operationalState: s?.rail_assurance_operational_state || null,
        boardAlignment: s?.rail_assurance_board_alignment || null,
        weight: Number.isFinite(Number(s?.rail_assurance_weight)) ? Number(s.rail_assurance_weight) : null,
        nrWeight: Number.isFinite(Number(s?.rail_nr_weight)) ? Number(s.rail_nr_weight) : null,
        liveBoardWeight: Number.isFinite(Number(s?.rail_live_board_weight)) ? Number(s.rail_live_board_weight) : null,
        nrDeweightedReason: s?.rail_nr_deweighted_reason || null,
      },
    };
  };

  const groups = new Map();
  for (const o of observations) {
    if (String(o?.source || "").toLowerCase() !== "rail") continue;
    if (String(o?.target_type || "").toLowerCase() !== "crs") continue;
    const exId = Number(o?.execution_id);
    const hasEx = Number.isFinite(exId);
    const key = hasEx ? `ex-${exId}` : `obs-${o?.observation_id || o?.created_at || Math.random()}`;
    const existing = groups.get(key) || {
      executionId: hasEx ? exId : null,
      runAt: hasEx ? exById.get(exId)?.run_at || o?.created_at || null : o?.created_at || null,
      crsRows: new Map(),
      railCalls:
        hasEx
          ? railCallsByExec.get(exId) || {
              total: { calls: 0, rows: 0, errors: 0 },
              nr: { calls: 0, rows: 0, errors: 0, endpoints: {} },
            raildata: { calls: 0, rows: 0, errors: 0 },
            }
          : {
              total: { calls: 0, rows: 0, errors: 0 },
              nr: { calls: 0, rows: 0, errors: 0, endpoints: {} },
              raildata: { calls: 0, rows: 0, errors: 0 },
            },
      runMeta: buildRunMeta(hasEx ? exById.get(exId) : null, o?.created_at || null),
    };
    const n = o?.normalized_json || {};
    const observed = o?.observed_json || {};
    const crs = String(o?.target_key || n?.crs || "").toUpperCase() || "N/A";
    existing.crsRows.set(crs, {
      crs,
      level: String(n?.level || o?.severity || "unknown").toLowerCase(),
      affected: n?.affected === true || observed?.affected === true,
      incidentCount: Number(n?.incident_count ?? observed?.incident_count ?? 0),
      timeRelevantCount: Number(n?.rail_time_relevant_count ?? observed?.rail_time_relevant_count ?? 0),
      pathOverlapCount: Number(n?.rail_path_overlap_count ?? observed?.rail_path_overlap_count ?? 0),
      majorOverlapCount: Number(n?.rail_major_overlap_count ?? observed?.rail_major_overlap_count ?? 0),
      incidents: asArray(observed?.incidents).slice(0, 4),
      departUtc: observed?.depart_utc || null,
      arriveUtc: observed?.arrive_utc || null,
      durationMins: Number.isFinite(Number(observed?.duration_mins)) ? Number(observed.duration_mins) : null,
      changes: Number.isFinite(Number(observed?.changes)) ? Number(observed.changes) : null,
      decision: o?.decision || null,
      reasonCode: o?.reason_code || null,
    });
    groups.set(key, existing);
  }

  for (const ex of executions) {
    const exId = Number(ex?.execution_id);
    if (!Number.isFinite(exId)) continue;
    const key = `ex-${exId}`;
    if (groups.has(key)) continue;
    const railCalls =
      railCallsByExec.get(exId) || {
        total: { calls: 0, rows: 0, errors: 0 },
        nr: { calls: 0, rows: 0, errors: 0, endpoints: {} },
        raildata: { calls: 0, rows: 0, errors: 0 },
      };
    const runMeta = buildRunMeta(ex, ex?.run_at || null);
    const hasRailActivity =
      Number(railCalls.total.calls || 0) > 0 ||
      Number(railCalls.total.rows || 0) > 0 ||
      runMeta?.checked === true ||
      runMeta?.stale === true ||
      runMeta?.railLiveBoard?.enabled === true ||
      runMeta?.railLiveBoard?.checked === true ||
      !!runMeta?.nrJourney ||
      !!runMeta?.coverageStatus;
    if (!hasRailActivity) continue;
    groups.set(key, {
      executionId: exId,
      runAt: ex?.run_at || null,
      crsRows: new Map(),
      railCalls,
      runMeta,
    });
  }

  return Array.from(groups.values())
    .map((g) => ({
      executionId: g.executionId,
      runAt: g.runAt,
      railCalls: g.railCalls || {
        total: { calls: 0, rows: 0, errors: 0 },
        nr: { calls: 0, rows: 0, errors: 0, endpoints: {} },
        raildata: { calls: 0, rows: 0, errors: 0 },
      },
      runMeta: g.runMeta,
      crsRows: Array.from(g.crsRows.values()).sort((a, b) => a.crs.localeCompare(b.crs)),
    }))
    .sort((a, b) => new Date(b.runAt || 0).getTime() - new Date(a.runAt || 0).getTime());
};

const railDebugFromDetail = (detail) => {
  const evals = asArray(detail?.evaluations);
  const latestEval = evals[0] || null;
  const freeRail = latestEval?.sources?.free_rail || {};
  const incidents = asArray(freeRail?.incidents);
  return {
    timeRelevantCount: Number(freeRail?.rail_time_relevant_count || 0),
    pathOverlapCount: Number(freeRail?.rail_path_overlap_count || 0),
    majorOverlapCount: Number(freeRail?.rail_major_overlap_count || 0),
    reasonCodes: asArray(freeRail?.rail_impact_reason_codes).filter(Boolean),
    timeline: freeRail?.rail_timeline || null,
    incidents: incidents.slice(0, 6),
  };
};

const roadDebugFromSummary = (summary = {}) => {
  const s = summary || {};
  const primary = {
    departAt: s?.road_query_depart_at || null,
    baseMins: Number.isFinite(Number(s?.road_google_base_mins)) ? Number(s.road_google_base_mins) : null,
    trafficMins: Number.isFinite(Number(s?.road_google_traffic_mins)) ? Number(s.road_google_traffic_mins) : null,
    safetyBufferMins: Number.isFinite(Number(s?.road_safety_buffer_mins)) ? Number(s.road_safety_buffer_mins) : null,
    parkingTransferMins: Number.isFinite(Number(s?.road_parking_transfer_mins)) ? Number(s.road_parking_transfer_mins) : null,
    finalEtaSafeMins: Number.isFinite(Number(s?.road_final_eta_safe_mins)) ? Number(s.road_final_eta_safe_mins) : null,
    stabilizationPasses: Number.isFinite(Number(s?.road_stabilization_passes)) ? Number(s.road_stabilization_passes) : null,
    stabilizationDeltaMins:
      Number.isFinite(Number(s?.road_stabilization_delta_mins)) ? Number(s.road_stabilization_delta_mins) : null,
  };
  const drive = {
    departAt: s?.drive_road_query_depart_at || null,
    baseMins: Number.isFinite(Number(s?.drive_google_base_mins)) ? Number(s.drive_google_base_mins) : null,
    trafficMins: Number.isFinite(Number(s?.drive_google_traffic_mins)) ? Number(s.drive_google_traffic_mins) : null,
    safetyBufferMins: Number.isFinite(Number(s?.drive_safety_buffer_mins)) ? Number(s.drive_safety_buffer_mins) : null,
    parkingTransferMins:
      Number.isFinite(Number(s?.drive_parking_transfer_mins)) ? Number(s.drive_parking_transfer_mins) : null,
    finalEtaSafeMins: Number.isFinite(Number(s?.drive_final_eta_safe_mins)) ? Number(s.drive_final_eta_safe_mins) : null,
  };
  const taxi = {
    departAt: s?.taxi_road_query_depart_at || null,
    baseMins: Number.isFinite(Number(s?.taxi_google_base_mins)) ? Number(s.taxi_google_base_mins) : null,
    trafficMins: Number.isFinite(Number(s?.taxi_google_traffic_mins)) ? Number(s.taxi_google_traffic_mins) : null,
    safetyBufferMins: Number.isFinite(Number(s?.taxi_safety_buffer_mins)) ? Number(s.taxi_safety_buffer_mins) : null,
    parkingTransferMins:
      Number.isFinite(Number(s?.taxi_parking_transfer_mins)) ? Number(s.taxi_parking_transfer_mins) : null,
    finalEtaSafeMins: Number.isFinite(Number(s?.taxi_final_eta_safe_mins)) ? Number(s.taxi_final_eta_safe_mins) : null,
  };
  const hasData = [
    primary.departAt,
    primary.baseMins,
    primary.trafficMins,
    primary.finalEtaSafeMins,
    drive.departAt,
    drive.baseMins,
    taxi.departAt,
    taxi.baseMins,
  ].some((v) => v != null);
  return {
    hasData,
    primary,
    drive,
    taxi,
    monitoringLevel: String(s?.road_monitoring_level || "unknown").toUpperCase(),
    rawLevel: String(s?.road_raw_monitoring_level || "unknown").toUpperCase(),
    networkLevel: String(s?.road_network_level || "unknown").toUpperCase(),
    nhLevel: String(s?.road_nh_level || "unknown").toUpperCase(),
    reason: s?.road_monitoring_reason || "N/A",
    feasibilityStatus: String(s?.road_feasibility_status || "unknown").toUpperCase(),
    marginMins: Number.isFinite(Number(s?.road_margin_mins)) ? Number(s.road_margin_mins) : null,
    delayFromBaseMins: Number.isFinite(Number(s?.road_delay_from_base_mins)) ? Number(s.road_delay_from_base_mins) : null,
    guidanceTrafficMins:
      Number.isFinite(Number(s?.road_guidance_traffic_mins)) ? Number(s.road_guidance_traffic_mins) : null,
    previousTrafficMins:
      Number.isFinite(Number(s?.road_plan_traffic_prev_mins)) ? Number(s.road_plan_traffic_prev_mins) : null,
    currentTrafficMins:
      Number.isFinite(Number(s?.road_plan_traffic_curr_mins)) ? Number(s.road_plan_traffic_curr_mins) : null,
    driftMins: Number.isFinite(Number(s?.road_plan_drift_mins)) ? Number(s.road_plan_drift_mins) : null,
    guidanceDriftMins: Number.isFinite(Number(s?.road_guidance_drift_mins)) ? Number(s.road_guidance_drift_mins) : null,
    guidanceDepartAt: s?.road_guidance_depart_utc || null,
    guidanceArriveAt: s?.road_guidance_arrive_utc || null,
    baseline: {
      lockedAt: s?.road_baseline_locked_at || null,
      selectedMode: s?.road_baseline_selected_mode || null,
      departAt: s?.road_baseline_depart_utc || null,
      arriveAt: s?.road_baseline_arrive_utc || null,
      finalEtaSafeMins: Number.isFinite(Number(s?.road_baseline_final_eta_safe_mins)) ? Number(s.road_baseline_final_eta_safe_mins) : null,
      nowMins: Number.isFinite(Number(s?.road_baseline_now_mins)) ? Number(s.road_baseline_now_mins) : null,
      deltaVsPlannedMins:
        Number.isFinite(Number(s?.road_baseline_delta_vs_planned_mins)) ? Number(s.road_baseline_delta_vs_planned_mins) : null,
      routeProvider: s?.road_baseline_route_provider || null,
      routeSummary: s?.road_baseline_route_summary || null,
      routeFingerprint: s?.road_baseline_route_fingerprint || null,
      polylineHash: s?.road_baseline_polyline_hash || null,
      majorRoads: asArray(s?.road_baseline_major_roads).filter(Boolean),
      monitoredRoads: asArray(s?.road_baseline_monitored_roads).filter(Boolean),
      status: String(s?.road_baseline_status || "unknown").toUpperCase(),
      reasonCode: s?.road_baseline_reason_code || null,
      reasonText: s?.road_baseline_reason_text || null,
      primaryCauseRoads: asArray(s?.road_baseline_primary_cause_roads).filter(Boolean),
      sourceDriver: s?.road_baseline_source_driver || null,
    },
    alternative: {
      selectedMode: s?.road_alternative_selected_mode || null,
      nowMins: Number.isFinite(Number(s?.road_alternative_now_mins)) ? Number(s.road_alternative_now_mins) : null,
      departAt: s?.road_alternative_depart_utc || null,
      arriveAt: s?.road_alternative_arrive_utc || null,
      routeProvider: s?.road_alternative_route_provider || null,
      routeSummary: s?.road_alternative_route_summary || null,
      routeFingerprint: s?.road_alternative_route_fingerprint || null,
      available: s?.road_alternative_available === true,
      savingsMins: Number.isFinite(Number(s?.road_alternative_savings_mins)) ? Number(s.road_alternative_savings_mins) : null,
      marginGainMins:
        Number.isFinite(Number(s?.road_alternative_margin_gain_mins)) ? Number(s.road_alternative_margin_gain_mins) : null,
      reasonCode: s?.road_alternative_reason_code || null,
      reasonText: s?.road_alternative_reason_text || null,
      primaryCauseRoads: asArray(s?.road_alternative_primary_cause_roads).filter(Boolean),
    },
    divergence: {
      changed: s?.road_route_diverged_from_baseline === true,
      type: s?.road_route_divergence_type || null,
      thresholdMins: Number.isFinite(Number(s?.road_alternative_threshold_mins)) ? Number(s.road_alternative_threshold_mins) : null,
    },
    customerMessages: {
      primary: s?.road_customer_message_primary || null,
      alternative: s?.road_customer_message_alternative || null,
    },
    assurance: {
      level: String(s?.road_assurance_level || "unknown").toUpperCase(),
      source: s?.road_assurance_source || null,
      reasonCode: s?.road_assurance_reason_code || null,
      reasonText: s?.road_assurance_reason_text || null,
      thresholdCrossed: s?.road_assurance_threshold_crossed || null,
      confidence: s?.road_assurance_confidence || null,
      usedInDecision: s?.road_assurance_used_in_decision === true,
      decisionBias: s?.road_assurance_decision_bias || null,
      altActionable: s?.road_assurance_alt_actionable === true,
      weight: Number.isFinite(Number(s?.road_assurance_weight)) ? Number(s.road_assurance_weight) : null,
    },
  };
};

const nhImpactRank = (impact) => {
  const key = String(impact || "fyi").toLowerCase();
  if (key === "major") return 3;
  if (key === "advisory") return 2;
  return 1;
};

const deriveNhDriverSummary = (detail) => {
  const latestRun = roadChecksByExecution(detail)[0] || null;
  if (!latestRun) return null;
  let best = null;
  for (const road of asArray(latestRun.roads)) {
    for (const match of asArray(road?.nhMatches).filter((item) => isNhEventRelevant(item))) {
      const candidate = {
        road: road?.road || "N/A",
        decision: road?.reasonCode || "none",
        direction: String(match?.direction || "").toUpperCase() || "N/A",
        impact: String(match?.impact || "fyi").toUpperCase(),
        junctionSpan:
          road?.routeJunctionSpan && road.routeJunctionSpan !== "N/A"
            ? road.routeJunctionSpan
            : match?.from || match?.to
            ? `${match?.from || "N/A"} -> ${match?.to || "N/A"}`
            : "N/A",
        overlapMins: Number.isFinite(Number(match?.timeOverlapMins)) ? Number(match.timeOverlapMins) : null,
        timeRelevant: match?.timeRelevant === true,
        minDistanceKm: Number.isFinite(Number(match?.minDistanceKm)) ? Number(match.minDistanceKm) : null,
      };
      if (!best) {
        best = candidate;
        continue;
      }
      const currentScore = [
        nhImpactRank(candidate.impact),
        candidate.timeRelevant ? 1 : 0,
        Number.isFinite(candidate.overlapMins) ? candidate.overlapMins : -1,
      ];
      const bestScore = [
        nhImpactRank(best.impact),
        best.timeRelevant ? 1 : 0,
        Number.isFinite(best.overlapMins) ? best.overlapMins : -1,
      ];
      if (currentScore[0] > bestScore[0] ||
          (currentScore[0] === bestScore[0] && currentScore[1] > bestScore[1]) ||
          (currentScore[0] === bestScore[0] && currentScore[1] === bestScore[1] && currentScore[2] > bestScore[2])) {
        best = candidate;
      }
    }
  }
  if (!best) return null;
  return `${best.road} ${best.direction} ${best.junctionSpan} impact=${best.impact} time_relevant=${best.timeRelevant ? "yes" : "no"}${
    Number.isFinite(best.overlapMins) ? ` overlap=${best.overlapMins}m` : ""
  }${Number.isFinite(best.minDistanceKm) ? ` dist=${best.minDistanceKm.toFixed(2)}km` : ""} decision=${best.decision}`;
};

const tomtomDebugFromSummary = (summary = {}) => {
  const s = summary || {};
  const tomtomSummary = s?.road_tomtom_summary && typeof s.road_tomtom_summary === "object" ? s.road_tomtom_summary : null;
  const segments = asArray(s?.road_tomtom_segments)
    .map((seg) => ({
      road: String(seg?.road || "").toUpperCase() || "N/A",
      plannedDurationMins: Number.isFinite(Number(seg?.planned_duration_mins)) ? Number(seg.planned_duration_mins) : null,
      travelMins: Number.isFinite(Number(seg?.tomtom_travel_mins)) ? Number(seg.tomtom_travel_mins) : null,
      delayMins: Number.isFinite(Number(seg?.tomtom_traffic_delay_mins)) ? Number(seg.tomtom_traffic_delay_mins) : null,
      deltaVsPlannedMins: Number.isFinite(Number(seg?.delta_vs_planned_mins)) ? Number(seg.delta_vs_planned_mins) : null,
      departureUtc: seg?.departure_utc || null,
      arrivalUtc: seg?.arrival_utc || null,
      error: seg?.error || null,
    }))
    .filter((seg) => seg.road || seg.error);
  return {
    enabled: s?.road_tomtom_enabled === true,
    checked: s?.road_tomtom_checked === true,
    checkedAt: s?.road_tomtom_checked_at || null,
    reason: s?.road_tomtom_reason || null,
    sampled: s?.road_tomtom_sampled === true,
    error: s?.road_tomtom_error || null,
    summary: tomtomSummary,
    segments,
    hasData: (s?.road_tomtom_enabled === true) || !!segments.length || !!tomtomSummary,
  };
};

const renderTomTomDebugBlock = (latestSummary) => {
  const tomtomDbg = tomtomDebugFromSummary(latestSummary);
  const sum = tomtomDbg.summary || {};
  return (
    <div style={{ gridColumn: "1 / -1" }}>
      <details className="detail-collapsible">
        <summary>TomTom assurance debug</summary>
        <div className="muted" style={{ marginTop: 8, display: "grid", gap: 6 }}>
          <div>
            <strong>Status:</strong>{" "}
            {`enabled=${tomtomDbg.enabled ? "yes" : "no"}, checked=${tomtomDbg.checked ? "yes" : "no"}, sampled=${
              tomtomDbg.sampled ? "yes" : "no"
            }, reason=${tomtomDbg.reason || "N/A"}${tomtomDbg.error ? `, err=${tomtomDbg.error}` : ""}`}
          </div>
          <div>
            <strong>Checked at:</strong> {tomtomDbg.checkedAt ? formatDt(tomtomDbg.checkedAt) : "N/A"}
          </div>
          {tomtomDbg.hasData ? (
            <>
              <div>
                <strong>Summary:</strong>{" "}
                {`segments=${Number(sum?.segments_ok || 0)}/${Number(sum?.segments_total || 0)}, planned=${
                  Number.isFinite(Number(sum?.total_planned_mins)) ? `${Number(sum.total_planned_mins)}m` : "N/A"
                }, tomtom=${
                  Number.isFinite(Number(sum?.total_tomtom_travel_mins)) ? `${Number(sum.total_tomtom_travel_mins)}m` : "N/A"
                }, delay=${
                  Number.isFinite(Number(sum?.total_tomtom_delay_mins)) ? `${Number(sum.total_tomtom_delay_mins)}m` : "N/A"
                }, delta_vs_planned=${
                  Number.isFinite(Number(sum?.delta_vs_planned_mins)) ? `${Number(sum.delta_vs_planned_mins)}m` : "N/A"
                }, alignment=${String(sum?.alignment || "unknown").toUpperCase()}, baseline_mode=${sum?.baseline_mode_used || "N/A"}, baseline_delta=${
                  Number.isFinite(Number(sum?.baseline_delta_vs_planned_mins)) ? `${Number(sum.baseline_delta_vs_planned_mins)}m` : "N/A"
                }, baseline_causes=${asArray(sum?.baseline_primary_cause_roads).filter(Boolean).join(", ") || "none"}`}
              </div>
              <div>
                <strong>Segments:</strong>{" "}
                {tomtomDbg.segments.length
                  ? tomtomDbg.segments
                      .slice(0, 8)
                      .map((seg) =>
                        seg.error
                          ? `${seg.road} err=${seg.error}`
                          : `${seg.road} planned=${seg.plannedDurationMins ?? "N/A"}m tomtom=${seg.travelMins ?? "N/A"}m delay=${
                              seg.delayMins ?? "N/A"
                            }m delta=${seg.deltaVsPlannedMins ?? "N/A"}m ${
                              seg.departureUtc || seg.arrivalUtc
                                ? `(${seg.departureUtc ? formatDt(seg.departureUtc) : "N/A"} -> ${
                                    seg.arrivalUtc ? formatDt(seg.arrivalUtc) : "N/A"
                                  })`
                                : ""
                            }`
                      )
                      .join(" | ")
                  : "none"}
              </div>
            </>
          ) : (
            <div>
              <strong>Summary:</strong> No TomTom payload on latest execution yet.
            </div>
          )}
        </div>
      </details>
    </div>
  );
};

const tflDebugFromSummary = (summary = {}) => {
  const s = summary || {};
  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
  const segments = asArray(s?.tfl_path_segments)
    .map((seg) => ({
      id: seg?.id || null,
      from: String(seg?.from_crs || "").toUpperCase() || null,
      via: String(seg?.interchange_crs || "").toUpperCase() || null,
      to: String(seg?.to_crs || "").toUpperCase() || null,
      start: seg?.window_start_utc || null,
      end: seg?.window_end_utc || null,
      lines: asArray(seg?.line_hints).map((x) => String(x || "")).filter(Boolean),
    }))
    .filter((seg) => seg.id || seg.via);
  const hasData = [
    s?.tfl_window_start_utc,
    s?.tfl_window_end_utc,
    toNum(s?.tfl_matched_count),
    toNum(s?.tfl_ignored_count),
    s?.tfl_query_checked_at,
    segments.length,
  ].some((v) => v != null && v !== 0);
  return {
    hasData,
    level: String(s?.tfl_level || "unknown").toUpperCase(),
    windowStart: s?.tfl_window_start_utc || null,
    windowEnd: s?.tfl_window_end_utc || null,
    matchedCount: toNum(s?.tfl_matched_count) ?? 0,
    ignoredCount: toNum(s?.tfl_ignored_count) ?? 0,
    matchReasons: asArray(s?.tfl_match_reasons),
    ignoreReasons: asArray(s?.tfl_ignore_reasons),
    queryCheckedAt: s?.tfl_query_checked_at || null,
    stalenessMins: toNum(s?.tfl_data_staleness_mins),
    impactedLines: asArray(s?.tfl_impacted_lines),
    globalImpactedLines: asArray(s?.tfl_global_impacted_lines),
    segments,
  };
};

const journeyTypeMeta = (row) => {
  const flow = String(row?.flow || "").toLowerCase();
  const direction = String(row?.direction || "").toLowerCase();
  const scheduleKey = String(row?.schedule || "").toUpperCase();
  const airportCode = String(row?.airport_code || "EMA").toUpperCase();
  const airportLabel = airportCode === "LHR" ? "LHR" : "EMA";

  if (airportCode === "LHR") {
    if (
      flow === "return" ||
      scheduleKey === "RETURN_AFTER_ARRIVAL" ||
      direction.startsWith("arriv")
    ) {
      return { label: `Return from ${airportLabel} to home`, cls: "journey-type return" };
    }
    if (flow === "towards_departure" || scheduleKey === "TOWARDS_DEPARTURE") {
      return { label: `Toward ${airportLabel} (Departure)`, cls: "journey-type departure" };
    }
    return { label: `Journey type: ${airportLabel} unknown`, cls: "journey-type unknown" };
  }

  const isReturn =
    flow === "return" ||
    flow === "ema_to_home" ||
    flow.includes("return") ||
    flow.includes("ema_to_home") ||
    direction === "ema_to_home" ||
    direction.includes("return");

  if (isReturn) {
    return { label: "Return from EMA to home", cls: "journey-type return" };
  }
  if (flow === "towards_arrival" || direction.startsWith("arriv")) {
    return { label: "Toward EMA (Arrival pickup)", cls: "journey-type arrival" };
  }
  if (flow === "towards_departure" || direction.startsWith("depart")) {
    return { label: "Toward EMA (Departure)", cls: "journey-type departure" };
  }
  return { label: "Journey type: Unknown", cls: "journey-type unknown" };
};

export default function EmaMonitorDashboardAdmin() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [phase, setPhase] = useState("email");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [dataPath, setDataPath] = useState("");
  const [airportCode, setAirportCode] = useState("EMA");
  const [rows, setRows] = useState([]);
  const [generatedAt, setGeneratedAt] = useState("");
  const [projectionHealth, setProjectionHealth] = useState({
    active_verified: 0,
    missing_projection: 0,
    stale_projection: 0,
  });
  const [notifyReliability, setNotifyReliability] = useState({
    total: 0,
    sent: 0,
    failed: 0,
    deduped: 0,
    policy: 0,
  });
  const [roadCorridors, setRoadCorridors] = useState([]);
  const [roadDetailByKey, setRoadDetailByKey] = useState({});
  const [roadAlerts, setRoadAlerts] = useState([]);
  const [roadAlertEvents, setRoadAlertEvents] = useState([]);
  const [roadAlertSummary, setRoadAlertSummary] = useState({
    open_count: 0,
    red_open_count: 0,
    amber_open_count: 0,
    cleared_count: 0,
    last_evaluated_at: null,
  });
  const [roadNhAlerts, setRoadNhAlerts] = useState([]);
  const [roadNhAlertEvents, setRoadNhAlertEvents] = useState([]);
  const [roadNhAlertSummary, setRoadNhAlertSummary] = useState({
    open_count: 0,
    red_open_count: 0,
    amber_open_count: 0,
    cleared_count: 0,
    last_evaluated_at: null,
  });
  const [roadAlertError, setRoadAlertError] = useState("");
  const [roadLoading, setRoadLoading] = useState(false);
  const [roadError, setRoadError] = useState("");
  const [roadOpen, setRoadOpen] = useState(false);
  const [alertCenterOpen, setAlertCenterOpen] = useState(false);
  const [railCorridors, setRailCorridors] = useState([]);
  const [railDetailByKey, setRailDetailByKey] = useState({});
  const [railAirportBoards, setRailAirportBoards] = useState(null);
  const [railAlerts, setRailAlerts] = useState([]);
  const [railAlertEvents, setRailAlertEvents] = useState([]);
  const [railAlertSummary, setRailAlertSummary] = useState({
    open_count: 0,
    red_open_count: 0,
    amber_open_count: 0,
    light_green_open_count: 0,
    cleared_count: 0,
    last_evaluated_at: null,
  });
  const [railLoading, setRailLoading] = useState(false);
  const [railError, setRailError] = useState("");
  const [railAlertError, setRailAlertError] = useState("");
  const [railOpen, setRailOpen] = useState(false);
  const [airportOpsAlerts, setAirportOpsAlerts] = useState([]);
  const [airportOpsAlertEvents, setAirportOpsAlertEvents] = useState([]);
  const [airportOpsAlertSummary, setAirportOpsAlertSummary] = useState({
    open_count: 0,
    red_open_count: 0,
    amber_open_count: 0,
    light_green_open_count: 0,
    cleared_count: 0,
    last_evaluated_at: null,
  });
  const [airportOpsAlertError, setAirportOpsAlertError] = useState("");
  const [weatherAlerts, setWeatherAlerts] = useState([]);
  const [weatherAlertEvents, setWeatherAlertEvents] = useState([]);
  const [weatherAlertSummary, setWeatherAlertSummary] = useState({
    open_count: 0,
    red_open_count: 0,
    amber_open_count: 0,
    light_green_open_count: 0,
    cleared_count: 0,
    last_evaluated_at: null,
  });
  const [weatherAlertError, setWeatherAlertError] = useState("");
  const [manualAlerts, setManualAlerts] = useState([]);
  const [manualAlertEvents, setManualAlertEvents] = useState([]);
  const [manualAlertSummary, setManualAlertSummary] = useState({
    open_count: 0,
    red_open_count: 0,
    amber_open_count: 0,
    light_green_open_count: 0,
    resolved_count: 0,
    last_evaluated_at: null,
  });
  const [manualAlertError, setManualAlertError] = useState("");
  const [manualAlertForm, setManualAlertForm] = useState({
    title: "",
    category: "external",
    severity: "amber",
    reason_text: "",
    source_url: "",
    linked_system: "airport_ops",
    linked_target_key: "",
    link_type: "possible_cause",
  });
  const [manualActionBusy, setManualActionBusy] = useState(false);
  const [flightOps, setFlightOps] = useState(null);
  const [flightOpsLoading, setFlightOpsLoading] = useState(false);
  const [flightOpsError, setFlightOpsError] = useState("");
  const [flightOpsOpen, setFlightOpsOpen] = useState(false);
  const [unverifiedOpen, setUnverifiedOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [detailByJourney, setDetailByJourney] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const [draftActionLoading, setDraftActionLoading] = useState({});

  const [search, setSearch] = useState("");
  const [amberRedOnly, setAmberRedOnly] = useState(false);
  const [dueSoonOnly, setDueSoonOnly] = useState(false);
  const [hyperOnly, setHyperOnly] = useState(false);
  const [railRiskOnly, setRailRiskOnly] = useState(false);
  const [notifyTelemetry, setNotifyTelemetry] = useState("all");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [pastOpen, setPastOpen] = useState(false);

  const loadData = async (path) => {
    if (!path) return;
    const sep = String(path).includes("?") ? "&" : "?";
    const json = await apiRequest(`${path}${sep}include_unverified=1&airport_code=${encodeURIComponent(airportCode)}`, { method: "GET" });
    setRows(Array.isArray(json.rows) ? json.rows : []);
    setGeneratedAt(json.generated_at || "");
    setProjectionHealth(json?.projection_health || { active_verified: 0, missing_projection: 0, stale_projection: 0 });
    setNotifyReliability(json?.notify_reliability || { total: 0, sent: 0, failed: 0, deduped: 0, policy: 0 });
  };

  const loadRoadCorridors = async (selectedAirportCode = airportCode) => {
    setRoadLoading(true);
    setRoadError("");
    try {
      const json = await apiRequest(`/api/road-corridors/latest?airport_code=${encodeURIComponent(selectedAirportCode)}`, {
        method: "GET",
      });
      setRoadCorridors(Array.isArray(json?.corridors) ? json.corridors : []);
    } catch (err) {
      setRoadCorridors([]);
      setRoadError(err?.message || "Unable to load motorway corridors.");
    } finally {
      setRoadLoading(false);
    }
  };

  const loadRoadCorridorDetails = async (corridors) => {
    const list = Array.isArray(corridors) ? corridors : [];
    if (!list.length) {
      setRoadDetailByKey({});
      return;
    }
    try {
      const results = await Promise.all(
        list.map(async (corridor) => {
          const json = await apiRequest(`/api/road-corridors/${encodeURIComponent(corridor.corridor_key)}/latest`, { method: "GET" });
          return [corridor.corridor_key, json];
        })
      );
      setRoadDetailByKey(Object.fromEntries(results));
    } catch (err) {
      setRoadDetailByKey({});
      setRoadError(err?.message || "Unable to load motorway segment status.");
    }
  };

  const loadRoadAlerts = async (selectedAirportCode = airportCode) => {
    setRoadAlertError("");
    try {
      const json = await apiRequest(
        `/api/road-corridors/airports/${encodeURIComponent(String(selectedAirportCode).toUpperCase())}/alerts/dashboard?current_limit=24&event_limit=12`,
        { method: "GET" }
      );
      setRoadAlerts(Array.isArray(json?.current) ? json.current : []);
      setRoadAlertEvents(Array.isArray(json?.recent_events) ? json.recent_events : []);
      setRoadAlertSummary(
        json?.summary || {
          open_count: 0,
          red_open_count: 0,
          amber_open_count: 0,
          cleared_count: 0,
          last_evaluated_at: null,
        }
      );
      setRoadNhAlerts(Array.isArray(json?.nh_alerts?.current) ? json.nh_alerts.current : []);
      setRoadNhAlertEvents(Array.isArray(json?.nh_alerts?.recent_events) ? json.nh_alerts.recent_events : []);
      setRoadNhAlertSummary(
        json?.nh_alerts?.summary || {
          open_count: 0,
          red_open_count: 0,
          amber_open_count: 0,
          cleared_count: 0,
          last_evaluated_at: null,
        }
      );
    } catch (err) {
      setRoadAlerts([]);
      setRoadAlertEvents([]);
      setRoadAlertSummary({
        open_count: 0,
        red_open_count: 0,
        amber_open_count: 0,
        cleared_count: 0,
        last_evaluated_at: null,
      });
      setRoadNhAlerts([]);
      setRoadNhAlertEvents([]);
      setRoadNhAlertSummary({
        open_count: 0,
        red_open_count: 0,
        amber_open_count: 0,
        cleared_count: 0,
        last_evaluated_at: null,
      });
      setRoadAlertError(err?.message || "Unable to load road alerts.");
    }
  };

  const loadRailCorridors = async (selectedAirportCode = airportCode) => {
    setRailLoading(true);
    setRailError("");
    try {
      const normalizedAirportCode = String(selectedAirportCode).toUpperCase();
      if (normalizedAirportCode === "LHR" || normalizedAirportCode === "EMA") {
        const json = await apiRequest(`/api/rail-corridors/airports/${encodeURIComponent(normalizedAirportCode)}/boards/latest?limit=5`, {
          method: "GET",
        });
        setRailAirportBoards(json || null);
        setRailCorridors([]);
      } else {
        const json = await apiRequest(`/api/rail-corridors/latest?airport_code=${encodeURIComponent(selectedAirportCode)}`, {
          method: "GET",
        });
        setRailAirportBoards(null);
        setRailCorridors(Array.isArray(json?.corridors) ? json.corridors : []);
      }
    } catch (err) {
      setRailAirportBoards(null);
      setRailCorridors([]);
      setRailError(err?.message || "Unable to load rail corridors.");
    } finally {
      setRailLoading(false);
    }
  };

  const loadRailAlerts = async (selectedAirportCode = airportCode) => {
    setRailAlertError("");
    try {
      const json = await apiRequest(
        `/api/rail-corridors/airports/${encodeURIComponent(String(selectedAirportCode).toUpperCase())}/alerts/dashboard?current_limit=24&event_limit=12`,
        { method: "GET" }
      );
      setRailAlerts(Array.isArray(json?.current) ? json.current : []);
      setRailAlertEvents(Array.isArray(json?.recent_events) ? json.recent_events : []);
      setRailAlertSummary(
        json?.summary || {
          open_count: 0,
          red_open_count: 0,
          amber_open_count: 0,
          light_green_open_count: 0,
          cleared_count: 0,
          last_evaluated_at: null,
        }
      );
    } catch (err) {
      setRailAlerts([]);
      setRailAlertEvents([]);
      setRailAlertSummary({
        open_count: 0,
        red_open_count: 0,
        amber_open_count: 0,
        light_green_open_count: 0,
        cleared_count: 0,
        last_evaluated_at: null,
      });
      setRailAlertError(err?.message || "Unable to load rail alerts.");
    }
  };

  const loadFlightOps = async (selectedAirportCode = airportCode) => {
    const normalizedAirportCode = String(selectedAirportCode).toUpperCase();
    if (!["EMA", "LHR"].includes(normalizedAirportCode)) {
      setFlightOps(null);
      setFlightOpsError("");
      return;
    }
    setFlightOpsLoading(true);
    setFlightOpsError("");
    try {
      const url =
        normalizedAirportCode === "EMA"
          ? "/api/ema/v2/journey/monitor/flights/snapshot"
          : "/api/ema/v2/journey/monitor/heathrow/flights/snapshot";
      const json = await apiRequest(url, { method: "GET" });
      setFlightOps(json || null);
    } catch (err) {
      setFlightOps(null);
      setFlightOpsError(err?.message || `Unable to load ${normalizedAirportCode} flights.`);
    } finally {
      setFlightOpsLoading(false);
    }
  };

  const loadAirportOpsAlerts = async (selectedAirportCode = airportCode) => {
    const normalizedAirportCode = String(selectedAirportCode).toUpperCase();
    if (!["EMA", "LHR"].includes(normalizedAirportCode)) {
      setAirportOpsAlerts([]);
      setAirportOpsAlertEvents([]);
      setAirportOpsAlertSummary({
        open_count: 0,
        red_open_count: 0,
        amber_open_count: 0,
        light_green_open_count: 0,
        cleared_count: 0,
        last_evaluated_at: null,
      });
      setAirportOpsAlertError("");
      return;
    }
    setAirportOpsAlertError("");
    try {
      const json = await apiRequest(
        `/api/airport-ops/airports/${encodeURIComponent(normalizedAirportCode)}/alerts/dashboard?current_limit=24&event_limit=12`,
        { method: "GET" }
      );
      setAirportOpsAlerts(Array.isArray(json?.current) ? json.current : []);
      setAirportOpsAlertEvents(Array.isArray(json?.recent_events) ? json.recent_events : []);
      setAirportOpsAlertSummary(
        json?.summary || {
          open_count: 0,
          red_open_count: 0,
          amber_open_count: 0,
          light_green_open_count: 0,
          cleared_count: 0,
          last_evaluated_at: null,
        }
      );
    } catch (err) {
      setAirportOpsAlerts([]);
      setAirportOpsAlertEvents([]);
      setAirportOpsAlertSummary({
        open_count: 0,
        red_open_count: 0,
        amber_open_count: 0,
        light_green_open_count: 0,
        cleared_count: 0,
        last_evaluated_at: null,
      });
      setAirportOpsAlertError(err?.message || `Unable to load ${normalizedAirportCode} airport ops alerts.`);
    }
  };

  const loadWeatherAlerts = async (selectedAirportCode = airportCode) => {
    const normalizedAirportCode = String(selectedAirportCode).toUpperCase();
    if (!["EMA", "LHR"].includes(normalizedAirportCode)) {
      setWeatherAlerts([]);
      setWeatherAlertEvents([]);
      setWeatherAlertSummary({
        open_count: 0,
        red_open_count: 0,
        amber_open_count: 0,
        light_green_open_count: 0,
        cleared_count: 0,
        last_evaluated_at: null,
      });
      setWeatherAlertError("");
      return;
    }
    setWeatherAlertError("");
    try {
      const json = await apiRequest(
        `/api/airport-weather/airports/${encodeURIComponent(normalizedAirportCode)}/alerts/dashboard?current_limit=10&event_limit=12`,
        { method: "GET" }
      );
      setWeatherAlerts(Array.isArray(json?.current) ? json.current : []);
      setWeatherAlertEvents(Array.isArray(json?.recent_events) ? json.recent_events : []);
      setWeatherAlertSummary(
        json?.summary || {
          open_count: 0,
          red_open_count: 0,
          amber_open_count: 0,
          light_green_open_count: 0,
          cleared_count: 0,
          last_evaluated_at: null,
        }
      );
    } catch (err) {
      setWeatherAlerts([]);
      setWeatherAlertEvents([]);
      setWeatherAlertSummary({
        open_count: 0,
        red_open_count: 0,
        amber_open_count: 0,
        light_green_open_count: 0,
        cleared_count: 0,
        last_evaluated_at: null,
      });
      setWeatherAlertError(err?.message || `Unable to load ${normalizedAirportCode} weather alerts.`);
    }
  };

  const loadManualAlerts = async (selectedAirportCode = airportCode) => {
    const normalizedAirportCode = String(selectedAirportCode).toUpperCase();
    if (!["EMA", "LHR"].includes(normalizedAirportCode)) {
      setManualAlerts([]);
      setManualAlertEvents([]);
      setManualAlertSummary({
        open_count: 0,
        red_open_count: 0,
        amber_open_count: 0,
        light_green_open_count: 0,
        resolved_count: 0,
        last_evaluated_at: null,
      });
      setManualAlertError("");
      return;
    }
    setManualAlertError("");
    try {
      const json = await apiRequest(
        `/api/manual-airport-alerts/airports/${encodeURIComponent(normalizedAirportCode)}/dashboard?current_limit=25&event_limit=12`,
        { method: "GET" }
      );
      setManualAlerts(Array.isArray(json?.current) ? json.current : []);
      setManualAlertEvents(Array.isArray(json?.recent_events) ? json.recent_events : []);
      setManualAlertSummary(
        json?.summary || {
          open_count: 0,
          red_open_count: 0,
          amber_open_count: 0,
          light_green_open_count: 0,
          resolved_count: 0,
          last_evaluated_at: null,
        }
      );
    } catch (err) {
      setManualAlerts([]);
      setManualAlertEvents([]);
      setManualAlertSummary({
        open_count: 0,
        red_open_count: 0,
        amber_open_count: 0,
        light_green_open_count: 0,
        resolved_count: 0,
        last_evaluated_at: null,
      });
      setManualAlertError(err?.message || `Unable to load ${normalizedAirportCode} manual alerts.`);
    }
  };

  const submitManualAlert = async (event) => {
    event.preventDefault();
    if (manualActionBusy) return;
    setManualActionBusy(true);
    try {
      const links = manualAlertForm.linked_target_key
        ? [
            {
              linked_system: manualAlertForm.linked_system,
              linked_target_key: manualAlertForm.linked_target_key,
              link_type: manualAlertForm.link_type,
            },
          ]
        : [];
      await apiRequest("/api/admin/manual-airport-alerts/create", {
        method: "POST",
        body: JSON.stringify({
          airport_code: airportCode,
          title: manualAlertForm.title,
          category: manualAlertForm.category,
          severity: manualAlertForm.severity,
          reason_text: manualAlertForm.reason_text,
          source_url: manualAlertForm.source_url || null,
          links,
        }),
      });
      setManualAlertForm({
        title: "",
        category: "external",
        severity: "amber",
        reason_text: "",
        source_url: "",
        linked_system: "airport_ops",
        linked_target_key: "",
        link_type: "possible_cause",
      });
      await loadManualAlerts(airportCode);
    } catch (err) {
      setManualAlertError(err?.message || "Unable to create manual alert.");
    } finally {
      setManualActionBusy(false);
    }
  };

  const resolveManualAlertAction = async (manualAlertId) => {
    if (!manualAlertId || manualActionBusy) return;
    setManualActionBusy(true);
    try {
      await apiRequest(`/api/admin/manual-airport-alerts/${encodeURIComponent(manualAlertId)}/resolve`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await loadManualAlerts(airportCode);
    } catch (err) {
      setManualAlertError(err?.message || "Unable to resolve manual alert.");
    } finally {
      setManualActionBusy(false);
    }
  };

  const loadRailCorridorDetails = async (corridors) => {
    const list = Array.isArray(corridors) ? corridors : [];
    if (!list.length) {
      setRailDetailByKey({});
      return;
    }
    try {
      const results = await Promise.all(
        list.map(async (corridor) => {
          const json = await apiRequest(`/api/rail-corridors/${encodeURIComponent(corridor.corridor_key)}/latest`, { method: "GET" });
          return [corridor.corridor_key, json];
        })
      );
      setRailDetailByKey(Object.fromEntries(results));
    } catch (err) {
      setRailDetailByKey({});
      setRailError(err?.message || "Unable to load rail details.");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const json = await apiRequest("/api/ema/v2/journey/monitor/admin/auth/session", { method: "GET" });
        if (json?.authenticated && json?.dashboard_data_path) {
          setDataPath(json.dashboard_data_path);
          setPhase("dashboard");
          await loadData(json.dashboard_data_path);
          await loadRoadCorridors(airportCode);
          await loadRoadAlerts(airportCode);
          await loadRailCorridors(airportCode);
          await loadRailAlerts(airportCode);
          await loadFlightOps(airportCode);
          await loadAirportOpsAlerts(airportCode);
          await loadWeatherAlerts(airportCode);
          await loadManualAlerts(airportCode);
        }
      } catch (_e) {}
    })();
    return undefined;
  }, [phase, dataPath, airportCode]);

  useEffect(() => {
    document.body.classList.add("ema-admin-mode");
    return () => document.body.classList.remove("ema-admin-mode");
  }, []);

  useEffect(() => {
    loadRoadCorridorDetails(roadCorridors).catch(() => {});
  }, [roadCorridors]);

  useEffect(() => {
    if (railAirportBoards) {
      setRailDetailByKey({});
      return;
    }
    loadRailCorridorDetails(railCorridors).catch(() => {});
  }, [railCorridors, railAirportBoards]);

  const submitEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const json = await apiRequest("/api/ema/v2/journey/monitor/admin/auth/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!json?.success) throw new Error(json?.error || "Request failed");
      setPhase("code");
      setMsg("If authorized, a code has been sent.");
    } catch (err) {
      setMsg(err.message || "Unable to request code.");
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const json = await apiRequest("/api/ema/v2/journey/monitor/admin/auth/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      if (!json?.success || !json?.dashboard_data_path) throw new Error(json?.error || "Verification failed");
      setDataPath(json.dashboard_data_path);
      setPhase("dashboard");
      await loadData(json.dashboard_data_path);
      await loadRoadAlerts(airportCode);
      await loadRoadCorridors(airportCode);
      await loadRailCorridors(airportCode);
      await loadRailAlerts(airportCode);
      await loadFlightOps(airportCode);
      await loadAirportOpsAlerts(airportCode);
      await loadWeatherAlerts(airportCode);
      await loadManualAlerts(airportCode);
    } catch (err) {
      setMsg(err.message || "Invalid or expired code.");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try {
      await apiRequest("/api/ema/v2/journey/monitor/admin/auth/logout", { method: "POST" });
    } catch (_e) {}
    setRows([]);
    setGeneratedAt("");
    setDataPath("");
    setCode("");
    setRoadAlerts([]);
    setRoadAlertEvents([]);
    setRoadAlertSummary({
      open_count: 0,
      red_open_count: 0,
      amber_open_count: 0,
      cleared_count: 0,
      last_evaluated_at: null,
    });
    setRoadNhAlerts([]);
    setRoadNhAlertEvents([]);
    setRailAlerts([]);
    setRailAlertEvents([]);
    setRoadNhAlertSummary({
      open_count: 0,
      red_open_count: 0,
      amber_open_count: 0,
      cleared_count: 0,
      last_evaluated_at: null,
    });
    setRailAlertSummary({
      open_count: 0,
      red_open_count: 0,
      amber_open_count: 0,
      light_green_open_count: 0,
      cleared_count: 0,
      last_evaluated_at: null,
    });
    setAirportOpsAlerts([]);
    setAirportOpsAlertEvents([]);
    setAirportOpsAlertSummary({
      open_count: 0,
      red_open_count: 0,
      amber_open_count: 0,
      light_green_open_count: 0,
      cleared_count: 0,
      last_evaluated_at: null,
    });
    setWeatherAlerts([]);
    setWeatherAlertEvents([]);
    setWeatherAlertSummary({
      open_count: 0,
      red_open_count: 0,
      amber_open_count: 0,
      light_green_open_count: 0,
      cleared_count: 0,
      last_evaluated_at: null,
    });
    setManualAlerts([]);
    setManualAlertEvents([]);
    setManualAlertSummary({
      open_count: 0,
      red_open_count: 0,
      amber_open_count: 0,
      light_green_open_count: 0,
      resolved_count: 0,
      last_evaluated_at: null,
    });
    setRoadAlertError("");
    setRailAlertError("");
    setAirportOpsAlertError("");
    setWeatherAlertError("");
    setManualAlertError("");
    setPhase("email");
    setAirportCode("EMA");
    setDetailByJourney({});
    setDetailLoading({});
  };

  const detailPathForJourney = (journeyId) => {
    const base = String(dataPath || "");
    const m = base.match(/(\/api\/ema\/v2\/journey\/monitor\/dashboard\/secure\/[^/]+)\/data(?:\?.*)?$/);
    if (!m) return null;
    return `${m[1]}/journey/${journeyId}/detail`;
  };

  const loadDetail = async (journeyId, { force = false } = {}) => {
    if (!journeyId || detailLoading[journeyId]) return;
    if (!force && detailByJourney[journeyId]) return;
    const path = detailPathForJourney(journeyId);
    if (!path) return;
    setDetailLoading((p) => ({ ...p, [journeyId]: true }));
    try {
      const json = await apiRequest(path, { method: "GET" });
      if (json?.success) {
        setDetailByJourney((p) => ({ ...p, [journeyId]: json }));
      }
    } catch (_e) {
      setDetailByJourney((p) => ({ ...p, [journeyId]: { error: "Unable to load detail timeline." } }));
    } finally {
      setDetailLoading((p) => ({ ...p, [journeyId]: false }));
    }
  };

  const notificationActionPath = (journeyId, notificationId, action) => {
    const detailPath = detailPathForJourney(journeyId);
    if (!detailPath || !notificationId || !action) return null;
    return `${detailPath.replace(/\/detail$/, "")}/notification/${notificationId}/${action}`;
  };

  const runDraftAction = async (journeyId, notificationId, action, body = null) => {
    const path = notificationActionPath(journeyId, notificationId, action);
    if (!path) return;
    setDraftActionLoading((p) => ({ ...p, [journeyId]: true }));
    try {
      await apiRequest(path, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      });
      await loadDetail(journeyId, { force: true });
    } finally {
      setDraftActionLoading((p) => ({ ...p, [journeyId]: false }));
    }
  };

  const isPastFlight = (row) => {
    if (isReturnGraceWindow(row)) return false;
    const bucket = String(row?.phase_bucket || "").toLowerCase();
    if (bucket === "post_flight") return true;
    const h = Number(row?.hours_to_flight);
    if (Number.isFinite(h)) return h < 0;
    return phaseLabel(row) === "Post flight";
  };

  const filteredRows = useMemo(() => {
    let out = [...rows];
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        [r.email, r.flight_number, r.postcode, r.journey_id]
          .map((x) => String(x || "").toLowerCase())
          .some((x) => x.includes(q))
      );
    }
    if (hyperOnly) out = out.filter((r) => phaseLabel(r).toLowerCase().includes("hyper"));
    if (railRiskOnly) {
      out = out.filter((r) => {
        const rc = String(r?.rail_reason_code || "").toLowerCase();
        return ["path_changed", "arrival_regression", "missed_connection_risk", "crs_overlap_disruption"].includes(rc);
      });
    }
    if (notifyTelemetry !== "all") {
      out = out.filter((r) => {
        const outcome = String(r?.notify_outcome || "").toLowerCase();
        const reason = String(r?.notify_decision_reason || "").toLowerCase();
        if (notifyTelemetry === "sent") return outcome === "sent" || reason === "email_sent";
        if (notifyTelemetry === "failed") return outcome === "failed" || reason === "send_failed";
        if (notifyTelemetry === "deduped") return outcome === "deduped" || reason === "content_deduped";
        if (notifyTelemetry === "policy") return outcome === "suppressed_policy" || reason.includes("slot_") || reason.includes("dedupe_window");
        return true;
      });
    }
    if (amberRedOnly) {
      out = out.filter((r) => {
        const o = overallFromRow(r);
        return o === "amber" || o === "red" || o === "critical_red";
      });
    }
    if (dueSoonOnly) {
      out = out.filter((r) => {
        const t = new Date(r?.next_check_due_at || "").getTime();
        if (!Number.isFinite(t)) return false;
        const mins = (t - Date.now()) / 60000;
        return mins >= -60 && mins <= 60;
      });
    }
    out.sort((a, b) => {
      const anchorDiff = flightAnchorTs(b) - flightAnchorTs(a);
      if (anchorDiff !== 0) return anchorDiff;

      const actionA = deriveAction(a);
      const actionB = deriveAction(b);
      const ar = actionRank(actionB) - actionRank(actionA);
      if (ar !== 0) return ar;

      const notifyA = minutesUntil(a?.next_notification_due_at);
      const notifyB = minutesUntil(b?.next_notification_due_at);
      const an = Number.isFinite(notifyA) ? notifyA : Infinity;
      const bn = Number.isFinite(notifyB) ? notifyB : Infinity;
      if (an !== bn) return an - bn;

      const phaseA = phaseLabel(a).toLowerCase().includes("hyper") ? 1 : 0;
      const phaseB = phaseLabel(b).toLowerCase().includes("hyper") ? 1 : 0;
      if (phaseA !== phaseB) return phaseB - phaseA;

      const oa = RANK[overallFromRow(a)] || 0;
      const ob = RANK[overallFromRow(b)] || 0;
      if (oa !== ob) return ob - oa;

      return String(a?.journey_id || "").localeCompare(String(b?.journey_id || ""));
    });
    return out;
  }, [rows, search, hyperOnly, railRiskOnly, notifyTelemetry, amberRedOnly, dueSoonOnly]);

  const currentRows = useMemo(() => filteredRows.filter((r) => !isPastFlight(r)), [filteredRows]);
  const pastRows = useMemo(() => filteredRows.filter((r) => isPastFlight(r)), [filteredRows]);
  const verifiedCurrentRows = useMemo(() => currentRows.filter((r) => !!r.email_verified), [currentRows]);
  const unverifiedCurrentRows = useMemo(() => currentRows.filter((r) => !r.email_verified), [currentRows]);
  const verifiedPastRows = useMemo(() => pastRows.filter((r) => !!r.email_verified), [pastRows]);
  const visibleUnverifiedCurrentRows = useMemo(
    () => (verifiedOnly ? [] : unverifiedCurrentRows),
    [verifiedOnly, unverifiedCurrentRows]
  );

  const summary = useMemo(() => {
    const active = currentRows.length;
    const requireNotification = currentRows.filter((r) => {
      const action = deriveAction(r);
      return action.includes("Notify user") || action.includes("Manual review");
    }).length;
    const hyper = currentRows.filter((r) => phaseLabel(r).toLowerCase().includes("hyper")).length;
    const schedulerErrors = currentRows.filter((r) => {
      const p = phaseLabel(r);
      const m = minutesUntil(r?.next_check_due_at);
      if (p === "Post flight" || p === "Out of window") return false;
      return m != null && m < 0;
    }).length;
    return { active, requireNotification, hyper, schedulerErrors, past: pastRows.length };
  }, [currentRows, pastRows.length]);

  const latestMonitorRunAt = useMemo(() => {
    let latest = null;
    for (const r of currentRows) {
      const ts = r?.last_monitor_at;
      if (!ts) continue;
      if (!latest || new Date(ts).getTime() > new Date(latest).getTime()) latest = ts;
    }
    return latest;
  }, [currentRows]);

  useEffect(() => {
    if (phase !== "dashboard" || !dataPath) return;
    setDetailByJourney({});
    setDetailLoading({});
    loadData(dataPath).catch(() => {});
    loadRoadCorridors(airportCode).catch(() => {});
    loadRoadAlerts(airportCode).catch(() => {});
    loadRailCorridors(airportCode).catch(() => {});
    loadRailAlerts(airportCode).catch(() => {});
    loadWeatherAlerts(airportCode).catch(() => {});
    loadManualAlerts(airportCode).catch(() => {});
  }, [airportCode]);

  const roadCorridorSummary = useMemo(() => {
    const worst = [...roadCorridors].sort((a, b) => (RANK[b.status_label] || 0) - (RANK[a.status_label] || 0))[0] || null;
    return {
      count: roadCorridors.length,
      worst,
    };
  }, [roadCorridors]);

  const railCorridorSummary = useMemo(() => {
    if (railAirportBoards) {
      const stationBoards = Array.isArray(railAirportBoards.station_boards) ? railAirportBoards.station_boards : [];
      const gatewayBoards = Array.isArray(railAirportBoards.gateway_boards) ? railAirportBoards.gateway_boards : [];
      const lineStatuses = Array.isArray(railAirportBoards.line_statuses) ? railAirportBoards.line_statuses : [];
      const all = [
        ...stationBoards.map((row) => ({
          key: row?.target?.target_key,
          status_label: row?.board?.status_label || row?.target?.effective_status_label || "unknown",
        })),
        ...gatewayBoards.map((row) => ({
          key: row?.target?.target_key,
          status_label: row?.board?.status_label || row?.target?.effective_status_label || "unknown",
        })),
        ...lineStatuses.map((row) => ({
          key: row?.target?.target_key,
          status_label: row?.target?.effective_status_label || "unknown",
        })),
      ];
      const worst = [...all].sort((a, b) => (RANK[b.status_label] || 0) - (RANK[a.status_label] || 0))[0] || null;
      return {
        count: stationBoards.length + gatewayBoards.length,
        worst: worst ? { corridor_key: worst.key, status_label: worst.status_label } : null,
      };
    }
    const worst = [...railCorridors].sort((a, b) => (RANK[b.status_label] || 0) - (RANK[a.status_label] || 0))[0] || null;
    return {
      count: railCorridors.length,
      worst,
    };
  }, [airportCode, railAirportBoards, railCorridors]);

  const activeRoadAlerts = useMemo(
    () => roadAlerts.filter((item) => String(item.status || "").toLowerCase() === "open"),
    [roadAlerts]
  );

  const activeNhAlerts = useMemo(
    () => roadNhAlerts.filter((item) => String(item.status || "").toLowerCase() === "open"),
    [roadNhAlerts]
  );

  const activeRailAlerts = useMemo(
    () => railAlerts.filter((item) => String(item.status || "").toLowerCase() === "open"),
    [railAlerts]
  );

  const activeAirportOpsAlerts = useMemo(
    () => airportOpsAlerts.filter((item) => String(item.status || "").toLowerCase() === "open"),
    [airportOpsAlerts]
  );
  const activeWeatherAlerts = useMemo(
    () => weatherAlerts.filter((item) => String(item.status || "").toLowerCase() === "open"),
    [weatherAlerts]
  );
  const activeManualAlerts = useMemo(
    () => manualAlerts.filter((item) => String(item.status || "").toLowerCase() === "open"),
    [manualAlerts]
  );

  const activeRoadAlertsSorted = useMemo(() => sortAlertsNewestFirst(activeRoadAlerts), [activeRoadAlerts]);
  const activeNhAlertsSorted = useMemo(() => sortAlertsNewestFirst(activeNhAlerts), [activeNhAlerts]);
  const activeRailAlertsSorted = useMemo(() => sortAlertsNewestFirst(activeRailAlerts), [activeRailAlerts]);
  const activeAirportOpsAlertsSorted = useMemo(() => sortAlertsNewestFirst(activeAirportOpsAlerts), [activeAirportOpsAlerts]);
  const activeWeatherAlertsSorted = useMemo(() => sortAlertsNewestFirst(activeWeatherAlerts), [activeWeatherAlerts]);
  const activeManualAlertsSorted = useMemo(() => sortAlertsNewestFirst(activeManualAlerts), [activeManualAlerts]);
  const manualLinksByTarget = useMemo(() => {
    const map = new Map();
    for (const item of activeManualAlerts) {
      const links = Array.isArray(item?.links_json) ? item.links_json : [];
      for (const link of links) {
        const key = `${String(link?.linked_system || "").toLowerCase()}:${String(link?.linked_target_key || "")}`;
        if (!key || key.endsWith(":")) continue;
        const arr = map.get(key) || [];
        arr.push({
          title: item.title,
          link_type: link?.link_type || "related",
        });
        map.set(key, arr);
      }
    }
    return map;
  }, [activeManualAlerts]);

  const totalOpenAlerts =
    (roadAlertSummary.open_count || 0) +
    (roadNhAlertSummary.open_count || 0) +
    (railAlertSummary.open_count || 0) +
    (airportOpsAlertSummary.open_count || 0) +
    (weatherAlertSummary.open_count || 0) +
    (manualAlertSummary.open_count || 0);

  const currentTable = useMemo(
    () =>
      verifiedCurrentRows.flatMap((r) => {
        const t = r?.monitor_targets || {};
        const cStats = checkpointStats(r);
        const phaseTxt = phaseLabel(r);
        const journeyType = journeyTypeMeta(r);
        const detail = detailByJourney[r.journey_id];
        const latestExec = asArray(detail?.executions)[0] || null;
        const latestEval = asArray(detail?.evaluations)[0] || null;
        const latestSources = latestEval?.sources || {};
        const freeRailSrc = latestSources?.free_rail || {};
        const latestSummary = latestExec?.summary || {};
        const railDebug = railDebugFromDetail(detail);
        const railMonReason = latestSummary?.rail_monitoring_reason || "N/A";
        const railDisruptionLevel = String(latestSummary?.free_rail_level || "unknown").toUpperCase();
        const railFeasStatus = String(latestSummary?.rail_feasibility_status || "unknown").toLowerCase();
        const railFeasMinsLate = Number(latestSummary?.rail_feasibility_mins_late);
        const railPhase = String(latestSummary?.rail_phase || "unknown").toLowerCase();
        const railAction = String(latestSummary?.rail_action || "keep_selected").toLowerCase();
        const roadPhase = String(latestSummary?.road_phase || "unknown").toLowerCase();
        const roadAction = String(latestSummary?.road_action || "monitor_all_legs").toLowerCase();
        const selectedDepart = latestSummary?.rail_selected_depart_utc || null;
        const selectedArrive = latestSummary?.rail_selected_arrive_utc || null;
        const selectedPath = String(latestSummary?.rail_selected_path || "").trim();
        const selectedFingerprint = String(latestSummary?.rail_selected_journey_fingerprint || "").trim();
        const selectedLegs = asArray(latestSummary?.rail_selected_legs).map((leg) => ({
          from: String(leg?.from || leg?.from_crs || "").toUpperCase() || "N/A",
          to: String(leg?.to || leg?.to_crs || "").toUpperCase() || "N/A",
          depart: leg?.depart_utc || leg?.depart || null,
          arrive: leg?.arrive_utc || leg?.arrive || null,
          realtime: String(leg?.realtime || "UNKNOWN").toUpperCase(),
        }));
        const altDepart = latestSummary?.rail_alt_depart_utc || null;
        const altArrive = latestSummary?.rail_alt_arrive_utc || null;
        const altPath = String(latestSummary?.rail_alt_path || "").trim();
        const altDelta = Number(latestSummary?.rail_alt_target_delta_mins);
        const altViable = latestSummary?.rail_alt_viable === true;
        const railFeasText =
          railFeasStatus === "missed_target"
            ? `MISSED_TARGET${Number.isFinite(railFeasMinsLate) ? ` (+${Math.round(railFeasMinsLate)}m)` : ""}`
            : railFeasStatus === "at_risk"
            ? "AT_RISK"
            : railFeasStatus === "on_time"
            ? "ON_TIME"
            : String(latestSummary?.rail_feasibility_status || "unknown").toUpperCase();
        const railAffected = asArray(latestSummary?.free_rail_affected_crs);
        const railChecked = freeRailSrc?.checked === true ? "yes" : "no";
        const railStale = freeRailSrc?.stale === true ? "yes" : "no";
        const railError = freeRailSrc?.error || null;
        const overall = overallFromRow(r);
        const action = deriveAction(r);
        const nextCheck = relativeWithDot(r?.next_check_due_at, "check");
        const nextNotify = relativeWithDot(r?.next_notification_due_at, "notify");
        const open = !!expanded[r.journey_id];
        const airportCode = String(r?.airport_code || "EMA").toUpperCase();
        const journeyText =
          airportCode === "LHR"
            ? `${r?.flight_number || "N/A"} • ${r?.postcode || "N/A"}`
            : `${r?.flight_number || "N/A"} • ${r?.postcode || "N/A"} • ${String(r?.direction || "—").slice(0, 6)}`;
        const progressPct = cStats.total > 0 ? Math.round((cStats.done / cStats.total) * 100) : 0;
        const lastChange = r?.last_monitor_at || cStats.latestTs || null;

        const rowNode = (
          <tr key={r.journey_id}>
            <td>{r.email || "N/A"}</td>
            <td>
              <strong>{journeyText}</strong>
              <div className={journeyType.cls}>{journeyType.label}</div>
              <div className="muted">{phaseTxt}</div>
              <div className="muted">{`Departure (UK): ${formatDt(r?.anchor_utc)}`}</div>
            </td>
            <td><span className={pillClass(overall)}>{overall.toUpperCase()}</span></td>
            <td>
              <span
                className={`action ${action.includes("Manual") ? "bad" : action.includes("Notify") ? "warn" : "ok"}`}
                title={actionReason(r)}
              >
                {action}
              </span>
            </td>
            {SOURCE_KEYS.map((k) => {
              const st = sourceState(r, k);
              return (
                <td key={`${r.journey_id}-${k}`}>
                  <span className={st.className} title={k === "flight" ? flightStatusTitle(r) : undefined}>
                    {st.text}
                  </span>
                  {k === "rail" && r?.rail_reason_code && r.rail_reason_code !== "none" && (
                    <div className="muted" style={{ marginTop: 4 }}>
                      <span className="pill warn" style={{ textTransform: "none", letterSpacing: 0 }}>
                        {r.rail_reason_code}
                      </span>
                    </div>
                  )}
                </td>
              );
            })}
            <td>
              <div className={`rel ${nextCheck.cls}`}>{nextCheck.text}</div>
            </td>
            <td>
              <div className={`rel ${nextNotify.cls}`}>{nextNotify.text}</div>
            </td>
            <td>
              <div className="phase-pill">{phaseTxt}</div>
              {r?.hyper_strict_anchor_lock && (
                <div className="muted" style={{ marginTop: 4 }}>
                  <span className="pill warn" style={{ textTransform: "none", letterSpacing: 0 }}>
                    HYPER_LOCK
                  </span>
                </div>
              )}
              <div className="bar"><span style={{ width: `${progressPct}%` }} /></div>
              <div className="muted">{`${cStats.done}/${cStats.total} done`}</div>
              <div className="muted">{`Last checkpoint: ${formatDt(cStats.latestTs || r?.last_monitor_at)}`}</div>
            </td>
            <td>
              <div><strong>Roads:</strong> {shortTargets(t.roads, 3)}</div>
              <div><strong>CRS:</strong> {shortTargets(t.crs, 3)}</div>
              <div><strong>Weather:</strong> {shortTargets(t.weather_locations, 2)}</div>
              <div>
                <strong>TfL:</strong>{" "}
                {(t.tfl_relevant || sourceState(r, "tfl").level !== "unknown")
                  ? shortTargets(t.tfl_crs || t.crs, 3)
                  : "No"}
              </div>
            </td>
            <td>
              <button
                className="btn tiny"
                type="button"
                onClick={() => {
                  const next = !open;
                  setExpanded((p) => ({ ...p, [r.journey_id]: next }));
                  if (next) loadDetail(r.journey_id);
                }}
              >
                {open ? "Hide" : "Open"}
              </button>
            </td>
            <td>
              <div>{formatDt(lastChange)}</div>
              <div className="muted">{sinceText(lastChange)}</div>
            </td>
          </tr>
        );

        if (!open) return [rowNode];
        const expNode = (
          <tr key={`${r.journey_id}-exp`} className="expanded-row">
            <td colSpan={16}>
              <div className="expanded-title">{overallWhy(r)}</div>
              <div className="expanded-grid">
                <div><strong>Journey ID:</strong> {r.journey_id}</div>
                <div><strong>Next rule:</strong> {r.next_notification_rule || "N/A"}</div>
                <div><strong>Strict hyper lock:</strong> {r?.hyper_strict_anchor_lock ? "ON" : "off"}</div>
                <div><strong>Last monitor:</strong> {formatDt(r.last_monitor_at)}</div>
                <div><strong>Last notify:</strong> {formatDt(r.last_notified_at)}</div>
                <div><strong>Targets roads:</strong> {(t.roads || []).join(", ") || "N/A"}</div>
                <div><strong>Targets CRS:</strong> {(t.crs || []).join(", ") || "N/A"}</div>
                <div><strong>Rail reason:</strong> {r.rail_reason_code || "none"}{r.rail_reason_at ? ` (${formatDt(r.rail_reason_at)})` : ""}</div>
                <div>
                  <strong>Rail network signal:</strong> {railDisruptionLevel}
                </div>
                <div>
                  <strong>Rail feasibility:</strong> {railFeasText}
                </div>
                <div>
                  <strong>Rail monitor reason:</strong> {railMonReason}
                </div>
                <div>
                  <strong>Rail affected CRS:</strong> {railAffected.length ? railAffected.join(", ") : "none"}
                </div>
                <div>
                  <strong>Rail check context:</strong>{" "}
                  {`checked=${railChecked}, stale=${railStale}, paid_rail_ran=${latestSummary?.paid_rail_ran === true ? "yes" : "no"}${
                    railError ? `, error=${railError}` : ""
                  }`}
                </div>
                <div>
                  <strong>Rail debug:</strong>{" "}
                  {`time=${railDebug.timeRelevantCount}, path=${railDebug.pathOverlapCount}, major=${railDebug.majorOverlapCount}${
                    railDebug.reasonCodes.length ? `, reasons=${railDebug.reasonCodes.join(", ")}` : ""
                  }`}
                </div>
                <div>
                  <strong>Rail timeline:</strong>{" "}
                  {railDebug.timeline?.rail_depart_utc || railDebug.timeline?.rail_arrive_utc
                    ? `${formatDt(railDebug.timeline?.rail_depart_utc)} -> ${formatDt(railDebug.timeline?.rail_arrive_utc)}`
                    : "N/A"}
                </div>
                <div>
                  <strong>Selected train:</strong>{" "}
                  {selectedDepart || selectedArrive ? `${formatDt(selectedDepart)} -> ${formatDt(selectedArrive)}` : "N/A"}
                </div>
                <div>
                  <strong>Selected path:</strong> {selectedPath || "N/A"}{selectedFingerprint ? ` (${selectedFingerprint.slice(0, 12)})` : ""}
                </div>
                <div>
                  <strong>Rail phase:</strong> {monitoringPhaseLabel(railPhase)}
                </div>
                <div>
                  <strong>Rail action:</strong> {String(railAction || "keep_selected").toUpperCase()}
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <strong>Rail assurance:</strong>{" "}
                  {`level=${String(latestSummary?.rail_assurance_level || "unknown").toUpperCase()}, source=${
                    latestSummary?.rail_assurance_source || "N/A"
                  }, used=${latestSummary?.rail_assurance_used_in_decision === true ? "yes" : "no"}, bias=${
                    latestSummary?.rail_assurance_decision_bias || "N/A"
                  }, state=${latestSummary?.rail_assurance_operational_state || "N/A"}, board_alignment=${
                    latestSummary?.rail_assurance_board_alignment || "N/A"
                  }, threshold=${latestSummary?.rail_assurance_threshold_crossed || "N/A"}, confidence=${
                    latestSummary?.rail_assurance_confidence || "N/A"
                  }, weight=${Number.isFinite(Number(latestSummary?.rail_assurance_weight)) ? Number(latestSummary.rail_assurance_weight) : "N/A"}, nr_weight=${
                    Number.isFinite(Number(latestSummary?.rail_nr_weight)) ? Number(latestSummary.rail_nr_weight) : "N/A"
                  }, live_board_weight=${
                    Number.isFinite(Number(latestSummary?.rail_live_board_weight)) ? Number(latestSummary.rail_live_board_weight) : "N/A"
                  }${latestSummary?.rail_nr_deweighted_reason ? `, nr_deweighted=${latestSummary.rail_nr_deweighted_reason}` : ""}`}
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <strong>Selected leg status:</strong>{" "}
                  {selectedLegs.length
                    ? selectedLegs
                        .slice(0, 6)
                        .map((leg) => `${leg.from}->${leg.to} ${leg.realtime} ${formatDt(leg.depart)} -> ${formatDt(leg.arrive)}`)
                        .join(" | ")
                    : "N/A"}
                </div>
                <div>
                  <strong>Alt candidate:</strong>{" "}
                  {altDepart || altArrive
                    ? `${formatDt(altDepart)} -> ${formatDt(altArrive)}${altPath ? ` (${altPath})` : ""}${
                        Number.isFinite(altDelta) ? ` (delta ${Math.round(altDelta)}m)` : ""
                      }${altViable ? " [viable]" : ""}`
                    : "none"}
                </div>
                <div>
                  <strong>Rail incidents:</strong>{" "}
                  {railDebug.incidents.length
                    ? railDebug.incidents
                        .slice(0, 3)
                        .map(
                          (it) =>
                            `${it.crs || "N/A"} ${String(it.incident_severity_norm || "low").toUpperCase()} ${String(
                              it.path_overlap_level || "none"
                            ).toUpperCase()} ov=${Number.isFinite(Number(it.time_overlap_mins)) ? `${it.time_overlap_mins}m` : "n/a"} impact=${
                              it.final_impact || "fyi"
                            }`
                        )
                        .join(" | ")
                    : "none"}
                </div>
                <div>
                  <strong>Road phase:</strong> {monitoringPhaseLabel(roadPhase)}
                </div>
                <div>
                  <strong>Road action:</strong> {String(roadAction || "monitor_all_legs").toUpperCase()}
                </div>
                {(() => {
                  const roadDbg = roadDebugFromSummary(latestSummary);
                  if (!roadDbg.hasData) return null;
                  return (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <details className="detail-collapsible">
                        <summary>Road query debug</summary>
                        <div className="muted" style={{ marginTop: 8, display: "grid", gap: 6 }}>
                          {deriveNhDriverSummary(detail) ? (
                            <div>
                              <strong>NH driver:</strong> {deriveNhDriverSummary(detail)}
                            </div>
                          ) : null}
                          <div>
                            <strong>Monitoring policy:</strong>{" "}
                            {`level=${roadDbg.monitoringLevel}, raw=${roadDbg.rawLevel}, network=${roadDbg.networkLevel}, nh=${roadDbg.nhLevel}, feasibility=${roadDbg.feasibilityStatus}, reason=${roadDbg.reason}`}
                          </div>
                          <div>
                            <strong>Traffic plan:</strong>{" "}
                            {`guidance=${roadDbg.guidanceTrafficMins ?? "N/A"}m, prev=${roadDbg.previousTrafficMins ?? "N/A"}m, curr=${roadDbg.currentTrafficMins ?? "N/A"}m, drift=${roadDbg.driftMins ?? "N/A"}m, guidance_drift=${roadDbg.guidanceDriftMins ?? "N/A"}m, delay_vs_base=${roadDbg.delayFromBaseMins ?? "N/A"}m, margin=${roadDbg.marginMins ?? "N/A"}m`}
                          </div>
                          <div>
                            <strong>Guidance window:</strong>{" "}
                            {`depart_at=${roadDbg.guidanceDepartAt ? formatDt(roadDbg.guidanceDepartAt) : "N/A"}, arrive_at=${roadDbg.guidanceArriveAt ? formatDt(roadDbg.guidanceArriveAt) : "N/A"}`}
                          </div>
                          <div>
                            <strong>Primary:</strong>{" "}
                            {`depart_at=${roadDbg.primary.departAt ? formatDt(roadDbg.primary.departAt) : "N/A"}, base=${roadDbg.primary.baseMins ?? "N/A"}m, traffic=${
                              roadDbg.primary.trafficMins ?? "N/A"
                            }m, buffer=${roadDbg.primary.safetyBufferMins ?? "N/A"}m, parking=${roadDbg.primary.parkingTransferMins ?? "N/A"}m, final_eta_safe=${
                              roadDbg.primary.finalEtaSafeMins ?? "N/A"
                            }m`}
                          </div>
                          <div>
                            <strong>Stabilization:</strong>{" "}
                            {`passes=${roadDbg.primary.stabilizationPasses ?? "N/A"}, delta=${roadDbg.primary.stabilizationDeltaMins ?? "N/A"}m`}
                          </div>
                          <div>
                            <strong>Self-drive:</strong>{" "}
                            {`depart_at=${roadDbg.drive.departAt ? formatDt(roadDbg.drive.departAt) : "N/A"}, base=${roadDbg.drive.baseMins ?? "N/A"}m, traffic=${
                              roadDbg.drive.trafficMins ?? "N/A"
                            }m, buffer=${roadDbg.drive.safetyBufferMins ?? "N/A"}m, parking=${roadDbg.drive.parkingTransferMins ?? "N/A"}m, final_eta_safe=${
                              roadDbg.drive.finalEtaSafeMins ?? "N/A"
                            }m`}
                          </div>
                          <div>
                            <strong>Taxi:</strong>{" "}
                            {`depart_at=${roadDbg.taxi.departAt ? formatDt(roadDbg.taxi.departAt) : "N/A"}, base=${roadDbg.taxi.baseMins ?? "N/A"}m, traffic=${
                              roadDbg.taxi.trafficMins ?? "N/A"
                            }m, buffer=${roadDbg.taxi.safetyBufferMins ?? "N/A"}m, parking=${roadDbg.taxi.parkingTransferMins ?? "N/A"}m, final_eta_safe=${
                              roadDbg.taxi.finalEtaSafeMins ?? "N/A"
                            }m`}
                          </div>
                          <div>
                            <strong>Baseline route:</strong>{" "}
                            {`locked_at=${roadDbg.baseline.lockedAt ? formatDt(roadDbg.baseline.lockedAt) : "N/A"}, mode=${
                              roadDbg.baseline.selectedMode || "N/A"
                            }, provider=${roadDbg.baseline.routeProvider || "N/A"}, summary=${roadDbg.baseline.routeSummary || "N/A"}`}
                          </div>
                          <div>
                            <strong>Baseline live timing:</strong>{" "}
                            {`depart_at=${roadDbg.baseline.departAt ? formatDt(roadDbg.baseline.departAt) : "N/A"}, arrive_at=${
                              roadDbg.baseline.arriveAt ? formatDt(roadDbg.baseline.arriveAt) : "N/A"
                            }, planned=${roadDbg.baseline.finalEtaSafeMins ?? "N/A"}m, now=${roadDbg.baseline.nowMins ?? "N/A"}m, delta=${
                              roadDbg.baseline.deltaVsPlannedMins ?? "N/A"
                            }m, status=${roadDbg.baseline.status}`}
                          </div>
                          <div>
                            <strong>Alternative route:</strong>{" "}
                            {`mode=${roadDbg.alternative.selectedMode || "N/A"}, available=${roadDbg.alternative.available ? "yes" : "no"}, provider=${
                              roadDbg.alternative.routeProvider || "N/A"
                            }, summary=${roadDbg.alternative.routeSummary || "N/A"}`}
                          </div>
                          <div>
                            <strong>Savings:</strong>{" "}
                            {`alternative_now=${roadDbg.alternative.nowMins ?? "N/A"}m, savings=${roadDbg.alternative.savingsMins ?? "N/A"}m, margin_gain=${
                              roadDbg.alternative.marginGainMins ?? "N/A"
                            }m, diverged=${roadDbg.divergence.changed ? "yes" : "no"}, divergence_type=${roadDbg.divergence.type || "N/A"}, threshold=${
                              roadDbg.divergence.thresholdMins ?? "N/A"
                            }m`}
                          </div>
                          <div>
                            <strong>Reason / source driver:</strong>{" "}
                            {`baseline_reason=${roadDbg.baseline.reasonCode || "N/A"} (${roadDbg.baseline.reasonText || "N/A"}), alternative_reason=${
                              roadDbg.alternative.reasonCode || "N/A"
                            } (${roadDbg.alternative.reasonText || "N/A"}), source_driver=${roadDbg.baseline.sourceDriver || "N/A"}`}
                          </div>
                          <div>
                            <strong>Road assurance:</strong>{" "}
                            {`level=${roadDbg.assurance.level}, source=${roadDbg.assurance.source || "N/A"}, used=${
                              roadDbg.assurance.usedInDecision ? "yes" : "no"
                            }, bias=${roadDbg.assurance.decisionBias || "N/A"}, threshold=${
                              roadDbg.assurance.thresholdCrossed || "N/A"
                            }, confidence=${roadDbg.assurance.confidence || "N/A"}, weight=${
                              roadDbg.assurance.weight != null ? roadDbg.assurance.weight : "N/A"
                            }, alt_actionable=${roadDbg.assurance.altActionable ? "yes" : "no"}, reason=${
                              roadDbg.assurance.reasonCode || "N/A"
                            } (${roadDbg.assurance.reasonText || "N/A"})`}
                          </div>
                          <div>
                            <strong>Road labels:</strong>{" "}
                            {`baseline_major=${roadDbg.baseline.majorRoads.length ? roadDbg.baseline.majorRoads.join(", ") : "none"}, baseline_monitored=${
                              roadDbg.baseline.monitoredRoads.length ? roadDbg.baseline.monitoredRoads.join(", ") : "none"
                            }, baseline_causes=${
                              roadDbg.baseline.primaryCauseRoads.length ? roadDbg.baseline.primaryCauseRoads.join(", ") : "none"
                            }, alternative_causes=${
                              roadDbg.alternative.primaryCauseRoads.length ? roadDbg.alternative.primaryCauseRoads.join(", ") : "none"
                            }`}
                          </div>
                          <div>
                            <strong>Phase 1 messages:</strong>{" "}
                            {`${roadDbg.customerMessages.primary || "N/A"}${roadDbg.customerMessages.alternative ? ` | ${roadDbg.customerMessages.alternative}` : ""}`}
                          </div>
                        </div>
                      </details>
                    </div>
                  );
                })()}
                {renderTomTomDebugBlock(latestSummary)}
                {(() => {
                  const tflDbg = tflDebugFromSummary(latestSummary);
                  return (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <details className="detail-collapsible">
                        <summary>TfL query debug</summary>
                        <div className="muted" style={{ marginTop: 8, display: "grid", gap: 6 }}>
                          <div>
                            <strong>Decision:</strong>{" "}
                            {`level=${tflDbg.level}, matched=${tflDbg.matchedCount}, ignored=${tflDbg.ignoredCount}, impacted=${tflDbg.impactedLines.length ? tflDbg.impactedLines.join(", ") : "none"}`}
                          </div>
                          <div>
                            <strong>Window:</strong>{" "}
                            {`${tflDbg.windowStart ? formatDt(tflDbg.windowStart) : "N/A"} -> ${tflDbg.windowEnd ? formatDt(tflDbg.windowEnd) : "N/A"}`}
                          </div>
                          <div>
                            <strong>Segments:</strong>{" "}
                            {tflDbg.segments.length
                              ? tflDbg.segments
                                  .slice(0, 4)
                                  .map(
                                    (seg) =>
                                      `${seg.from || "N/A"}->${seg.via || "N/A"}->${seg.to || "N/A"} (${seg.start ? formatDt(seg.start) : "N/A"}..${
                                        seg.end ? formatDt(seg.end) : "N/A"
                                      })`
                                  )
                                  .join(" | ")
                              : "none"}
                          </div>
                          <div>
                            <strong>Reasons:</strong>{" "}
                            {`match=[${tflDbg.matchReasons.length ? tflDbg.matchReasons.join(", ") : "none"}], ignore=[${
                              tflDbg.ignoreReasons.length ? tflDbg.ignoreReasons.join(", ") : "none"
                            }]`}
                          </div>
                          <div>
                            <strong>Query:</strong>{" "}
                            {`checked_at=${tflDbg.queryCheckedAt ? formatDt(tflDbg.queryCheckedAt) : "N/A"}, staleness=${
                              tflDbg.stalenessMins != null ? `${tflDbg.stalenessMins}m` : "N/A"
                            }, global_impacted=${tflDbg.globalImpactedLines.length ? tflDbg.globalImpactedLines.join(", ") : "none"}`}
                          </div>
                        </div>
                      </details>
                    </div>
                  );
                })()}
              </div>
              <div style={{ marginTop: 10 }}>
                <details className="detail-collapsible">
                  <summary>Road checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = roadChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading road checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No road checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`road-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • roads: ${run.roads.length}`}
                                {` • NH calls: ${Number(run.nhCalls?.calls || 0)}`}
                                {Number(run.nhCalls?.errors || 0) > 0 ? ` • NH errors: ${Number(run.nhCalls.errors)}` : ""}
                                {Number(run.runMeta?.nhMatchedCount || 0) > 0
                                  ? ` • NH matched: ${Number(run.runMeta.nhMatchedCount)} (maj:${run.runMeta?.nhImpactCounts?.major || 0}, adv:${run.runMeta?.nhImpactCounts?.advisory || 0}, fyi:${run.runMeta?.nhImpactCounts?.fyi || 0}, strong:${run.runMeta?.nhImpactCounts?.strong || 0}, time:${run.runMeta?.nhImpactCounts?.timeRelevant || 0})`
                                  : ""}
                                {run.runMeta?.roadNhDue === false ? " • NH not due" : ""}
                                {run.runMeta?.nhChecked === false ? " • NH unchecked" : ""}
                                {run.runMeta?.nhError ? ` • NH ${run.runMeta.nhError}` : ""}
                              </span>
                            </div>
                            {run.roads.map((road) => (
                              <div key={`${run.executionId || idx}-${road.road}`} className="road-line">
                                <span className="road-name">{road.road}</span>
                                <span className={pillClass(road.level)}>{String(road.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`duration: ${road.durationMins != null ? `${road.durationMins}m` : "N/A"}`}</span>
                                {road.timeline && (
                                  <span className="muted">
                                    {`timeline: ${road.timeline.enterAt ? formatDt(road.timeline.enterAt) : "N/A"} -> ${
                                      road.timeline.exitAt ? formatDt(road.timeline.exitAt) : "N/A"
                                    } (${road.timeline.durationMins != null ? `${road.timeline.durationMins}m` : "N/A"}, ${
                                      road.timeline.distanceKm != null ? `${road.timeline.distanceKm}km` : "N/A"
                                    })`}
                                  </span>
                                )}
                                <span className="muted">
                                  {`route dir: ${
                                    road.routeDirections.length
                                      ? road.routeDirections.join(", ")
                                      : road.routeDirectionsParsed.length
                                      ? `${road.routeDirectionsParsed.join(", ")} (from segment key)`
                                      : road.timeline?.directions?.length
                                      ? `${road.timeline.directions.join(", ")} (from step vector)`
                                      : "N/A"
                                  }`}
                                </span>
                                <span className="muted">
                                  {`junctions: ${
                                    road.routeJunctions.length
                                      ? road.routeJunctions.join(", ")
                                      : road.routeJunctionsParsed.length
                                      ? `${road.routeJunctionsParsed.join(", ")} (from segment key)`
                                      : road.timeline?.junctions?.length
                                      ? `${road.timeline.junctions.join(", ")} (from step instruction)`
                                      : road.timeline?.inferredJunctions?.length
                                      ? `${road.timeline.inferredJunctions.join(", ")} (inferred)`
                                      : "N/A"
                                  }`}
                                </span>
                                <span className="muted">{`junction span: ${road.routeJunctionSpan || "N/A"}`}</span>
                                <span className="muted">
                                  {`NH events: ${road.nhEventCount}`}
                                  {road.nhDirections.length ? ` (${road.nhDirections.join(", ")})` : ""}
                                  {road.nhEventCountRaw > road.nhEventCount
                                    ? ` [filtered ${road.nhEventCount}/${road.nhEventCountRaw}]`
                                    : ""}
                                </span>
                                {road.nhSegmentOverlapRatio != null && (
                                  <span className="muted">{`NH overlap: ${Math.round(road.nhSegmentOverlapRatio * 100)}%`}</span>
                                )}
                                {(road.nhOverlapScore != null || road.nhDirectionMatch != null || road.nhSegmentMatch != null) && (
                                  <span className="muted">
                                    {`NH diag: score=${road.nhOverlapScore != null ? road.nhOverlapScore : "N/A"}${
                                      road.nhOverlapConfidence ? ` (${road.nhOverlapConfidence})` : ""
                                    }, dir_match=${ynna(road.nhDirectionMatch)}, seg_match=${ynna(road.nhSegmentMatch)}`}
                                  </span>
                                )}
                                {road.nhMatches?.length > 0 && (
                                  <span className="muted">
                                    {`NH map: ${road.nhMatches
                                      .slice(0, 3)
                                      .map((m) => {
                                        const dir = m.direction || "N/A";
                                        const seg = m.from || m.to ? `${m.from || "?"}->${m.to || "?"}` : "N/A";
                                        const dist = m.minDistanceKm != null ? `${m.minDistanceKm}km` : "N/A";
                                        const overlap = Number.isFinite(Number(m.timeOverlapMins)) ? `${Number(m.timeOverlapMins)}m` : "n/a";
                                        return `${m.impact || "fyi"} ${dir} ${seg} d=${dist} s=${
                                          m.overlapScore != null ? m.overlapScore : "N/A"
                                        } t=${m.timeRelevant ? "yes" : "no"} ov=${overlap} dm=${ynna(m.directionMatch)} jm=${ynna(
                                          m.junctionOverlap
                                        )}`;
                                      })
                                      .join(" | ")}`}
                                  </span>
                                )}
                                <span className="muted">
                                  {`flags: NH_due=${
                                    road.nhDue === true
                                      ? "yes"
                                      : road.nhDue === false
                                      ? "no"
                                      : run.runMeta?.roadNhDue === true
                                      ? "yes"
                                      : run.runMeta?.roadNhDue === false
                                      ? "no"
                                      : "?"
                                  }, Google_due=${
                                    road.googleDue === true
                                      ? "yes"
                                      : road.googleDue === false
                                      ? "no"
                                      : run.runMeta?.roadGoogleDue === true
                                      ? "yes"
                                      : run.runMeta?.roadGoogleDue === false
                                      ? "no"
                                      : "?"
                                  }, paid_road=${
                                    road.paidRoadRan === true
                                      ? "yes"
                                      : road.paidRoadRan === false
                                      ? "no"
                                      : run.runMeta?.paidRoadRan === true
                                      ? "yes"
                                      : "no"
                                  }`}
                                </span>
                                <span className="muted">{`decision: ${road.decision || "—"} / ${road.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>
                <details className="detail-collapsible" style={{ marginTop: 8 }}>
                  <summary>Rail checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = railChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading rail checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No rail checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`rail-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • CRS: ${run.crsRows.length}`}
                                {` • NR calls: ${Number(run.railCalls?.nr?.calls || 0)}`}
                                {Number(run.railCalls?.nr?.errors || 0) > 0 ? ` • NR errors: ${Number(run.railCalls.nr.errors)}` : ""}
                                {` • RailData calls: ${Number(run.railCalls?.raildata?.calls || 0)}`}
                                {Number(run.railCalls?.raildata?.errors || 0) > 0
                                  ? ` • RailData errors: ${Number(run.railCalls.raildata.errors)}`
                                  : ""}
                                {run.runMeta?.paidRailRan === true ? " • NR paid: yes" : " • NR paid: no"}
                                {run.runMeta?.checked === true
                                  ? " • RailData checked: yes"
                                  : run.runMeta?.stale === true
                                  ? " • RailData checked: no (stale)"
                                  : " • RailData checked: no"}
                                {run.runMeta?.railMonitoringReason ? ` • reason: ${run.runMeta.railMonitoringReason}` : ""}
                                {run.runMeta?.error ? ` • free_rail_error: ${run.runMeta.error}` : ""}
                                {run.runMeta?.coverageStatus
                                  ? ` • coverage: ${run.runMeta.coverageStatus} (ok=${Number(run.runMeta?.checkedOkCount || 0)}, err=${Number(
                                      run.runMeta?.checkedErrorCount || 0
                                    )}${run.runMeta?.checkedErrorCrs?.length ? `, crs=${run.runMeta.checkedErrorCrs.join(",")}` : ""})`
                                  : ""}
                                {` • dbg: time=${Number(run.runMeta?.timeRelevantCount || 0)}, path=${Number(
                                  run.runMeta?.pathOverlapCount || 0
                                )}, major=${Number(run.runMeta?.majorOverlapCount || 0)}`}
                                {` • paid_incidents=${Number(run.runMeta?.paidIncidentCount || 0)} (used=${
                                  run.runMeta?.paidIncidentSignalUsed ? "yes" : "no"
                                }, ignored=${run.runMeta?.paidIncidentSignalIgnored ? "yes" : "no"})`}
                                {run.runMeta?.reasonCodes?.length ? ` • reasons: ${run.runMeta.reasonCodes.join(", ")}` : ""}
                              </span>
                            </div>
                            {run.runMeta?.timeline?.rail_depart_utc || run.runMeta?.timeline?.rail_arrive_utc ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`timeline: ${formatDt(run.runMeta?.timeline?.rail_depart_utc)} -> ${formatDt(
                                  run.runMeta?.timeline?.rail_arrive_utc
                                )}`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR journey: ${formatDt(run.runMeta.nrJourney.depart)} -> ${formatDt(run.runMeta.nrJourney.arrive)} (${
                                  run.runMeta.nrJourney.durationMins != null ? `${run.runMeta.nrJourney.durationMins}m` : "N/A"
                                }, changes=${run.runMeta.nrJourney.changes != null ? run.runMeta.nrJourney.changes : "N/A"}, realtime=${
                                  run.runMeta.nrJourney.realtime || "N/A"
                                }${
                                  Number.isFinite(Number(run.runMeta.nrJourney.targetDeltaMins))
                                    ? `, target_delta=${Math.round(Number(run.runMeta.nrJourney.targetDeltaMins))}m`
                                    : ""
                                }, disruption=${String(run.runMeta.nrDisruptionLevel || "unknown").toUpperCase()})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrLastChecked ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR snapshot: ${formatDt(run.runMeta.nrLastChecked)} (${sinceText(run.runMeta.nrLastChecked) || "just now"})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney?.legs?.length ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR legs: ${run.runMeta.nrJourney.legs
                                  .map(
                                    (leg) =>
                                      `${leg.from}->${leg.to} ${leg.realtime} ${formatDt(leg.depart)} -> ${formatDt(leg.arrive)}`
                                  )
                                  .join(" | ")}`}
                              </div>
                            ) : null}
                            {run.runMeta?.railBaselineOutcome?.status ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`Baseline train outcome: ${String(run.runMeta.railBaselineOutcome.status || "unknown").toUpperCase()}${
                                  run.runMeta.railBaselineOutcome.final ? " (final)" : ""
                                }${
                                  run.runMeta.railBaselineOutcome.seenAt
                                    ? `, seen_at=${formatDt(run.runMeta.railBaselineOutcome.seenAt)}`
                                    : ""
                                }${
                                  run.runMeta.railMonitoringMode
                                    ? `, mode=${String(run.runMeta.railMonitoringMode).toUpperCase()}`
                                    : ""
                                }${
                                  run.runMeta.railActiveUntil
                                    ? `, active_until=${formatDt(run.runMeta.railActiveUntil)}`
                                    : ""
                                }${
                                  run.runMeta.railPostWindowContextOnly ? ", advisory_context_only=yes" : ""
                                }`}
                              </div>
                            ) : null}
                            {run.runMeta?.railLiveBoard?.enabled ? (
                              <div className="muted" style={{ marginBottom: 6, display: "grid", gap: 4 }}>
                                <div>
                                  {`Live board assurance: checked=${run.runMeta.railLiveBoard.checked ? "yes" : "no"}, reason=${
                                    run.runMeta.railLiveBoard.reason || "N/A"
                                  }${
                                    run.runMeta.railLiveBoard.checkedAt
                                      ? `, checked_at=${formatDt(run.runMeta.railLiveBoard.checkedAt)}`
                                      : ""
                                  }`}
                                </div>
                                {run.runMeta?.railLiveBoard?.summary ? (
                                  <div>
                                    {`Board summary: current=${run.runMeta.railLiveBoard.summary.current_leg || "N/A"}, next=${
                                      run.runMeta.railLiveBoard.summary.next_leg || "N/A"
                                    }, matched=${Number(run.runMeta.railLiveBoard.summary.matched_count || 0)}/${
                                      Number(run.runMeta.railLiveBoard.summary.total_queries || 0)
                                    }, delayed=${Number(run.runMeta.railLiveBoard.summary.delayed_count || 0)}, cancelled=${
                                      Number(run.runMeta.railLiveBoard.summary.cancelled_count || 0)
                                    }, alignment=${String(run.runMeta.railLiveBoard.summary.alignment || "unknown").toUpperCase()}`}
                                  </div>
                                ) : null}
                                {run.runMeta?.railLiveBoard?.queries?.length ? (
                                  <div>
                                    {`Board queries: ${run.runMeta.railLiveBoard.queries
                                      .map((q) => {
                                        const route = q?.target_from && q?.target_to ? `${q.target_from}->${q.target_to}` : "N/A";
                                        const target =
                                          q?.target_depart_local || q?.target_arrive_local
                                            ? `${q.target_depart_local || "?"}/${q.target_arrive_local || "?"}`
                                            : "N/A";
                                        const actual =
                                          q?.std || q?.estimate ? `${q.std || "?"}/${q.estimate || "?"}` : "N/A";
                                        const status = q?.cancelled ? "CANCELLED" : q?.status || "UNKNOWN";
                                        return `${q.crs || "N/A"} ${q.role || "query"} ${route} target=${target} actual=${actual} status=${status} score=${
                                          q?.match_score != null ? q.match_score : "0"
                                        }${q?.error ? ` err=${q.error}` : ""}`;
                                      })
                                      .join(" | ")}`}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                            {!run.crsRows.length && run.runMeta?.nrJourney?.legs?.length ? (
                              <div style={{ display: "grid", gap: 4, marginBottom: 6 }}>
                                {run.runMeta.nrJourney.legs.map((leg, legIdx) => {
                                  const level = railRealtimeLevel(leg.realtime);
                                  return (
                                    <div key={`${run.executionId || idx}-nr-leg-${legIdx}`} className="road-line">
                                      <span className="road-name">{`${leg.from}->${leg.to}`}</span>
                                      <span className={pillClass(level)}>{String(level || "unknown").toUpperCase()}</span>
                                      <span className="muted">{`realtime: ${leg.realtime || "N/A"}`}</span>
                                      <span className="muted">
                                        {`journey: ${formatDt(leg.depart)} -> ${formatDt(leg.arrive)}`}
                                      </span>
                                      <span className="muted">{`operator: ${leg.operator || "N/A"}`}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                            {!run.crsRows.length && !run.runMeta?.nrJourney?.legs?.length && run.runMeta?.railLiveBoard?.queries?.length ? (
                              <div style={{ display: "grid", gap: 4, marginBottom: 6 }}>
                                {run.runMeta.railLiveBoard.queries.map((q, qIdx) => {
                                  const level = railRealtimeLevel(q?.status || q?.estimate);
                                  const route =
                                    q?.target_from && q?.target_to ? `${q.target_from}->${q.target_to}` : q?.crs || "N/A";
                                  const target =
                                    q?.target_depart_local || q?.target_arrive_local
                                      ? `${q.target_depart_local || "?"}/${q.target_arrive_local || "?"}`
                                      : "N/A";
                                  const actual = q?.std || q?.estimate ? `${q.std || "?"}/${q.estimate || "?"}` : "N/A";
                                  return (
                                    <div key={`${run.executionId || idx}-board-q-${qIdx}`} className="road-line">
                                      <span className="road-name">{`${q?.crs || "N/A"} ${q?.role || "query"} ${route}`}</span>
                                      <span className={pillClass(level)}>{String(level || "unknown").toUpperCase()}</span>
                                      <span className="muted">{`target: ${target}`}</span>
                                      <span className="muted">{`actual: ${actual}`}</span>
                                      <span className="muted">{`status: ${q?.cancelled ? "CANCELLED" : q?.status || "UNKNOWN"}`}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                            {run.crsRows.map((row) => (
                              <div key={`${run.executionId || idx}-${row.crs}`} className="road-line">
                                <span className="road-name">{row.crs}</span>
                                <span className={pillClass(row.level)}>{String(row.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`affected: ${row.affected ? "yes" : "no"}`}</span>
                                <span className="muted">{`incidents: ${row.incidentCount}`}</span>
                                <span className="muted">{`dbg: time=${row.timeRelevantCount}, path=${row.pathOverlapCount}, major=${row.majorOverlapCount}`}</span>
                                {(row.departUtc || row.arriveUtc) && (
                                  <span className="muted">
                                    {`journey: ${formatDt(row.departUtc)} -> ${formatDt(row.arriveUtc)} (${
                                      row.durationMins != null ? `${row.durationMins}m` : "N/A"
                                    }, changes=${row.changes != null ? row.changes : "N/A"})`}
                                  </span>
                                )}
                                {row.incidents?.length > 0 && (
                                  <span className="muted">
                                    {`inc map: ${row.incidents
                                      .slice(0, 3)
                                      .map((it) => {
                                        const sev = String(it?.incident_severity_norm || "low").toUpperCase();
                                        const path = String(it?.path_overlap_level || "none").toUpperCase();
                                        const ov = Number.isFinite(Number(it?.time_overlap_mins)) ? `${Number(it.time_overlap_mins)}m` : "n/a";
                                        return `${sev}/${path} ov=${ov} impact=${it?.final_impact || "fyi"} (${
                                          it?.start_utc ? formatDt(it.start_utc) : "N/A"
                                        } -> ${it?.end_utc ? formatDt(it.end_utc) : "N/A"})`;
                                      })
                                      .join(" | ")}`}
                                  </span>
                                )}
                                <span className="muted">{`decision: ${row.decision || "—"} / ${row.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>
                <details className="detail-collapsible" style={{ marginTop: 8 }}>
                  <summary>Rail checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = railChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading rail checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No rail checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`rail-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • CRS: ${run.crsRows.length}`}
                                {` • NR calls: ${Number(run.railCalls?.nr?.calls || 0)}`}
                                {Number(run.railCalls?.nr?.errors || 0) > 0 ? ` • NR errors: ${Number(run.railCalls.nr.errors)}` : ""}
                                {` • RailData calls: ${Number(run.railCalls?.raildata?.calls || 0)}`}
                                {Number(run.railCalls?.raildata?.errors || 0) > 0
                                  ? ` • RailData errors: ${Number(run.railCalls.raildata.errors)}`
                                  : ""}
                                {run.runMeta?.paidRailRan === true ? " • NR paid: yes" : " • NR paid: no"}
                                {run.runMeta?.checked === true
                                  ? " • RailData checked: yes"
                                  : run.runMeta?.stale === true
                                  ? " • RailData checked: no (stale)"
                                  : " • RailData checked: no"}
                                {run.runMeta?.railMonitoringReason ? ` • reason: ${run.runMeta.railMonitoringReason}` : ""}
                                {run.runMeta?.error ? ` • free_rail_error: ${run.runMeta.error}` : ""}
                                {run.runMeta?.coverageStatus
                                  ? ` • coverage: ${run.runMeta.coverageStatus} (ok=${Number(run.runMeta?.checkedOkCount || 0)}, err=${Number(
                                      run.runMeta?.checkedErrorCount || 0
                                    )}${run.runMeta?.checkedErrorCrs?.length ? `, crs=${run.runMeta.checkedErrorCrs.join(",")}` : ""})`
                                  : ""}
                                {` • dbg: time=${Number(run.runMeta?.timeRelevantCount || 0)}, path=${Number(
                                  run.runMeta?.pathOverlapCount || 0
                                )}, major=${Number(run.runMeta?.majorOverlapCount || 0)}`}
                                {` • paid_incidents=${Number(run.runMeta?.paidIncidentCount || 0)} (used=${
                                  run.runMeta?.paidIncidentSignalUsed ? "yes" : "no"
                                }, ignored=${run.runMeta?.paidIncidentSignalIgnored ? "yes" : "no"})`}
                                {run.runMeta?.reasonCodes?.length ? ` • reasons: ${run.runMeta.reasonCodes.join(", ")}` : ""}
                              </span>
                            </div>
                            {run.runMeta?.timeline?.rail_depart_utc || run.runMeta?.timeline?.rail_arrive_utc ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`timeline: ${formatDt(run.runMeta?.timeline?.rail_depart_utc)} -> ${formatDt(
                                  run.runMeta?.timeline?.rail_arrive_utc
                                )}`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR journey: ${formatDt(run.runMeta.nrJourney.depart)} -> ${formatDt(run.runMeta.nrJourney.arrive)} (${
                                  run.runMeta.nrJourney.durationMins != null ? `${run.runMeta.nrJourney.durationMins}m` : "N/A"
                                }, changes=${run.runMeta.nrJourney.changes != null ? run.runMeta.nrJourney.changes : "N/A"}, realtime=${
                                  run.runMeta.nrJourney.realtime || "N/A"
                                }${
                                  Number.isFinite(Number(run.runMeta.nrJourney.targetDeltaMins))
                                    ? `, target_delta=${Math.round(Number(run.runMeta.nrJourney.targetDeltaMins))}m`
                                    : ""
                                }, disruption=${String(run.runMeta.nrDisruptionLevel || "unknown").toUpperCase()})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrLastChecked ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR snapshot: ${formatDt(run.runMeta.nrLastChecked)} (${sinceText(run.runMeta.nrLastChecked) || "just now"})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney?.legs?.length ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR legs: ${run.runMeta.nrJourney.legs
                                  .map(
                                    (leg) =>
                                      `${leg.from}->${leg.to} ${leg.realtime} ${formatDt(leg.depart)} -> ${formatDt(leg.arrive)}`
                                  )
                                  .join(" | ")}`}
                              </div>
                            ) : null}
                            {run.runMeta?.railBaselineOutcome?.status ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`Baseline train outcome: ${String(run.runMeta.railBaselineOutcome.status || "unknown").toUpperCase()}${
                                  run.runMeta.railBaselineOutcome.final ? " (final)" : ""
                                }${
                                  run.runMeta.railBaselineOutcome.seenAt
                                    ? `, seen_at=${formatDt(run.runMeta.railBaselineOutcome.seenAt)}`
                                    : ""
                                }${
                                  run.runMeta.railMonitoringMode
                                    ? `, mode=${String(run.runMeta.railMonitoringMode).toUpperCase()}`
                                    : ""
                                }${
                                  run.runMeta.railActiveUntil
                                    ? `, active_until=${formatDt(run.runMeta.railActiveUntil)}`
                                    : ""
                                }${
                                  run.runMeta.railPostWindowContextOnly ? ", advisory_context_only=yes" : ""
                                }`}
                              </div>
                            ) : null}
                            {run.runMeta?.railLiveBoard?.enabled ? (
                              <div className="muted" style={{ marginBottom: 6, display: "grid", gap: 4 }}>
                                <div>
                                  {`Live board assurance: checked=${run.runMeta.railLiveBoard.checked ? "yes" : "no"}, reason=${
                                    run.runMeta.railLiveBoard.reason || "N/A"
                                  }${
                                    run.runMeta.railLiveBoard.checkedAt
                                      ? `, checked_at=${formatDt(run.runMeta.railLiveBoard.checkedAt)}`
                                      : ""
                                  }`}
                                </div>
                                {run.runMeta?.railLiveBoard?.summary ? (
                                  <div>
                                    {`Board summary: current=${run.runMeta.railLiveBoard.summary.current_leg || "N/A"}, next=${
                                      run.runMeta.railLiveBoard.summary.next_leg || "N/A"
                                    }, matched=${Number(run.runMeta.railLiveBoard.summary.matched_count || 0)}/${
                                      Number(run.runMeta.railLiveBoard.summary.total_queries || 0)
                                    }, delayed=${Number(run.runMeta.railLiveBoard.summary.delayed_count || 0)}, cancelled=${
                                      Number(run.runMeta.railLiveBoard.summary.cancelled_count || 0)
                                    }, alignment=${String(run.runMeta.railLiveBoard.summary.alignment || "unknown").toUpperCase()}`}
                                  </div>
                                ) : null}
                                {run.runMeta?.railLiveBoard?.queries?.length ? (
                                  <div>
                                    {`Board queries: ${run.runMeta.railLiveBoard.queries
                                      .map((q) => {
                                        const route = q?.target_from && q?.target_to ? `${q.target_from}->${q.target_to}` : "N/A";
                                        const target =
                                          q?.target_depart_local || q?.target_arrive_local
                                            ? `${q.target_depart_local || "?"}/${q.target_arrive_local || "?"}`
                                            : "N/A";
                                        const actual =
                                          q?.std || q?.estimate ? `${q.std || "?"}/${q.estimate || "?"}` : "N/A";
                                        const status = q?.cancelled ? "CANCELLED" : q?.status || "UNKNOWN";
                                        return `${q.crs || "N/A"} ${q.role || "query"} ${route} target=${target} actual=${actual} status=${status} score=${
                                          q?.match_score != null ? q.match_score : "0"
                                        }${q?.error ? ` err=${q.error}` : ""}`;
                                      })
                                      .join(" | ")}`}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                            {run.crsRows.map((row) => (
                              <div key={`${run.executionId || idx}-${row.crs}`} className="road-line">
                                <span className="road-name">{row.crs}</span>
                                <span className={pillClass(row.level)}>{String(row.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`affected: ${row.affected ? "yes" : "no"}`}</span>
                                <span className="muted">{`incidents: ${row.incidentCount}`}</span>
                                <span className="muted">{`dbg: time=${row.timeRelevantCount}, path=${row.pathOverlapCount}, major=${row.majorOverlapCount}`}</span>
                                {(row.departUtc || row.arriveUtc) && (
                                  <span className="muted">
                                    {`journey: ${formatDt(row.departUtc)} -> ${formatDt(row.arriveUtc)} (${
                                      row.durationMins != null ? `${row.durationMins}m` : "N/A"
                                    }, changes=${row.changes != null ? row.changes : "N/A"})`}
                                  </span>
                                )}
                                {row.incidents?.length > 0 && (
                                  <span className="muted">
                                    {`incidents: ${row.incidents
                                      .map((i) => {
                                        const sev = String(i?.severity || i?.impact || "unknown").toUpperCase();
                                        const title = i?.summary || i?.title || i?.reason || "incident";
                                        const start = i?.start_at ? formatDt(i.start_at) : "N/A";
                                        const end = i?.end_at ? formatDt(i.end_at) : "N/A";
                                        return `${sev} ${title} (${start}..${end})`;
                                      })
                                      .join(" | ")}`}
                                  </span>
                                )}
                                <span className="muted">{`decision: ${row.decision || "—"} / ${row.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>
                <details className="detail-collapsible" style={{ marginTop: 8 }}>
                  <summary>Rail checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = railChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading rail checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No rail checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`rail-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • CRS: ${run.crsRows.length}`}
                                {` • NR calls: ${Number(run.railCalls?.nr?.calls || 0)}`}
                                {Number(run.railCalls?.nr?.errors || 0) > 0 ? ` • NR errors: ${Number(run.railCalls.nr.errors)}` : ""}
                                {` • RailData calls: ${Number(run.railCalls?.raildata?.calls || 0)}`}
                                {Number(run.railCalls?.raildata?.errors || 0) > 0
                                  ? ` • RailData errors: ${Number(run.railCalls.raildata.errors)}`
                                  : ""}
                                {run.runMeta?.paidRailRan === true ? " • NR paid: yes" : " • NR paid: no"}
                                {run.runMeta?.checked === true
                                  ? " • RailData checked: yes"
                                  : run.runMeta?.stale === true
                                  ? " • RailData checked: no (stale)"
                                  : " • RailData checked: no"}
                                {run.runMeta?.railMonitoringReason ? ` • reason: ${run.runMeta.railMonitoringReason}` : ""}
                                {run.runMeta?.error ? ` • free_rail_error: ${run.runMeta.error}` : ""}
                                {run.runMeta?.coverageStatus
                                  ? ` • coverage: ${run.runMeta.coverageStatus} (ok=${Number(run.runMeta?.checkedOkCount || 0)}, err=${Number(
                                      run.runMeta?.checkedErrorCount || 0
                                    )}${run.runMeta?.checkedErrorCrs?.length ? `, crs=${run.runMeta.checkedErrorCrs.join(",")}` : ""})`
                                  : ""}
                                {` • dbg: time=${Number(run.runMeta?.timeRelevantCount || 0)}, path=${Number(
                                  run.runMeta?.pathOverlapCount || 0
                                )}, major=${Number(run.runMeta?.majorOverlapCount || 0)}`}
                                {` • paid_incidents=${Number(run.runMeta?.paidIncidentCount || 0)} (used=${
                                  run.runMeta?.paidIncidentSignalUsed ? "yes" : "no"
                                }, ignored=${run.runMeta?.paidIncidentSignalIgnored ? "yes" : "no"})`}
                                {run.runMeta?.reasonCodes?.length ? ` • reasons: ${run.runMeta.reasonCodes.join(", ")}` : ""}
                              </span>
                            </div>
                            {run.runMeta?.timeline?.rail_depart_utc || run.runMeta?.timeline?.rail_arrive_utc ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`timeline: ${formatDt(run.runMeta?.timeline?.rail_depart_utc)} -> ${formatDt(
                                  run.runMeta?.timeline?.rail_arrive_utc
                                )}`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR journey: ${formatDt(run.runMeta.nrJourney.depart)} -> ${formatDt(run.runMeta.nrJourney.arrive)} (${
                                  run.runMeta.nrJourney.durationMins != null ? `${run.runMeta.nrJourney.durationMins}m` : "N/A"
                                }, changes=${run.runMeta.nrJourney.changes != null ? run.runMeta.nrJourney.changes : "N/A"}, realtime=${
                                  run.runMeta.nrJourney.realtime || "N/A"
                                }${
                                  Number.isFinite(Number(run.runMeta.nrJourney.targetDeltaMins))
                                    ? `, target_delta=${Math.round(Number(run.runMeta.nrJourney.targetDeltaMins))}m`
                                    : ""
                                }, disruption=${String(run.runMeta.nrDisruptionLevel || "unknown").toUpperCase()})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrLastChecked ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR snapshot: ${formatDt(run.runMeta.nrLastChecked)} (${sinceText(run.runMeta.nrLastChecked) || "just now"})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney?.legs?.length ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR legs: ${run.runMeta.nrJourney.legs
                                  .map(
                                    (leg) =>
                                      `${leg.from}->${leg.to} ${leg.realtime} ${formatDt(leg.depart)} -> ${formatDt(leg.arrive)}`
                                  )
                                  .join(" | ")}`}
                              </div>
                            ) : null}
                            {run.crsRows.map((row) => (
                              <div key={`${run.executionId || idx}-${row.crs}`} className="road-line">
                                <span className="road-name">{row.crs}</span>
                                <span className={pillClass(row.level)}>{String(row.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`affected: ${row.affected ? "yes" : "no"}`}</span>
                                <span className="muted">{`incidents: ${row.incidentCount}`}</span>
                                <span className="muted">{`dbg: time=${row.timeRelevantCount}, path=${row.pathOverlapCount}, major=${row.majorOverlapCount}`}</span>
                                {(row.departUtc || row.arriveUtc) && (
                                  <span className="muted">
                                    {`journey: ${formatDt(row.departUtc)} -> ${formatDt(row.arriveUtc)} (${
                                      row.durationMins != null ? `${row.durationMins}m` : "N/A"
                                    }, changes=${row.changes != null ? row.changes : "N/A"})`}
                                  </span>
                                )}
                                <span className="muted">{`decision: ${row.decision || "—"} / ${row.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>

                <details className="detail-collapsible" style={{ marginTop: 8 }}>
                  <summary>Rail checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = railChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading rail checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No rail checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`rail-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • CRS: ${run.crsRows.length}`}
                                {` • NR calls: ${Number(run.railCalls?.nr?.calls || 0)}`}
                                {Number(run.railCalls?.nr?.errors || 0) > 0 ? ` • NR errors: ${Number(run.railCalls.nr.errors)}` : ""}
                                {` • RailData calls: ${Number(run.railCalls?.raildata?.calls || 0)}`}
                                {Number(run.railCalls?.raildata?.errors || 0) > 0
                                  ? ` • RailData errors: ${Number(run.railCalls.raildata.errors)}`
                                  : ""}
                                {run.runMeta?.paidRailRan === true ? " • NR paid: yes" : " • NR paid: no"}
                                {run.runMeta?.checked === true
                                  ? " • RailData checked: yes"
                                  : run.runMeta?.stale === true
                                  ? " • RailData checked: no (stale)"
                                  : " • RailData checked: no"}
                                {run.runMeta?.railMonitoringReason ? ` • reason: ${run.runMeta.railMonitoringReason}` : ""}
                                {run.runMeta?.error ? ` • free_rail_error: ${run.runMeta.error}` : ""}
                                {run.runMeta?.coverageStatus
                                  ? ` • coverage: ${run.runMeta.coverageStatus} (ok=${Number(run.runMeta?.checkedOkCount || 0)}, err=${Number(
                                      run.runMeta?.checkedErrorCount || 0
                                    )}${run.runMeta?.checkedErrorCrs?.length ? `, crs=${run.runMeta.checkedErrorCrs.join(",")}` : ""})`
                                  : ""}
                                {` • dbg: time=${Number(run.runMeta?.timeRelevantCount || 0)}, path=${Number(
                                  run.runMeta?.pathOverlapCount || 0
                                )}, major=${Number(run.runMeta?.majorOverlapCount || 0)}`}
                                {` • paid_incidents=${Number(run.runMeta?.paidIncidentCount || 0)} (used=${
                                  run.runMeta?.paidIncidentSignalUsed ? "yes" : "no"
                                }, ignored=${run.runMeta?.paidIncidentSignalIgnored ? "yes" : "no"})`}
                                {run.runMeta?.reasonCodes?.length ? ` • reasons: ${run.runMeta.reasonCodes.join(", ")}` : ""}
                              </span>
                            </div>
                            {run.runMeta?.timeline?.rail_depart_utc || run.runMeta?.timeline?.rail_arrive_utc ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`timeline: ${formatDt(run.runMeta?.timeline?.rail_depart_utc)} -> ${formatDt(
                                  run.runMeta?.timeline?.rail_arrive_utc
                                )}`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR journey: ${formatDt(run.runMeta.nrJourney.depart)} -> ${formatDt(run.runMeta.nrJourney.arrive)} (${
                                  run.runMeta.nrJourney.durationMins != null ? `${run.runMeta.nrJourney.durationMins}m` : "N/A"
                                }, changes=${run.runMeta.nrJourney.changes != null ? run.runMeta.nrJourney.changes : "N/A"}, realtime=${
                                  run.runMeta.nrJourney.realtime || "N/A"
                                }${
                                  Number.isFinite(Number(run.runMeta.nrJourney.targetDeltaMins))
                                    ? `, target_delta=${Math.round(Number(run.runMeta.nrJourney.targetDeltaMins))}m`
                                    : ""
                                }, disruption=${String(run.runMeta.nrDisruptionLevel || "unknown").toUpperCase()})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrLastChecked ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR snapshot: ${formatDt(run.runMeta.nrLastChecked)} (${sinceText(run.runMeta.nrLastChecked) || "just now"})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney?.legs?.length ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR legs: ${run.runMeta.nrJourney.legs
                                  .map(
                                    (leg) =>
                                      `${leg.from}->${leg.to} ${leg.realtime} ${formatDt(leg.depart)} -> ${formatDt(leg.arrive)}`
                                  )
                                  .join(" | ")}`}
                              </div>
                            ) : null}
                            {run.runMeta?.railBaselineOutcome?.status ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`Baseline train outcome: ${String(run.runMeta.railBaselineOutcome.status || "unknown").toUpperCase()}${
                                  run.runMeta.railBaselineOutcome.final ? " (final)" : ""
                                }${
                                  run.runMeta.railBaselineOutcome.seenAt
                                    ? `, seen_at=${formatDt(run.runMeta.railBaselineOutcome.seenAt)}`
                                    : ""
                                }${
                                  run.runMeta.railMonitoringMode
                                    ? `, mode=${String(run.runMeta.railMonitoringMode).toUpperCase()}`
                                    : ""
                                }${
                                  run.runMeta.railActiveUntil
                                    ? `, active_until=${formatDt(run.runMeta.railActiveUntil)}`
                                    : ""
                                }${
                                  run.runMeta.railPostWindowContextOnly ? ", advisory_context_only=yes" : ""
                                }`}
                              </div>
                            ) : null}
                            {run.runMeta?.railLiveBoard?.enabled ? (
                              <div className="muted" style={{ marginBottom: 6, display: "grid", gap: 4 }}>
                                <div>
                                  {`Live board assurance: checked=${run.runMeta.railLiveBoard.checked ? "yes" : "no"}, reason=${
                                    run.runMeta.railLiveBoard.reason || "N/A"
                                  }${
                                    run.runMeta.railLiveBoard.checkedAt
                                      ? `, checked_at=${formatDt(run.runMeta.railLiveBoard.checkedAt)}`
                                      : ""
                                  }`}
                                </div>
                                {run.runMeta?.railLiveBoard?.summary ? (
                                  <div>
                                    {`Board summary: current=${run.runMeta.railLiveBoard.summary.current_leg || "N/A"}, next=${
                                      run.runMeta.railLiveBoard.summary.next_leg || "N/A"
                                    }, matched=${Number(run.runMeta.railLiveBoard.summary.matched_count || 0)}/${
                                      Number(run.runMeta.railLiveBoard.summary.total_queries || 0)
                                    }, delayed=${Number(run.runMeta.railLiveBoard.summary.delayed_count || 0)}, cancelled=${
                                      Number(run.runMeta.railLiveBoard.summary.cancelled_count || 0)
                                    }, alignment=${String(run.runMeta.railLiveBoard.summary.alignment || "unknown").toUpperCase()}`}
                                  </div>
                                ) : null}
                                {run.runMeta?.railLiveBoard?.queries?.length ? (
                                  <div>
                                    {`Board queries: ${run.runMeta.railLiveBoard.queries
                                      .map((q) => {
                                        const route = q?.target_from && q?.target_to ? `${q.target_from}->${q.target_to}` : "N/A";
                                        const target =
                                          q?.target_depart_local || q?.target_arrive_local
                                            ? `${q.target_depart_local || "?"}/${q.target_arrive_local || "?"}`
                                            : "N/A";
                                        const actual =
                                          q?.std || q?.estimate ? `${q.std || "?"}/${q.estimate || "?"}` : "N/A";
                                        const status = q?.cancelled ? "CANCELLED" : q?.status || "UNKNOWN";
                                        return `${q.crs || "N/A"} ${q.role || "query"} ${route} target=${target} actual=${actual} status=${status} score=${
                                          q?.match_score != null ? q.match_score : "0"
                                        }${q?.error ? ` err=${q.error}` : ""}`;
                                      })
                                      .join(" | ")}`}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                            {run.crsRows.map((row) => (
                              <div key={`${run.executionId || idx}-${row.crs}`} className="road-line">
                                <span className="road-name">{row.crs}</span>
                                <span className={pillClass(row.level)}>{String(row.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`affected: ${row.affected ? "yes" : "no"}`}</span>
                                <span className="muted">{`incidents: ${row.incidentCount}`}</span>
                                <span className="muted">{`dbg: time=${row.timeRelevantCount}, path=${row.pathOverlapCount}, major=${row.majorOverlapCount}`}</span>
                                {(row.departUtc || row.arriveUtc) && (
                                  <span className="muted">
                                    {`journey: ${formatDt(row.departUtc)} -> ${formatDt(row.arriveUtc)} (${
                                      row.durationMins != null ? `${row.durationMins}m` : "N/A"
                                    }, changes=${row.changes != null ? row.changes : "N/A"})`}
                                  </span>
                                )}
                                {row.incidents?.length > 0 && (
                                  <span className="muted">
                                    {`inc map: ${row.incidents
                                      .slice(0, 3)
                                      .map((it) => {
                                        const sev = String(it?.incident_severity_norm || "low").toUpperCase();
                                        const path = String(it?.path_overlap_level || "none").toUpperCase();
                                        const ov = Number.isFinite(Number(it?.time_overlap_mins)) ? `${Number(it.time_overlap_mins)}m` : "n/a";
                                        return `${sev}/${path} ov=${ov} impact=${it?.final_impact || "fyi"} (${
                                          it?.start_utc ? formatDt(it.start_utc) : "N/A"
                                        } -> ${it?.end_utc ? formatDt(it.end_utc) : "N/A"})`;
                                      })
                                      .join(" | ")}`}
                                  </span>
                                )}
                                <span className="muted">{`decision: ${row.decision || "—"} / ${row.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>

                <details className="detail-collapsible" style={{ marginTop: 8 }}>
                  <summary>Rail checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = railChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading rail checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No rail checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`rail-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • CRS: ${run.crsRows.length}`}
                                {` • NR calls: ${Number(run.railCalls?.nr?.calls || 0)}`}
                                {Number(run.railCalls?.nr?.errors || 0) > 0 ? ` • NR errors: ${Number(run.railCalls.nr.errors)}` : ""}
                                {` • RailData calls: ${Number(run.railCalls?.raildata?.calls || 0)}`}
                                {Number(run.railCalls?.raildata?.errors || 0) > 0
                                  ? ` • RailData errors: ${Number(run.railCalls.raildata.errors)}`
                                  : ""}
                                {run.runMeta?.paidRailRan === true ? " • NR paid: yes" : " • NR paid: no"}
                                {run.runMeta?.checked === true
                                  ? " • RailData checked: yes"
                                  : run.runMeta?.stale === true
                                  ? " • RailData checked: no (stale)"
                                  : " • RailData checked: no"}
                                {run.runMeta?.railMonitoringReason ? ` • reason: ${run.runMeta.railMonitoringReason}` : ""}
                                {run.runMeta?.error ? ` • free_rail_error: ${run.runMeta.error}` : ""}
                                {run.runMeta?.coverageStatus
                                  ? ` • coverage: ${run.runMeta.coverageStatus} (ok=${Number(run.runMeta?.checkedOkCount || 0)}, err=${Number(
                                      run.runMeta?.checkedErrorCount || 0
                                    )}${run.runMeta?.checkedErrorCrs?.length ? `, crs=${run.runMeta.checkedErrorCrs.join(",")}` : ""})`
                                  : ""}
                                {` • dbg: time=${Number(run.runMeta?.timeRelevantCount || 0)}, path=${Number(
                                  run.runMeta?.pathOverlapCount || 0
                                )}, major=${Number(run.runMeta?.majorOverlapCount || 0)}`}
                                {` • paid_incidents=${Number(run.runMeta?.paidIncidentCount || 0)} (used=${
                                  run.runMeta?.paidIncidentSignalUsed ? "yes" : "no"
                                }, ignored=${run.runMeta?.paidIncidentSignalIgnored ? "yes" : "no"})`}
                                {run.runMeta?.reasonCodes?.length ? ` • reasons: ${run.runMeta.reasonCodes.join(", ")}` : ""}
                              </span>
                            </div>
                            {run.runMeta?.timeline?.rail_depart_utc || run.runMeta?.timeline?.rail_arrive_utc ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`timeline: ${formatDt(run.runMeta?.timeline?.rail_depart_utc)} -> ${formatDt(
                                  run.runMeta?.timeline?.rail_arrive_utc
                                )}`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR journey: ${formatDt(run.runMeta.nrJourney.depart)} -> ${formatDt(run.runMeta.nrJourney.arrive)} (${
                                  run.runMeta.nrJourney.durationMins != null ? `${run.runMeta.nrJourney.durationMins}m` : "N/A"
                                }, changes=${run.runMeta.nrJourney.changes != null ? run.runMeta.nrJourney.changes : "N/A"}, realtime=${
                                  run.runMeta.nrJourney.realtime || "N/A"
                                }${
                                  Number.isFinite(Number(run.runMeta.nrJourney.targetDeltaMins))
                                    ? `, target_delta=${Math.round(Number(run.runMeta.nrJourney.targetDeltaMins))}m`
                                    : ""
                                }, disruption=${String(run.runMeta.nrDisruptionLevel || "unknown").toUpperCase()})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrLastChecked ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR snapshot: ${formatDt(run.runMeta.nrLastChecked)} (${sinceText(run.runMeta.nrLastChecked) || "just now"})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney?.legs?.length ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR legs: ${run.runMeta.nrJourney.legs
                                  .map(
                                    (leg) =>
                                      `${leg.from}->${leg.to} ${leg.realtime} ${formatDt(leg.depart)} -> ${formatDt(leg.arrive)}`
                                  )
                                  .join(" | ")}`}
                              </div>
                            ) : null}
                            {run.runMeta?.railBaselineOutcome?.status ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`Baseline train outcome: ${String(run.runMeta.railBaselineOutcome.status || "unknown").toUpperCase()}${
                                  run.runMeta.railBaselineOutcome.final ? " (final)" : ""
                                }${
                                  run.runMeta.railBaselineOutcome.seenAt
                                    ? `, seen_at=${formatDt(run.runMeta.railBaselineOutcome.seenAt)}`
                                    : ""
                                }${
                                  run.runMeta.railMonitoringMode
                                    ? `, mode=${String(run.runMeta.railMonitoringMode).toUpperCase()}`
                                    : ""
                                }${
                                  run.runMeta.railActiveUntil
                                    ? `, active_until=${formatDt(run.runMeta.railActiveUntil)}`
                                    : ""
                                }${
                                  run.runMeta.railPostWindowContextOnly ? ", advisory_context_only=yes" : ""
                                }`}
                              </div>
                            ) : null}
                            {run.runMeta?.railLiveBoard?.enabled ? (
                              <div className="muted" style={{ marginBottom: 6, display: "grid", gap: 4 }}>
                                <div>
                                  {`Live board assurance: checked=${run.runMeta.railLiveBoard.checked ? "yes" : "no"}, reason=${
                                    run.runMeta.railLiveBoard.reason || "N/A"
                                  }${
                                    run.runMeta.railLiveBoard.checkedAt
                                      ? `, checked_at=${formatDt(run.runMeta.railLiveBoard.checkedAt)}`
                                      : ""
                                  }`}
                                </div>
                                {run.runMeta?.railLiveBoard?.summary ? (
                                  <div>
                                    {`Board summary: current=${run.runMeta.railLiveBoard.summary.current_leg || "N/A"}, next=${
                                      run.runMeta.railLiveBoard.summary.next_leg || "N/A"
                                    }, matched=${Number(run.runMeta.railLiveBoard.summary.matched_count || 0)}/${
                                      Number(run.runMeta.railLiveBoard.summary.total_queries || 0)
                                    }, delayed=${Number(run.runMeta.railLiveBoard.summary.delayed_count || 0)}, cancelled=${
                                      Number(run.runMeta.railLiveBoard.summary.cancelled_count || 0)
                                    }, alignment=${String(run.runMeta.railLiveBoard.summary.alignment || "unknown").toUpperCase()}`}
                                  </div>
                                ) : null}
                                {run.runMeta?.railLiveBoard?.queries?.length ? (
                                  <div>
                                    {`Board queries: ${run.runMeta.railLiveBoard.queries
                                      .map((q) => {
                                        const route = q?.target_from && q?.target_to ? `${q.target_from}->${q.target_to}` : "N/A";
                                        const target =
                                          q?.target_depart_local || q?.target_arrive_local
                                            ? `${q.target_depart_local || "?"}/${q.target_arrive_local || "?"}`
                                            : "N/A";
                                        const actual =
                                          q?.std || q?.estimate ? `${q.std || "?"}/${q.estimate || "?"}` : "N/A";
                                        const status = q?.cancelled ? "CANCELLED" : q?.status || "UNKNOWN";
                                        return `${q.crs || "N/A"} ${q.role || "query"} ${route} target=${target} actual=${actual} status=${status} score=${
                                          q?.match_score != null ? q.match_score : "0"
                                        }${q?.error ? ` err=${q.error}` : ""}`;
                                      })
                                      .join(" | ")}`}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                            {run.crsRows.map((row) => (
                              <div key={`${run.executionId || idx}-${row.crs}`} className="road-line">
                                <span className="road-name">{row.crs}</span>
                                <span className={pillClass(row.level)}>{String(row.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`affected: ${row.affected ? "yes" : "no"}`}</span>
                                <span className="muted">{`incidents: ${row.incidentCount}`}</span>
                                <span className="muted">{`dbg: time=${row.timeRelevantCount}, path=${row.pathOverlapCount}, major=${row.majorOverlapCount}`}</span>
                                {(row.departUtc || row.arriveUtc) && (
                                  <span className="muted">
                                    {`journey: ${formatDt(row.departUtc)} -> ${formatDt(row.arriveUtc)} (${
                                      row.durationMins != null ? `${row.durationMins}m` : "N/A"
                                    }, changes=${row.changes != null ? row.changes : "N/A"})`}
                                  </span>
                                )}
                                {row.incidents?.length > 0 && (
                                  <span className="muted">
                                    {`inc map: ${row.incidents
                                      .slice(0, 3)
                                      .map((it) => {
                                        const sev = String(it?.incident_severity_norm || "low").toUpperCase();
                                        const path = String(it?.path_overlap_level || "none").toUpperCase();
                                        const ov = Number.isFinite(Number(it?.time_overlap_mins)) ? `${Number(it.time_overlap_mins)}m` : "n/a";
                                        return `${sev}/${path} ov=${ov} impact=${it?.final_impact || "fyi"} (${
                                          it?.start_utc ? formatDt(it.start_utc) : "N/A"
                                        } -> ${it?.end_utc ? formatDt(it.end_utc) : "N/A"})`;
                                      })
                                      .join(" | ")}`}
                                  </span>
                                )}
                                <span className="muted">{`decision: ${row.decision || "—"} / ${row.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>

                <details className="detail-collapsible" style={{ marginTop: 8 }}>
                  <summary>Rail checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = railChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading rail checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No rail checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`rail-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • CRS: ${run.crsRows.length}`}
                                {` • NR calls: ${Number(run.railCalls?.nr?.calls || 0)}`}
                                {Number(run.railCalls?.nr?.errors || 0) > 0 ? ` • NR errors: ${Number(run.railCalls.nr.errors)}` : ""}
                                {` • RailData calls: ${Number(run.railCalls?.raildata?.calls || 0)}`}
                                {Number(run.railCalls?.raildata?.errors || 0) > 0
                                  ? ` • RailData errors: ${Number(run.railCalls.raildata.errors)}`
                                  : ""}
                                {run.runMeta?.paidRailRan === true ? " • NR paid: yes" : " • NR paid: no"}
                                {run.runMeta?.checked === true
                                  ? " • RailData checked: yes"
                                  : run.runMeta?.stale === true
                                  ? " • RailData checked: no (stale)"
                                  : " • RailData checked: no"}
                                {run.runMeta?.railMonitoringReason ? ` • reason: ${run.runMeta.railMonitoringReason}` : ""}
                                {run.runMeta?.error ? ` • free_rail_error: ${run.runMeta.error}` : ""}
                                {run.runMeta?.coverageStatus
                                  ? ` • coverage: ${run.runMeta.coverageStatus} (ok=${Number(run.runMeta?.checkedOkCount || 0)}, err=${Number(
                                      run.runMeta?.checkedErrorCount || 0
                                    )}${run.runMeta?.checkedErrorCrs?.length ? `, crs=${run.runMeta.checkedErrorCrs.join(",")}` : ""})`
                                  : ""}
                                {` • dbg: time=${Number(run.runMeta?.timeRelevantCount || 0)}, path=${Number(
                                  run.runMeta?.pathOverlapCount || 0
                                )}, major=${Number(run.runMeta?.majorOverlapCount || 0)}`}
                                {` • paid_incidents=${Number(run.runMeta?.paidIncidentCount || 0)} (used=${
                                  run.runMeta?.paidIncidentSignalUsed ? "yes" : "no"
                                }, ignored=${run.runMeta?.paidIncidentSignalIgnored ? "yes" : "no"})`}
                                {run.runMeta?.reasonCodes?.length ? ` • reasons: ${run.runMeta.reasonCodes.join(", ")}` : ""}
                              </span>
                            </div>
                            {run.runMeta?.timeline?.rail_depart_utc || run.runMeta?.timeline?.rail_arrive_utc ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`timeline: ${formatDt(run.runMeta?.timeline?.rail_depart_utc)} -> ${formatDt(
                                  run.runMeta?.timeline?.rail_arrive_utc
                                )}`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR journey: ${formatDt(run.runMeta.nrJourney.depart)} -> ${formatDt(run.runMeta.nrJourney.arrive)} (${
                                  run.runMeta.nrJourney.durationMins != null ? `${run.runMeta.nrJourney.durationMins}m` : "N/A"
                                }, changes=${run.runMeta.nrJourney.changes != null ? run.runMeta.nrJourney.changes : "N/A"}, realtime=${
                                  run.runMeta.nrJourney.realtime || "N/A"
                                }${
                                  Number.isFinite(Number(run.runMeta.nrJourney.targetDeltaMins))
                                    ? `, target_delta=${Math.round(Number(run.runMeta.nrJourney.targetDeltaMins))}m`
                                    : ""
                                }, disruption=${String(run.runMeta.nrDisruptionLevel || "unknown").toUpperCase()})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrLastChecked ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR snapshot: ${formatDt(run.runMeta.nrLastChecked)} (${sinceText(run.runMeta.nrLastChecked) || "just now"})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney?.legs?.length ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR legs: ${run.runMeta.nrJourney.legs
                                  .map(
                                    (leg) =>
                                      `${leg.from}->${leg.to} ${leg.realtime} ${formatDt(leg.depart)} -> ${formatDt(leg.arrive)}`
                                  )
                                  .join(" | ")}`}
                              </div>
                            ) : null}
                            {run.crsRows.map((row) => (
                              <div key={`${run.executionId || idx}-${row.crs}`} className="road-line">
                                <span className="road-name">{row.crs}</span>
                                <span className={pillClass(row.level)}>{String(row.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`affected: ${row.affected ? "yes" : "no"}`}</span>
                                <span className="muted">{`incidents: ${row.incidentCount}`}</span>
                                <span className="muted">{`dbg: time=${row.timeRelevantCount}, path=${row.pathOverlapCount}, major=${row.majorOverlapCount}`}</span>
                                {(row.departUtc || row.arriveUtc) && (
                                  <span className="muted">
                                    {`journey: ${formatDt(row.departUtc)} -> ${formatDt(row.arriveUtc)} (${
                                      row.durationMins != null ? `${row.durationMins}m` : "N/A"
                                    }, changes=${row.changes != null ? row.changes : "N/A"})`}
                                  </span>
                                )}
                                <span className="muted">{`decision: ${row.decision || "—"} / ${row.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>

                <details className="detail-collapsible" style={{ marginTop: 8 }}>
                  <summary>Rail checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = railChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading rail checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No rail checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`rail-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • CRS: ${run.crsRows.length}`}
                                {` • NR calls: ${Number(run.railCalls?.nr?.calls || 0)}`}
                                {Number(run.railCalls?.nr?.errors || 0) > 0 ? ` • NR errors: ${Number(run.railCalls.nr.errors)}` : ""}
                                {` • RailData calls: ${Number(run.railCalls?.raildata?.calls || 0)}`}
                                {Number(run.railCalls?.raildata?.errors || 0) > 0
                                  ? ` • RailData errors: ${Number(run.railCalls.raildata.errors)}`
                                  : ""}
                                {run.runMeta?.paidRailRan === true ? " • NR paid: yes" : " • NR paid: no"}
                                {run.runMeta?.checked === true
                                  ? " • RailData checked: yes"
                                  : run.runMeta?.stale === true
                                  ? " • RailData checked: no (stale)"
                                  : " • RailData checked: no"}
                                {run.runMeta?.railMonitoringReason ? ` • reason: ${run.runMeta.railMonitoringReason}` : ""}
                                {run.runMeta?.error ? ` • free_rail_error: ${run.runMeta.error}` : ""}
                                {run.runMeta?.coverageStatus
                                  ? ` • coverage: ${run.runMeta.coverageStatus} (ok=${Number(run.runMeta?.checkedOkCount || 0)}, err=${Number(
                                      run.runMeta?.checkedErrorCount || 0
                                    )}${run.runMeta?.checkedErrorCrs?.length ? `, crs=${run.runMeta.checkedErrorCrs.join(",")}` : ""})`
                                  : ""}
                                {` • dbg: time=${Number(run.runMeta?.timeRelevantCount || 0)}, path=${Number(
                                  run.runMeta?.pathOverlapCount || 0
                                )}, major=${Number(run.runMeta?.majorOverlapCount || 0)}`}
                                {` • paid_incidents=${Number(run.runMeta?.paidIncidentCount || 0)} (used=${
                                  run.runMeta?.paidIncidentSignalUsed ? "yes" : "no"
                                }, ignored=${run.runMeta?.paidIncidentSignalIgnored ? "yes" : "no"})`}
                                {run.runMeta?.reasonCodes?.length ? ` • reasons: ${run.runMeta.reasonCodes.join(", ")}` : ""}
                              </span>
                            </div>
                            {run.runMeta?.timeline?.rail_depart_utc || run.runMeta?.timeline?.rail_arrive_utc ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`timeline: ${formatDt(run.runMeta?.timeline?.rail_depart_utc)} -> ${formatDt(
                                  run.runMeta?.timeline?.rail_arrive_utc
                                )}`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR journey: ${formatDt(run.runMeta.nrJourney.depart)} -> ${formatDt(run.runMeta.nrJourney.arrive)} (${
                                  run.runMeta.nrJourney.durationMins != null ? `${run.runMeta.nrJourney.durationMins}m` : "N/A"
                                }, changes=${run.runMeta.nrJourney.changes != null ? run.runMeta.nrJourney.changes : "N/A"}, realtime=${
                                  run.runMeta.nrJourney.realtime || "N/A"
                                }${
                                  Number.isFinite(Number(run.runMeta.nrJourney.targetDeltaMins))
                                    ? `, target_delta=${Math.round(Number(run.runMeta.nrJourney.targetDeltaMins))}m`
                                    : ""
                                }, disruption=${String(run.runMeta.nrDisruptionLevel || "unknown").toUpperCase()})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrLastChecked ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR snapshot: ${formatDt(run.runMeta.nrLastChecked)} (${sinceText(run.runMeta.nrLastChecked) || "just now"})`}
                              </div>
                            ) : null}
                            {run.runMeta?.nrJourney?.legs?.length ? (
                              <div className="muted" style={{ marginBottom: 6 }}>
                                {`NR legs: ${run.runMeta.nrJourney.legs
                                  .map(
                                    (leg) =>
                                      `${leg.from}->${leg.to} ${leg.realtime} ${formatDt(leg.depart)} -> ${formatDt(leg.arrive)}`
                                  )
                                  .join(" | ")}`}
                              </div>
                            ) : null}
                            {run.crsRows.map((row) => (
                              <div key={`${run.executionId || idx}-${row.crs}`} className="road-line">
                                <span className="road-name">{row.crs}</span>
                                <span className={pillClass(row.level)}>{String(row.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`affected: ${row.affected ? "yes" : "no"}`}</span>
                                <span className="muted">{`incidents: ${row.incidentCount}`}</span>
                                <span className="muted">{`dbg: time=${row.timeRelevantCount}, path=${row.pathOverlapCount}, major=${row.majorOverlapCount}`}</span>
                                {(row.departUtc || row.arriveUtc) && (
                                  <span className="muted">
                                    {`journey: ${formatDt(row.departUtc)} -> ${formatDt(row.arriveUtc)} (${
                                      row.durationMins != null ? `${row.durationMins}m` : "N/A"
                                    }, changes=${row.changes != null ? row.changes : "N/A"})`}
                                  </span>
                                )}
                                <span className="muted">{`decision: ${row.decision || "—"} / ${row.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>

                <details className="detail-collapsible" style={{ marginTop: 8 }}>
                  <summary>Rail checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = railChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading rail checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No rail checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`rail-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • CRS: ${run.crsRows.length}`}
                                {` • NR calls: ${Number(run.railCalls?.nr?.calls || 0)}`}
                                {Number(run.railCalls?.nr?.errors || 0) > 0 ? ` • NR errors: ${Number(run.railCalls.nr.errors)}` : ""}
                                {` • RailData calls: ${Number(run.railCalls?.raildata?.calls || 0)}`}
                                {Number(run.railCalls?.raildata?.errors || 0) > 0 ? ` • RailData errors: ${Number(run.railCalls.raildata.errors)}` : ""}
                                {run.runMeta?.paidRailRan === true ? " • NR paid: yes" : " • NR paid: no"}
                                {run.runMeta?.checked === true
                                  ? " • RailData checked: yes"
                                  : run.runMeta?.stale === true
                                  ? " • RailData checked: no (stale)"
                                  : " • RailData checked: no"}
                                {run.runMeta?.railMonitoringReason ? ` • reason: ${run.runMeta.railMonitoringReason}` : ""}
                                {run.runMeta?.error ? ` • free_rail_error: ${run.runMeta.error}` : ""}
                              </span>
                            </div>
                            {run.crsRows.map((row) => (
                              <div key={`${run.executionId || idx}-${row.crs}`} className="road-line">
                                <span className="road-name">{row.crs}</span>
                                <span className={pillClass(row.level)}>{String(row.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`affected: ${row.affected ? "yes" : "no"}`}</span>
                                <span className="muted">{`incidents: ${row.incidentCount}`}</span>
                                <span className="muted">{`dbg: time=${row.timeRelevantCount}, path=${row.pathOverlapCount}, major=${row.majorOverlapCount}`}</span>
                                {(row.departUtc || row.arriveUtc) && (
                                  <span className="muted">
                                    {`journey: ${formatDt(row.departUtc)} -> ${formatDt(row.arriveUtc)} (${
                                      row.durationMins != null ? `${row.durationMins}m` : "N/A"
                                    }, changes=${row.changes != null ? row.changes : "N/A"})`}
                                  </span>
                                )}
                                <span className="muted">{`decision: ${row.decision || "—"} / ${row.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>

                {renderCustomerViewPanel({
                  row: r,
                  detail,
                  draftActionBusy: !!draftActionLoading[r.journey_id],
                  onDraftAction: (notificationId, action, body = null) => runDraftAction(r.journey_id, notificationId, action, body),
                })}

                <strong>Ledger Timeline</strong>
                {detailLoading[r.journey_id] && <div className="muted" style={{ marginTop: 6 }}>Loading detail...</div>}
                {!detailLoading[r.journey_id] && detailByJourney[r.journey_id]?.error && (
                  <div className="muted" style={{ marginTop: 6 }}>{detailByJourney[r.journey_id].error}</div>
                )}
                {!detailLoading[r.journey_id] && detailByJourney[r.journey_id]?.matrix && (
                  <details style={{ marginTop: 10 }}>
                    <summary>Check matrix</summary>
                    {renderCheckMatrix(detailByJourney[r.journey_id])}
                  </details>
                )}
                {!detailLoading[r.journey_id] && detailByJourney[r.journey_id]?.executions && (
                  <>
                    <div className="timeline-grid" style={{ marginTop: 8 }}>
                      <div>
                        <div className="muted" style={{ marginBottom: 8 }}>
                          Source totals:{" "}
                          {(detailByJourney[r.journey_id].source_call_totals || []).length
                            ? (detailByJourney[r.journey_id].source_call_totals || [])
                                .filter((x) => !(String(x.source || "").toLowerCase() === "monitor_runner" && Number(x.total_calls || 0) === 0))
                                .map((x) => `${sourceShort(x.source)}:${x.total_calls}`)
                                .join(" • ")
                            : "N/A"}
                        </div>
                        <div className="muted" style={{ marginBottom: 4 }}>Recent executions</div>
                        {(detailByJourney[r.journey_id].executions || []).slice(0, 8).map((x) => {
                          const execLevel = overallFromExecution(x, r);
                          const htf = Number(x?.hours_to_flight);
                          const showHtf = Number.isFinite(htf) && Math.abs(htf) >= 0.1;
                          const callRows = (detailByJourney[r.journey_id].source_calls || []).filter((s) => Number(s.execution_id) === Number(x.execution_id));
                          const bySource = callRows.reduce((acc, it) => {
                            if (String(it?.source || "").toLowerCase() === "monitor_runner" && Number(it?.call_count || 0) === 0) return acc;
                            const key = String(it?.source || "unknown");
                            acc[key] = (acc[key] || 0) + Number(it?.call_count || 0);
                            return acc;
                          }, {});
                          const callText = Object.keys(bySource).length
                            ? Object.entries(bySource)
                                .map(([k, v]) => `${sourceShort(k)}:${v}`)
                                .join(" ")
                            : "heartbeat";
                          const forcedText = x?.summary?.run_forced ? " • FORCED" : "";
                          const hyperLockText = x?.summary?.hyper_strict_anchor_lock ? " • HYPER_LOCK" : "";
                          return (
                            <div key={`ex-${x.execution_id}`} className="timeline-item">
                              <strong>{formatDt(x.run_at)}</strong>{" "}
                              <span className={pillClass(execLevel)}>{String(execLevel || "unknown").toUpperCase()}</span>{" "}
                              <span className="muted">{x.mode || "—"} {showHtf ? `• ${htf.toFixed(1)}h` : ""} • {callText}{forcedText}{hyperLockText}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div>
                        <div className="muted" style={{ marginBottom: 4 }}>Recent notifications</div>
                        {(detailByJourney[r.journey_id].notifications || []).slice(0, 8).map((n) => (
                          <div key={`nt-${n.notification_id}`} className="timeline-item">
                            <strong>{formatDt(notificationDisplayAt(n))}</strong>{" "}
                            <span className={`pill ${notificationPillClass(n)}`}>{String(notificationDisplayDecision(n) || "—").toUpperCase()}</span>{" "}
                            <span className="muted">
                              {describeNotificationLifecycle(n).join(" • ") || "—"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div className="muted" style={{ marginBottom: 4 }}>Recent deltas</div>
                      {(detailByJourney[r.journey_id].observations || []).slice(0, 12).map((o) => {
                        const changed = !!o?.delta_json?.changed;
                        const changedFields = Array.isArray(o?.delta_json?.changed_fields) ? o.delta_json.changed_fields : [];
                        return (
                          <div key={`ob-${o.observation_id}`} className="timeline-item">
                            <strong>{formatDt(o.created_at)}</strong>{" "}
                            <span className={pillClass(o.severity)}>{String(o.severity || "unknown").toUpperCase()}</span>{" "}
                            <span className="muted">
                              {String(o.source || "").toUpperCase()}:{String(o.target_key || "").toUpperCase()} • {o.decision || "no_change"} • {o.reason_code || "none"}
                              {changed ? ` • Δ ${changedFields.slice(0, 4).join(", ") || "changed"}` : ""}
                            </span>
                          </div>
                        );
                      })}
                      {!(detailByJourney[r.journey_id].observations || []).length && (
                        <div className="muted">No observation deltas yet.</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </td>
          </tr>
        );
        return [rowNode, expNode];
      }),
    [verifiedCurrentRows, expanded, detailByJourney, detailLoading]
  );

  const unverifiedTable = useMemo(
    () =>
      visibleUnverifiedCurrentRows.flatMap((r) => {
        const detail = detailByJourney[r.journey_id];
        const open = !!expanded[r.journey_id];
        const overall = overallFromRow(r);
        const nextCheck = relativeWithDot(r?.next_check_due_at, "check");
        const phaseTxt = phaseLabel(r);
        const journeyType = journeyTypeMeta(r);
        const cache = detail?.projection_cache || {};
        const cacheState = cache?.search_would_hit_cache_now ? "Cache hit" : "Fresh search";

        const rowNode = (
          <tr key={`unverified-${r.journey_id}`}>
            <td>{r.email || "N/A"}</td>
            <td>
              <strong>{`${r?.flight_number || "N/A"} • ${r?.postcode || "N/A"}`}</strong>
              <div className={journeyType.cls}>{journeyType.label}</div>
              <div className="muted">{phaseTxt}</div>
              <div className="muted">{`Departure (UK): ${formatDt(r?.anchor_utc)}`}</div>
            </td>
            <td><span className={pillClass(overall)}>{overall.toUpperCase()}</span></td>
            <td>
              <div className="muted">{cacheState}</div>
              <div className="muted">{cache?.cache_serves_until ? `Until ${formatDt(cache.cache_serves_until)}` : "No cache expiry"}</div>
            </td>
            <td>
              <div className={`rel ${nextCheck?.cls || ""}`}>{nextCheck?.text || "—"}</div>
            </td>
            <td>
              <button
                className="btn secondary"
                type="button"
                onClick={() => {
                  setExpanded((p) => ({ ...p, [r.journey_id]: !p[r.journey_id] }));
                  if (!open) loadDetail(r.journey_id);
                }}
              >
                {open ? "Hide" : "Customer view"}
              </button>
            </td>
          </tr>
        );

        if (!open) return [rowNode];

        const detailNode = (
          <tr key={`unverified-detail-${r.journey_id}`}>
            <td colSpan={6}>
              {detailLoading[r.journey_id] && <div className="muted">Loading detail...</div>}
              {!detailLoading[r.journey_id] && detailByJourney[r.journey_id]?.error && (
                <div className="muted">{detailByJourney[r.journey_id].error}</div>
              )}
              {!detailLoading[r.journey_id] && !detailByJourney[r.journey_id]?.error &&
                renderCustomerViewPanel({
                  row: r,
                  detail,
                  draftActionBusy: !!draftActionLoading[r.journey_id],
                  onDraftAction: (notificationId, action, body = null) => runDraftAction(r.journey_id, notificationId, action, body),
                })}
            </td>
          </tr>
        );

        return [rowNode, detailNode];
      }),
    [visibleUnverifiedCurrentRows, expanded, detailByJourney, detailLoading]
  );

  const pastTable = useMemo(
    () =>
      verifiedPastRows.flatMap((r) => {
        const t = r?.monitor_targets || {};
        const cStats = checkpointStats(r);
        const phaseTxt = phaseLabel(r);
        const journeyType = journeyTypeMeta(r);
        const detail = detailByJourney[r.journey_id];
        const latestExec = asArray(detail?.executions)[0] || null;
        const latestEval = asArray(detail?.evaluations)[0] || null;
        const latestSources = latestEval?.sources || {};
        const freeRailSrc = latestSources?.free_rail || {};
        const latestSummary = latestExec?.summary || {};
        const railDebug = railDebugFromDetail(detail);
        const railMonReason = latestSummary?.rail_monitoring_reason || "N/A";
        const railDisruptionLevel = String(latestSummary?.free_rail_level || "unknown").toUpperCase();
        const railFeasStatus = String(latestSummary?.rail_feasibility_status || "unknown").toLowerCase();
        const railFeasMinsLate = Number(latestSummary?.rail_feasibility_mins_late);
        const railPhase = String(latestSummary?.rail_phase || "unknown").toLowerCase();
        const railAction = String(latestSummary?.rail_action || "keep_selected").toLowerCase();
        const roadPhase = String(latestSummary?.road_phase || "unknown").toLowerCase();
        const roadAction = String(latestSummary?.road_action || "monitor_all_legs").toLowerCase();
        const selectedDepart = latestSummary?.rail_selected_depart_utc || null;
        const selectedArrive = latestSummary?.rail_selected_arrive_utc || null;
        const selectedPath = String(latestSummary?.rail_selected_path || "").trim();
        const selectedFingerprint = String(latestSummary?.rail_selected_journey_fingerprint || "").trim();
        const selectedLegs = asArray(latestSummary?.rail_selected_legs).map((leg) => ({
          from: String(leg?.from || leg?.from_crs || "").toUpperCase() || "N/A",
          to: String(leg?.to || leg?.to_crs || "").toUpperCase() || "N/A",
          depart: leg?.depart_utc || leg?.depart || null,
          arrive: leg?.arrive_utc || leg?.arrive || null,
          realtime: String(leg?.realtime || "UNKNOWN").toUpperCase(),
        }));
        const altDepart = latestSummary?.rail_alt_depart_utc || null;
        const altArrive = latestSummary?.rail_alt_arrive_utc || null;
        const altPath = String(latestSummary?.rail_alt_path || "").trim();
        const altDelta = Number(latestSummary?.rail_alt_target_delta_mins);
        const altViable = latestSummary?.rail_alt_viable === true;
        const railFeasText =
          railFeasStatus === "missed_target"
            ? `MISSED_TARGET${Number.isFinite(railFeasMinsLate) ? ` (+${Math.round(railFeasMinsLate)}m)` : ""}`
            : railFeasStatus === "at_risk"
            ? "AT_RISK"
            : railFeasStatus === "on_time"
            ? "ON_TIME"
            : String(latestSummary?.rail_feasibility_status || "unknown").toUpperCase();
        const railAffected = asArray(latestSummary?.free_rail_affected_crs);
        const railChecked = freeRailSrc?.checked === true ? "yes" : "no";
        const railStale = freeRailSrc?.stale === true ? "yes" : "no";
        const railError = freeRailSrc?.error || null;
        const overall = overallFromRow(r);
        const action = deriveAction(r);
        const nextCheck = relativeWithDot(r?.next_check_due_at, "check");
        const nextNotify = relativeWithDot(r?.next_notification_due_at, "notify");
        const open = !!expanded[r.journey_id];
        const journeyText = `${r?.flight_number || "N/A"} • ${r?.postcode || "N/A"} • ${String(r?.direction || "—").slice(0, 6)}`;
        const progressPct = cStats.total > 0 ? Math.round((cStats.done / cStats.total) * 100) : 0;
        const lastChange = r?.last_monitor_at || cStats.latestTs || null;

        const rowNode = (
          <tr key={r.journey_id}>
            <td>{r.email || "N/A"}</td>
            <td>
              <strong>{journeyText}</strong>
              <div className={journeyType.cls}>{journeyType.label}</div>
              <div className="muted">{phaseTxt}</div>
              <div className="muted">{`Departure (UK): ${formatDt(r?.anchor_utc)}`}</div>
            </td>
            <td><span className={pillClass(overall)}>{overall.toUpperCase()}</span></td>
            <td>
              <span
                className={`action ${action.includes("Manual") ? "bad" : action.includes("Notify") ? "warn" : "ok"}`}
                title={actionReason(r)}
              >
                {action}
              </span>
            </td>
            {SOURCE_KEYS.map((k) => {
              const st = sourceState(r, k);
              return (
                <td key={`${r.journey_id}-${k}`}>
                  <span className={st.className} title={k === "flight" ? flightStatusTitle(r) : undefined}>
                    {st.text}
                  </span>
                  {k === "rail" && r?.rail_reason_code && r.rail_reason_code !== "none" && (
                    <div className="muted" style={{ marginTop: 4 }}>
                      <span className="pill warn" style={{ textTransform: "none", letterSpacing: 0 }}>
                        {r.rail_reason_code}
                      </span>
                    </div>
                  )}
                </td>
              );
            })}
            <td>
              <div className={`rel ${nextCheck.cls}`}>{nextCheck.text}</div>
            </td>
            <td>
              <div className={`rel ${nextNotify.cls}`}>{nextNotify.text}</div>
            </td>
            <td>
              <div className="phase-pill">{phaseTxt}</div>
              {r?.hyper_strict_anchor_lock && (
                <div className="muted" style={{ marginTop: 4 }}>
                  <span className="pill warn" style={{ textTransform: "none", letterSpacing: 0 }}>
                    HYPER_LOCK
                  </span>
                </div>
              )}
              <div className="bar"><span style={{ width: `${progressPct}%` }} /></div>
              <div className="muted">{`${cStats.done}/${cStats.total} done`}</div>
              <div className="muted">{`Last checkpoint: ${formatDt(cStats.latestTs || r?.last_monitor_at)}`}</div>
            </td>
            <td>
              <div><strong>Roads:</strong> {shortTargets(t.roads, 3)}</div>
              <div><strong>CRS:</strong> {shortTargets(t.crs, 3)}</div>
              <div><strong>Weather:</strong> {shortTargets(t.weather_locations, 2)}</div>
              <div>
                <strong>TfL:</strong>{" "}
                {(t.tfl_relevant || sourceState(r, "tfl").level !== "unknown")
                  ? shortTargets(t.tfl_crs || t.crs, 3)
                  : "No"}
              </div>
            </td>
            <td>
              <button
                className="btn tiny"
                type="button"
                onClick={() => {
                  const next = !open;
                  setExpanded((p) => ({ ...p, [r.journey_id]: next }));
                  if (next) loadDetail(r.journey_id);
                }}
              >
                {open ? "Hide" : "Open"}
              </button>
            </td>
            <td>
              <div>{formatDt(lastChange)}</div>
              <div className="muted">{sinceText(lastChange)}</div>
            </td>
          </tr>
        );

        if (!open) return [rowNode];
        const expNode = (
          <tr key={`${r.journey_id}-exp`} className="expanded-row">
            <td colSpan={16}>
              <div className="expanded-title">{overallWhy(r)}</div>
              <div className="expanded-grid">
                <div><strong>Journey ID:</strong> {r.journey_id}</div>
                <div><strong>Next rule:</strong> {r.next_notification_rule || "N/A"}</div>
                <div><strong>Strict hyper lock:</strong> {r?.hyper_strict_anchor_lock ? "ON" : "off"}</div>
                <div><strong>Last monitor:</strong> {formatDt(r.last_monitor_at)}</div>
                <div><strong>Last notify:</strong> {formatDt(r.last_notified_at)}</div>
                <div><strong>Targets roads:</strong> {(t.roads || []).join(", ") || "N/A"}</div>
                <div><strong>Targets CRS:</strong> {(t.crs || []).join(", ") || "N/A"}</div>
                <div><strong>Rail reason:</strong> {r.rail_reason_code || "none"}{r.rail_reason_at ? ` (${formatDt(r.rail_reason_at)})` : ""}</div>
                <div>
                  <strong>Rail network signal:</strong> {railDisruptionLevel}
                </div>
                <div>
                  <strong>Rail feasibility:</strong> {railFeasText}
                </div>
                <div>
                  <strong>Rail monitor reason:</strong> {railMonReason}
                </div>
                <div>
                  <strong>Rail affected CRS:</strong> {railAffected.length ? railAffected.join(", ") : "none"}
                </div>
                <div>
                  <strong>Rail check context:</strong>{" "}
                  {`checked=${railChecked}, stale=${railStale}, paid_rail_ran=${latestSummary?.paid_rail_ran === true ? "yes" : "no"}${
                    railError ? `, error=${railError}` : ""
                  }`}
                </div>
                <div>
                  <strong>Rail debug:</strong>{" "}
                  {`time=${railDebug.timeRelevantCount}, path=${railDebug.pathOverlapCount}, major=${railDebug.majorOverlapCount}${
                    railDebug.reasonCodes.length ? `, reasons=${railDebug.reasonCodes.join(", ")}` : ""
                  }`}
                </div>
                <div>
                  <strong>Rail timeline:</strong>{" "}
                  {railDebug.timeline?.rail_depart_utc || railDebug.timeline?.rail_arrive_utc
                    ? `${formatDt(railDebug.timeline?.rail_depart_utc)} -> ${formatDt(railDebug.timeline?.rail_arrive_utc)}`
                    : "N/A"}
                </div>
                <div>
                  <strong>Selected train:</strong>{" "}
                  {selectedDepart || selectedArrive ? `${formatDt(selectedDepart)} -> ${formatDt(selectedArrive)}` : "N/A"}
                </div>
                <div>
                  <strong>Selected path:</strong> {selectedPath || "N/A"}{selectedFingerprint ? ` (${selectedFingerprint.slice(0, 12)})` : ""}
                </div>
                <div>
                  <strong>Rail phase:</strong> {monitoringPhaseLabel(railPhase)}
                </div>
                <div>
                  <strong>Rail action:</strong> {String(railAction || "keep_selected").toUpperCase()}
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <strong>Rail assurance:</strong>{" "}
                  {`level=${String(latestSummary?.rail_assurance_level || "unknown").toUpperCase()}, source=${
                    latestSummary?.rail_assurance_source || "N/A"
                  }, used=${latestSummary?.rail_assurance_used_in_decision === true ? "yes" : "no"}, bias=${
                    latestSummary?.rail_assurance_decision_bias || "N/A"
                  }, state=${latestSummary?.rail_assurance_operational_state || "N/A"}, board_alignment=${
                    latestSummary?.rail_assurance_board_alignment || "N/A"
                  }, threshold=${latestSummary?.rail_assurance_threshold_crossed || "N/A"}, confidence=${
                    latestSummary?.rail_assurance_confidence || "N/A"
                  }, weight=${Number.isFinite(Number(latestSummary?.rail_assurance_weight)) ? Number(latestSummary.rail_assurance_weight) : "N/A"}, nr_weight=${
                    Number.isFinite(Number(latestSummary?.rail_nr_weight)) ? Number(latestSummary.rail_nr_weight) : "N/A"
                  }, live_board_weight=${
                    Number.isFinite(Number(latestSummary?.rail_live_board_weight)) ? Number(latestSummary.rail_live_board_weight) : "N/A"
                  }${latestSummary?.rail_nr_deweighted_reason ? `, nr_deweighted=${latestSummary.rail_nr_deweighted_reason}` : ""}`}
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <strong>Selected leg status:</strong>{" "}
                  {selectedLegs.length
                    ? selectedLegs
                        .slice(0, 6)
                        .map((leg) => `${leg.from}->${leg.to} ${leg.realtime} ${formatDt(leg.depart)} -> ${formatDt(leg.arrive)}`)
                        .join(" | ")
                    : "N/A"}
                </div>
                <div>
                  <strong>Alt candidate:</strong>{" "}
                  {altDepart || altArrive
                    ? `${formatDt(altDepart)} -> ${formatDt(altArrive)}${altPath ? ` (${altPath})` : ""}${
                        Number.isFinite(altDelta) ? ` (delta ${Math.round(altDelta)}m)` : ""
                      }${altViable ? " [viable]" : ""}`
                    : "none"}
                </div>
                <div>
                  <strong>Rail incidents:</strong>{" "}
                  {railDebug.incidents.length
                    ? railDebug.incidents
                        .slice(0, 3)
                        .map(
                          (it) =>
                            `${it.crs || "N/A"} ${String(it.incident_severity_norm || "low").toUpperCase()} ${String(
                              it.path_overlap_level || "none"
                            ).toUpperCase()} ov=${Number.isFinite(Number(it.time_overlap_mins)) ? `${it.time_overlap_mins}m` : "n/a"} impact=${
                              it.final_impact || "fyi"
                            }`
                        )
                        .join(" | ")
                    : "none"}
                </div>
                <div>
                  <strong>Road phase:</strong> {monitoringPhaseLabel(roadPhase)}
                </div>
                <div>
                  <strong>Road action:</strong> {String(roadAction || "monitor_all_legs").toUpperCase()}
                </div>
                {(() => {
                  const roadDbg = roadDebugFromSummary(latestSummary);
                  if (!roadDbg.hasData) return null;
                  return (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <details className="detail-collapsible">
                        <summary>Road query debug</summary>
                        <div className="muted" style={{ marginTop: 8, display: "grid", gap: 6 }}>
                          {deriveNhDriverSummary(detail) ? (
                            <div>
                              <strong>NH driver:</strong> {deriveNhDriverSummary(detail)}
                            </div>
                          ) : null}
                          <div>
                            <strong>Monitoring policy:</strong>{" "}
                            {`level=${roadDbg.monitoringLevel}, raw=${roadDbg.rawLevel}, network=${roadDbg.networkLevel}, nh=${roadDbg.nhLevel}, feasibility=${roadDbg.feasibilityStatus}, reason=${roadDbg.reason}`}
                          </div>
                          <div>
                            <strong>Traffic plan:</strong>{" "}
                            {`guidance=${roadDbg.guidanceTrafficMins ?? "N/A"}m, prev=${roadDbg.previousTrafficMins ?? "N/A"}m, curr=${roadDbg.currentTrafficMins ?? "N/A"}m, drift=${roadDbg.driftMins ?? "N/A"}m, guidance_drift=${roadDbg.guidanceDriftMins ?? "N/A"}m, delay_vs_base=${roadDbg.delayFromBaseMins ?? "N/A"}m, margin=${roadDbg.marginMins ?? "N/A"}m`}
                          </div>
                          <div>
                            <strong>Guidance window:</strong>{" "}
                            {`depart_at=${roadDbg.guidanceDepartAt ? formatDt(roadDbg.guidanceDepartAt) : "N/A"}, arrive_at=${roadDbg.guidanceArriveAt ? formatDt(roadDbg.guidanceArriveAt) : "N/A"}`}
                          </div>
                          <div>
                            <strong>Primary:</strong>{" "}
                            {`depart_at=${roadDbg.primary.departAt ? formatDt(roadDbg.primary.departAt) : "N/A"}, base=${roadDbg.primary.baseMins ?? "N/A"}m, traffic=${
                              roadDbg.primary.trafficMins ?? "N/A"
                            }m, buffer=${roadDbg.primary.safetyBufferMins ?? "N/A"}m, parking=${roadDbg.primary.parkingTransferMins ?? "N/A"}m, final_eta_safe=${
                              roadDbg.primary.finalEtaSafeMins ?? "N/A"
                            }m`}
                          </div>
                          <div>
                            <strong>Stabilization:</strong>{" "}
                            {`passes=${roadDbg.primary.stabilizationPasses ?? "N/A"}, delta=${roadDbg.primary.stabilizationDeltaMins ?? "N/A"}m`}
                          </div>
                          <div>
                            <strong>Self-drive:</strong>{" "}
                            {`depart_at=${roadDbg.drive.departAt ? formatDt(roadDbg.drive.departAt) : "N/A"}, base=${roadDbg.drive.baseMins ?? "N/A"}m, traffic=${
                              roadDbg.drive.trafficMins ?? "N/A"
                            }m, buffer=${roadDbg.drive.safetyBufferMins ?? "N/A"}m, parking=${roadDbg.drive.parkingTransferMins ?? "N/A"}m, final_eta_safe=${
                              roadDbg.drive.finalEtaSafeMins ?? "N/A"
                            }m`}
                          </div>
                          <div>
                            <strong>Taxi:</strong>{" "}
                            {`depart_at=${roadDbg.taxi.departAt ? formatDt(roadDbg.taxi.departAt) : "N/A"}, base=${roadDbg.taxi.baseMins ?? "N/A"}m, traffic=${
                              roadDbg.taxi.trafficMins ?? "N/A"
                            }m, buffer=${roadDbg.taxi.safetyBufferMins ?? "N/A"}m, parking=${roadDbg.taxi.parkingTransferMins ?? "N/A"}m, final_eta_safe=${
                              roadDbg.taxi.finalEtaSafeMins ?? "N/A"
                            }m`}
                          </div>
                          <div>
                            <strong>Baseline route:</strong>{" "}
                            {`locked_at=${roadDbg.baseline.lockedAt ? formatDt(roadDbg.baseline.lockedAt) : "N/A"}, mode=${
                              roadDbg.baseline.selectedMode || "N/A"
                            }, provider=${roadDbg.baseline.routeProvider || "N/A"}, summary=${roadDbg.baseline.routeSummary || "N/A"}`}
                          </div>
                          <div>
                            <strong>Baseline live timing:</strong>{" "}
                            {`depart_at=${roadDbg.baseline.departAt ? formatDt(roadDbg.baseline.departAt) : "N/A"}, arrive_at=${
                              roadDbg.baseline.arriveAt ? formatDt(roadDbg.baseline.arriveAt) : "N/A"
                            }, planned=${roadDbg.baseline.finalEtaSafeMins ?? "N/A"}m, now=${roadDbg.baseline.nowMins ?? "N/A"}m, delta=${
                              roadDbg.baseline.deltaVsPlannedMins ?? "N/A"
                            }m, status=${roadDbg.baseline.status}`}
                          </div>
                          <div>
                            <strong>Alternative route:</strong>{" "}
                            {`mode=${roadDbg.alternative.selectedMode || "N/A"}, available=${roadDbg.alternative.available ? "yes" : "no"}, provider=${
                              roadDbg.alternative.routeProvider || "N/A"
                            }, summary=${roadDbg.alternative.routeSummary || "N/A"}`}
                          </div>
                          <div>
                            <strong>Savings:</strong>{" "}
                            {`alternative_now=${roadDbg.alternative.nowMins ?? "N/A"}m, savings=${roadDbg.alternative.savingsMins ?? "N/A"}m, margin_gain=${
                              roadDbg.alternative.marginGainMins ?? "N/A"
                            }m, diverged=${roadDbg.divergence.changed ? "yes" : "no"}, divergence_type=${roadDbg.divergence.type || "N/A"}, threshold=${
                              roadDbg.divergence.thresholdMins ?? "N/A"
                            }m`}
                          </div>
                          <div>
                            <strong>Reason / source driver:</strong>{" "}
                            {`baseline_reason=${roadDbg.baseline.reasonCode || "N/A"} (${roadDbg.baseline.reasonText || "N/A"}), alternative_reason=${
                              roadDbg.alternative.reasonCode || "N/A"
                            } (${roadDbg.alternative.reasonText || "N/A"}), source_driver=${roadDbg.baseline.sourceDriver || "N/A"}`}
                          </div>
                          <div>
                            <strong>Road assurance:</strong>{" "}
                            {`level=${roadDbg.assurance.level}, source=${roadDbg.assurance.source || "N/A"}, used=${
                              roadDbg.assurance.usedInDecision ? "yes" : "no"
                            }, bias=${roadDbg.assurance.decisionBias || "N/A"}, threshold=${
                              roadDbg.assurance.thresholdCrossed || "N/A"
                            }, confidence=${roadDbg.assurance.confidence || "N/A"}, weight=${
                              roadDbg.assurance.weight != null ? roadDbg.assurance.weight : "N/A"
                            }, alt_actionable=${roadDbg.assurance.altActionable ? "yes" : "no"}, reason=${
                              roadDbg.assurance.reasonCode || "N/A"
                            } (${roadDbg.assurance.reasonText || "N/A"})`}
                          </div>
                          <div>
                            <strong>Road labels:</strong>{" "}
                            {`baseline_major=${roadDbg.baseline.majorRoads.length ? roadDbg.baseline.majorRoads.join(", ") : "none"}, baseline_monitored=${
                              roadDbg.baseline.monitoredRoads.length ? roadDbg.baseline.monitoredRoads.join(", ") : "none"
                            }, baseline_causes=${
                              roadDbg.baseline.primaryCauseRoads.length ? roadDbg.baseline.primaryCauseRoads.join(", ") : "none"
                            }, alternative_causes=${
                              roadDbg.alternative.primaryCauseRoads.length ? roadDbg.alternative.primaryCauseRoads.join(", ") : "none"
                            }`}
                          </div>
                          <div>
                            <strong>Phase 1 messages:</strong>{" "}
                            {`${roadDbg.customerMessages.primary || "N/A"}${roadDbg.customerMessages.alternative ? ` | ${roadDbg.customerMessages.alternative}` : ""}`}
                          </div>
                        </div>
                      </details>
                    </div>
                  );
                })()}
                {renderTomTomDebugBlock(latestSummary)}
                {(() => {
                  const tflDbg = tflDebugFromSummary(latestSummary);
                  return (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <details className="detail-collapsible">
                        <summary>TfL query debug</summary>
                        <div className="muted" style={{ marginTop: 8, display: "grid", gap: 6 }}>
                          <div>
                            <strong>Decision:</strong>{" "}
                            {`level=${tflDbg.level}, matched=${tflDbg.matchedCount}, ignored=${tflDbg.ignoredCount}, impacted=${
                              tflDbg.impactedLines.length ? tflDbg.impactedLines.join(", ") : "none"
                            }`}
                          </div>
                          <div>
                            <strong>Window:</strong>{" "}
                            {`${tflDbg.windowStart ? formatDt(tflDbg.windowStart) : "N/A"} -> ${
                              tflDbg.windowEnd ? formatDt(tflDbg.windowEnd) : "N/A"
                            }`}
                          </div>
                          <div>
                            <strong>Segments:</strong>{" "}
                            {tflDbg.segments.length
                              ? tflDbg.segments
                                  .slice(0, 4)
                                  .map(
                                    (seg) =>
                                      `${seg.from || "N/A"}->${seg.via || "N/A"}->${seg.to || "N/A"} (${ 
                                        seg.start ? formatDt(seg.start) : "N/A"
                                      }..${seg.end ? formatDt(seg.end) : "N/A"})`
                                  )
                                  .join(" | ")
                              : "none"}
                          </div>
                          <div>
                            <strong>Reasons:</strong>{" "}
                            {`match=[${tflDbg.matchReasons.length ? tflDbg.matchReasons.join(", ") : "none"}], ignore=[${
                              tflDbg.ignoreReasons.length ? tflDbg.ignoreReasons.join(", ") : "none"
                            }]`}
                          </div>
                          <div>
                            <strong>Query:</strong>{" "}
                            {`checked_at=${tflDbg.queryCheckedAt ? formatDt(tflDbg.queryCheckedAt) : "N/A"}, staleness=${
                              tflDbg.stalenessMins != null ? `${tflDbg.stalenessMins}m` : "N/A"
                            }, global_impacted=${
                              tflDbg.globalImpactedLines.length ? tflDbg.globalImpactedLines.join(", ") : "none"
                            }`}
                          </div>
                        </div>
                      </details>
                    </div>
                  );
                })()}
              </div>
              <div style={{ marginTop: 10 }}>
                <details className="detail-collapsible">
                  <summary>Road checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = roadChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading road checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No road checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`road-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • roads: ${run.roads.length}`}
                                {` • NH calls: ${Number(run.nhCalls?.calls || 0)}`}
                                {Number(run.nhCalls?.errors || 0) > 0 ? ` • NH errors: ${Number(run.nhCalls.errors)}` : ""}
                                {Number(run.runMeta?.nhMatchedCount || 0) > 0
                                  ? ` • NH matched: ${Number(run.runMeta.nhMatchedCount)} (maj:${run.runMeta?.nhImpactCounts?.major || 0}, adv:${run.runMeta?.nhImpactCounts?.advisory || 0}, fyi:${run.runMeta?.nhImpactCounts?.fyi || 0}, strong:${run.runMeta?.nhImpactCounts?.strong || 0}, time:${run.runMeta?.nhImpactCounts?.timeRelevant || 0})`
                                  : ""}
                                {run.runMeta?.roadNhDue === false ? " • NH not due" : ""}
                                {run.runMeta?.nhChecked === false ? " • NH unchecked" : ""}
                                {run.runMeta?.nhError ? ` • NH ${run.runMeta.nhError}` : ""}
                              </span>
                            </div>
                            {run.roads.map((road) => (
                              <div key={`${run.executionId || idx}-${road.road}`} className="road-line">
                                <span className="road-name">{road.road}</span>
                                <span className={pillClass(road.level)}>{String(road.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`duration: ${road.durationMins != null ? `${road.durationMins}m` : "N/A"}`}</span>
                                {road.timeline && (
                                  <span className="muted">
                                    {`timeline: ${road.timeline.enterAt ? formatDt(road.timeline.enterAt) : "N/A"} -> ${
                                      road.timeline.exitAt ? formatDt(road.timeline.exitAt) : "N/A"
                                    } (${road.timeline.durationMins != null ? `${road.timeline.durationMins}m` : "N/A"}, ${
                                      road.timeline.distanceKm != null ? `${road.timeline.distanceKm}km` : "N/A"
                                    })`}
                                  </span>
                                )}
                                <span className="muted">
                                  {`route dir: ${
                                    road.routeDirections.length
                                      ? road.routeDirections.join(", ")
                                      : road.routeDirectionsParsed.length
                                      ? `${road.routeDirectionsParsed.join(", ")} (from segment key)`
                                      : road.timeline?.directions?.length
                                      ? `${road.timeline.directions.join(", ")} (from step vector)`
                                      : "N/A"
                                  }`}
                                </span>
                                <span className="muted">
                                  {`junctions: ${
                                    road.routeJunctions.length
                                      ? road.routeJunctions.join(", ")
                                      : road.routeJunctionsParsed.length
                                      ? `${road.routeJunctionsParsed.join(", ")} (from segment key)`
                                      : road.timeline?.junctions?.length
                                      ? `${road.timeline.junctions.join(", ")} (from step instruction)`
                                      : road.timeline?.inferredJunctions?.length
                                      ? `${road.timeline.inferredJunctions.join(", ")} (inferred)`
                                      : "N/A"
                                  }`}
                                </span>
                                <span className="muted">{`junction span: ${road.routeJunctionSpan || "N/A"}`}</span>
                                <span className="muted">
                                  {`NH events: ${road.nhEventCount}`}
                                  {road.nhDirections.length ? ` (${road.nhDirections.join(", ")})` : ""}
                                  {road.nhEventCountRaw > road.nhEventCount
                                    ? ` [filtered ${road.nhEventCount}/${road.nhEventCountRaw}]`
                                    : ""}
                                </span>
                                {road.nhSegmentOverlapRatio != null && (
                                  <span className="muted">{`NH overlap: ${Math.round(road.nhSegmentOverlapRatio * 100)}%`}</span>
                                )}
                                {(road.nhOverlapScore != null || road.nhDirectionMatch != null || road.nhSegmentMatch != null) && (
                                  <span className="muted">
                                    {`NH diag: score=${road.nhOverlapScore != null ? road.nhOverlapScore : "N/A"}${
                                      road.nhOverlapConfidence ? ` (${road.nhOverlapConfidence})` : ""
                                    }, dir_match=${ynna(road.nhDirectionMatch)}, seg_match=${ynna(road.nhSegmentMatch)}`}
                                  </span>
                                )}
                                {road.nhMatches?.length > 0 && (
                                  <span className="muted">
                                    {`NH map: ${road.nhMatches
                                      .slice(0, 3)
                                      .map((m) => {
                                        const dir = m.direction || "N/A";
                                        const seg = m.from || m.to ? `${m.from || "?"}->${m.to || "?"}` : "N/A";
                                        const dist = m.minDistanceKm != null ? `${m.minDistanceKm}km` : "N/A";
                                        const overlap = Number.isFinite(Number(m.timeOverlapMins)) ? `${Number(m.timeOverlapMins)}m` : "n/a";
                                        return `${m.impact || "fyi"} ${dir} ${seg} d=${dist} s=${
                                          m.overlapScore != null ? m.overlapScore : "N/A"
                                        } t=${m.timeRelevant ? "yes" : "no"} ov=${overlap} dm=${ynna(m.directionMatch)} jm=${ynna(
                                          m.junctionOverlap
                                        )}`;
                                      })
                                      .join(" | ")}`}
                                  </span>
                                )}
                                <span className="muted">
                                  {`flags: NH_due=${
                                    road.nhDue === true
                                      ? "yes"
                                      : road.nhDue === false
                                      ? "no"
                                      : run.runMeta?.roadNhDue === true
                                      ? "yes"
                                      : run.runMeta?.roadNhDue === false
                                      ? "no"
                                      : "?"
                                  }, Google_due=${
                                    road.googleDue === true
                                      ? "yes"
                                      : road.googleDue === false
                                      ? "no"
                                      : run.runMeta?.roadGoogleDue === true
                                      ? "yes"
                                      : run.runMeta?.roadGoogleDue === false
                                      ? "no"
                                      : "?"
                                  }, paid_road=${
                                    road.paidRoadRan === true
                                      ? "yes"
                                      : road.paidRoadRan === false
                                      ? "no"
                                      : run.runMeta?.paidRoadRan === true
                                      ? "yes"
                                      : "no"
                                  }`}
                                </span>
                                <span className="muted">{`decision: ${road.decision || "—"} / ${road.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>

                <details className="detail-collapsible" style={{ marginTop: 8 }}>
                  <summary>Rail checks</summary>
                  {(() => {
                    const detail = detailByJourney[r.journey_id];
                    const runs = railChecksByExecution(detail);
                    if (detailLoading[r.journey_id]) return <div className="muted" style={{ marginTop: 8 }}>Loading rail checks...</div>;
                    if (!runs.length) return <div className="muted" style={{ marginTop: 8 }}>No rail checks captured yet.</div>;
                    return (
                      <div className="road-run-list">
                        {runs.slice(0, 20).map((run, idx) => (
                          <div key={`rail-run-${r.journey_id}-${run.executionId || idx}`} className="road-run-item">
                            <div className="road-run-head">
                              <strong>{formatDt(run.runAt)}</strong>
                              <span className="muted">
                                {Number.isFinite(Number(run.executionId)) ? ` • exec ${run.executionId}` : ""}
                                {` • CRS: ${run.crsRows.length}`}
                                {` • NR calls: ${Number(run.railCalls?.nr?.calls || 0)}`}
                                {Number(run.railCalls?.nr?.errors || 0) > 0 ? ` • NR errors: ${Number(run.railCalls.nr.errors)}` : ""}
                                {` • RailData calls: ${Number(run.railCalls?.raildata?.calls || 0)}`}
                                {Number(run.railCalls?.raildata?.errors || 0) > 0 ? ` • RailData errors: ${Number(run.railCalls.raildata.errors)}` : ""}
                                {run.runMeta?.paidRailRan === true ? " • NR paid: yes" : " • NR paid: no"}
                                {run.runMeta?.checked === true
                                  ? " • RailData checked: yes"
                                  : run.runMeta?.stale === true
                                  ? " • RailData checked: no (stale)"
                                  : " • RailData checked: no"}
                                {run.runMeta?.railMonitoringReason ? ` • reason: ${run.runMeta.railMonitoringReason}` : ""}
                                {run.runMeta?.error ? ` • free_rail_error: ${run.runMeta.error}` : ""}
                              </span>
                            </div>
                            {run.crsRows.map((row) => (
                              <div key={`${run.executionId || idx}-${row.crs}`} className="road-line">
                                <span className="road-name">{row.crs}</span>
                                <span className={pillClass(row.level)}>{String(row.level || "unknown").toUpperCase()}</span>
                                <span className="muted">{`affected: ${row.affected ? "yes" : "no"}`}</span>
                                <span className="muted">{`incidents: ${row.incidentCount}`}</span>
                                <span className="muted">{`dbg: time=${row.timeRelevantCount}, path=${row.pathOverlapCount}, major=${row.majorOverlapCount}`}</span>
                                {(row.departUtc || row.arriveUtc) && (
                                  <span className="muted">
                                    {`journey: ${formatDt(row.departUtc)} -> ${formatDt(row.arriveUtc)} (${
                                      row.durationMins != null ? `${row.durationMins}m` : "N/A"
                                    }, changes=${row.changes != null ? row.changes : "N/A"})`}
                                  </span>
                                )}
                                <span className="muted">{`decision: ${row.decision || "—"} / ${row.reasonCode || "—"}`}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </details>

                {renderCustomerViewPanel({
                  row: r,
                  detail,
                  draftActionBusy: !!draftActionLoading[r.journey_id],
                  onDraftAction: (notificationId, action, body = null) => runDraftAction(r.journey_id, notificationId, action, body),
                })}

                <strong>Ledger Timeline</strong>
                {detailLoading[r.journey_id] && <div className="muted" style={{ marginTop: 6 }}>Loading detail...</div>}
                {!detailLoading[r.journey_id] && detailByJourney[r.journey_id]?.error && (
                  <div className="muted" style={{ marginTop: 6 }}>{detailByJourney[r.journey_id].error}</div>
                )}
                {!detailLoading[r.journey_id] && detailByJourney[r.journey_id]?.matrix && (
                  <details style={{ marginTop: 10 }}>
                    <summary>Check matrix</summary>
                    {renderCheckMatrix(detailByJourney[r.journey_id])}
                  </details>
                )}
                {!detailLoading[r.journey_id] && detailByJourney[r.journey_id]?.executions && (
                  <>
                    <div className="timeline-grid" style={{ marginTop: 8 }}>
                      <div>
                        <div className="muted" style={{ marginBottom: 8 }}>
                          Source totals:{" "}
                          {(detailByJourney[r.journey_id].source_call_totals || []).length
                            ? (detailByJourney[r.journey_id].source_call_totals || [])
                                .filter((x) => !(String(x.source || "").toLowerCase() === "monitor_runner" && Number(x.total_calls || 0) === 0))
                                .map((x) => `${sourceShort(x.source)}:${x.total_calls}`)
                                .join(" • ")
                            : "N/A"}
                        </div>
                        <div className="muted" style={{ marginBottom: 4 }}>Recent executions</div>
                        {(detailByJourney[r.journey_id].executions || []).slice(0, 8).map((x) => {
                          const execLevel = overallFromExecution(x, r);
                          const htf = Number(x?.hours_to_flight);
                          const showHtf = Number.isFinite(htf) && Math.abs(htf) >= 0.1;
                          const callRows = (detailByJourney[r.journey_id].source_calls || []).filter((s) => Number(s.execution_id) === Number(x.execution_id));
                          const bySource = callRows.reduce((acc, it) => {
                            if (String(it?.source || "").toLowerCase() === "monitor_runner" && Number(it?.call_count || 0) === 0) return acc;
                            const key = String(it?.source || "unknown");
                            acc[key] = (acc[key] || 0) + Number(it?.call_count || 0);
                            return acc;
                          }, {});
                          const callText = Object.keys(bySource).length
                            ? Object.entries(bySource)
                                .map(([k, v]) => `${sourceShort(k)}:${v}`)
                                .join(" ")
                            : "heartbeat";
                          const forcedText = x?.summary?.run_forced ? " • FORCED" : "";
                          const hyperLockText = x?.summary?.hyper_strict_anchor_lock ? " • HYPER_LOCK" : "";
                          return (
                            <div key={`ex-${x.execution_id}`} className="timeline-item">
                              <strong>{formatDt(x.run_at)}</strong>{" "}
                              <span className={pillClass(execLevel)}>{String(execLevel || "unknown").toUpperCase()}</span>{" "}
                              <span className="muted">{x.mode || "—"} {showHtf ? `• ${htf.toFixed(1)}h` : ""} • {callText}{forcedText}{hyperLockText}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div>
                        <div className="muted" style={{ marginBottom: 4 }}>Recent notifications</div>
                        {(detailByJourney[r.journey_id].notifications || []).slice(0, 8).map((n) => (
                          <div key={`nt-${n.notification_id}`} className="timeline-item">
                            <strong>{formatDt(notificationDisplayAt(n))}</strong>{" "}
                            <span className={`pill ${notificationPillClass(n)}`}>{String(notificationDisplayDecision(n) || "—").toUpperCase()}</span>{" "}
                            <span className="muted">
                              {describeNotificationLifecycle(n).join(" • ") || "—"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div className="muted" style={{ marginBottom: 4 }}>Recent deltas</div>
                      {(detailByJourney[r.journey_id].observations || []).slice(0, 12).map((o) => {
                        const changed = !!o?.delta_json?.changed;
                        const changedFields = Array.isArray(o?.delta_json?.changed_fields) ? o.delta_json.changed_fields : [];
                        return (
                          <div key={`ob-${o.observation_id}`} className="timeline-item">
                            <strong>{formatDt(o.created_at)}</strong>{" "}
                            <span className={pillClass(o.severity)}>{String(o.severity || "unknown").toUpperCase()}</span>{" "}
                            <span className="muted">
                              {String(o.source || "").toUpperCase()}:{String(o.target_key || "").toUpperCase()} • {o.decision || "no_change"} • {o.reason_code || "none"}
                              {changed ? ` • Δ ${changedFields.slice(0, 4).join(", ") || "changed"}` : ""}
                            </span>
                          </div>
                        );
                      })}
                      {!(detailByJourney[r.journey_id].observations || []).length && (
                        <div className="muted">No observation deltas yet.</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </td>
          </tr>
        );
        return [rowNode, expNode];
      }),
    [verifiedPastRows, expanded, detailByJourney, detailLoading]
  );

  return (
    <div className="ema-admin-wrap">
      <style>{`
        body.ema-admin-mode header,
        body.ema-admin-mode footer,
        body.ema-admin-mode nav,
        body.ema-admin-mode .navbar,
        body.ema-admin-mode .site-header,
        body.ema-admin-mode .site-footer,
        body.ema-admin-mode .global-header,
        body.ema-admin-mode .global-footer,
        body.ema-admin-mode #site-header,
        body.ema-admin-mode #site-footer {
          display: none !important;
        }
        body.ema-admin-mode {
          background: #07132b !important;
          overflow-x: hidden;
        }
        .ema-admin-wrap { min-height: 100vh; background:#07132b; color:#d7e3fb; padding:16px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; }
        .card { max-width:520px; background:#0d1f42; border:1px solid #203761; border-radius:14px; padding:16px; margin:20px auto; }
        .title { font-size:42px; line-height:1.1; font-weight:900; margin:0; }
        .label { display:block; font-size:12px; color:#9db0d3; margin-bottom:6px; text-transform:uppercase; letter-spacing:.04em; }
        .input { width:100%; border:1px solid #2b4778; background:#091a39; color:#e2ecff; border-radius:10px; padding:10px 12px; font-size:16px; }
        .btn { border:0; border-radius:10px; padding:10px 14px; font-weight:700; cursor:pointer; }
        .btn.primary { background:#17a36b; color:white; }
        .btn.secondary { background:#193563; color:#d7e3fb; margin-right:8px; }
        .btn.tiny { background:#18345f; color:#d5e4ff; padding:6px 10px; font-size:12px; border:1px solid #2f4f84; }
        .msg { margin-top:10px; font-size:13px; color:#c7d6f6; }
        .top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:12px; gap:12px; }
        .meta { color:#a8bad9; font-size:13px; margin-top:6px; }
        .filters { display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin:12px 0; }
        .chip { border:1px solid #2c4d82; border-radius:999px; padding:6px 10px; background:#122b51; color:#d7e3fb; font-size:12px; }
        .chip input { margin-right:6px; }
        .search { min-width:240px; }
        .table-card { border:1px solid #203761; border-radius:14px; overflow:auto; background:linear-gradient(180deg,#0a1a37 0%,#09162f 100%); }
        table { width:100%; min-width:2000px; border-collapse:collapse; }
        th, td { border-bottom:1px solid #203761; text-align:left; padding:10px; font-size:13px; vertical-align:top; }
        th { position:sticky; top:0; background:#0f2348; color:#cfe2ff; text-transform:uppercase; font-size:12px; letter-spacing:.04em; }
        tr:hover td { background: rgba(74, 122, 206, 0.08); }
        .muted { color:#9db0d3; font-size:12px; }
        .pill { display:inline-block; border-radius:999px; border:1px solid transparent; padding:3px 9px; font-weight:700; font-size:12px; text-transform:uppercase; letter-spacing:.02em; }
        .pill.ok { color:#9ef4c8; border-color: rgba(17,192,122,.5); background: rgba(17,192,122,.18); }
        .pill.light { color:#b6f7dc; border-color: rgba(105,216,168,.5); background: rgba(105,216,168,.18); }
        .pill.warn { color:#ffe0b2; border-color: rgba(255,180,84,.55); background: rgba(255,180,84,.2); }
        .pill.bad { color:#ffd0d0; border-color: rgba(255,107,107,.55); background: rgba(255,107,107,.2); }
        .pill.na { color:#c9d6f2; border-color: rgba(126,143,173,.45); background: rgba(126,143,173,.15); }
        .phase-pill { display:inline-block; padding:2px 8px; border-radius:999px; background:#173666; border:1px solid #2d4f84; font-size:12px; font-weight:700; }
        .bar { margin-top:6px; width:160px; height:8px; border-radius:999px; background:#10284c; border:1px solid #264675; overflow:hidden; }
        .bar span { display:block; height:100%; background:linear-gradient(90deg,#11c07a,#4fd4ff); }
        .rel { position:relative; padding-left:12px; white-space:nowrap; }
        .rel:before { content:""; width:7px; height:7px; border-radius:50%; position:absolute; left:0; top:6px; background:#7e8fad; }
        .rel.ok:before { background:#11c07a; }
        .rel.warn:before { background:#ffb454; }
        .rel.bad:before { background:#ff6b6b; }
        .action { display:inline-block; padding:4px 8px; border-radius:8px; font-weight:700; }
        .action.ok { background:rgba(17,192,122,.14); color:#9ef4c8; }
        .action.warn { background:rgba(255,180,84,.16); color:#ffe0b2; }
        .action.bad { background:rgba(255,107,107,.16); color:#ffd0d0; }
        .expanded-row td { background: rgba(11, 30, 60, 0.75); }
        .expanded-title { font-size:14px; font-weight:800; margin-bottom:8px; color:#dff0ff; }
        .expanded-grid { display:grid; grid-template-columns: repeat(2, minmax(240px, 1fr)); gap:6px 12px; }
        .timeline-grid { display:grid; grid-template-columns: repeat(2, minmax(260px, 1fr)); gap:8px 12px; }
        .timeline-item { font-size:12px; margin-bottom:6px; color:#d7e3fb; }
        .summary-grid { display:grid; grid-template-columns: repeat(5, minmax(160px, 1fr)); gap:10px; margin: 8px 0 12px; }
        .summary-card { border:1px solid #2a4b80; border-radius:12px; background:#0f274d; padding:10px; }
        .summary-label { font-size:11px; text-transform:uppercase; letter-spacing:.05em; color:#9db0d3; }
        .summary-value { font-size:24px; font-weight:800; margin-top:4px; color:#e3efff; }
        .corridor-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(280px, 1fr)); gap:12px; }
        .corridor-card { border:1px solid #2a4b80; border-radius:12px; background:#0f274d; padding:12px; display:grid; gap:10px; }
        .corridor-head { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
        .corridor-title { font-size:14px; font-weight:800; color:#e3efff; line-height:1.3; }
        .alert-panel { border:1px solid #2a4b80; border-radius:12px; background:#0d2244; padding:12px; margin-bottom:14px; display:grid; gap:12px; }
        .alert-panel-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; }
        .alert-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(300px, 1fr)); gap:12px; }
        .alert-column { display:grid; gap:10px; }
        .alert-column-title { font-size:12px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; color:#c7daf9; }
        .alert-item { border:1px solid rgba(124, 156, 216, 0.28); border-radius:10px; background:#10284f; padding:10px; display:grid; gap:6px; }
        .alert-item.amber { background:linear-gradient(180deg, rgba(255, 180, 84, 0.12), rgba(16, 40, 79, 0.96)); }
        .alert-item.red, .alert-item.critical_red { background:linear-gradient(180deg, rgba(255, 107, 107, 0.15), rgba(16, 40, 79, 0.98)); }
        .alert-item-head { display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
        .alert-drawer-list { display:grid; gap:10px; }
        .alert-row-list { display:grid; gap:10px; }
        .alert-grid-head { display:grid; grid-template-columns:1.1fr 0.9fr 1fr 1.25fr 1.9fr 1.15fr 110px; gap:12px; padding:0 16px 6px; color:#9eb6dd; font-size:11px; text-transform:uppercase; letter-spacing:.05em; }
        .alert-grid-row { border:1px solid rgba(124, 156, 216, 0.24); border-radius:10px; background:#10284f; padding:10px 16px; display:grid; grid-template-columns:1.1fr 0.9fr 1fr 1.25fr 1.9fr 1.15fr 110px; gap:12px; align-items:center; }
        .alert-grid-row.amber { background:linear-gradient(180deg, rgba(255, 180, 84, 0.12), rgba(16, 40, 79, 0.96)); }
        .alert-grid-row.red, .alert-grid-row.critical_red { background:linear-gradient(180deg, rgba(255, 107, 107, 0.15), rgba(16, 40, 79, 0.98)); }
        .alert-grid-row.stale { border-color:rgba(255, 180, 84, 0.45); box-shadow:0 0 0 1px rgba(255, 180, 84, 0.18) inset; }
        .alert-row { border:1px solid rgba(124, 156, 216, 0.28); border-radius:10px; background:#10284f; padding:10px 12px; display:grid; gap:8px; }
        .alert-row.amber { background:linear-gradient(180deg, rgba(255, 180, 84, 0.12), rgba(16, 40, 79, 0.96)); }
        .alert-row.red, .alert-row.critical_red { background:linear-gradient(180deg, rgba(255, 107, 107, 0.15), rgba(16, 40, 79, 0.98)); }
        .alert-row-main { display:grid; grid-template-columns:minmax(220px, 1.15fr) minmax(150px, 0.85fr) minmax(320px, 2fr) auto; gap:12px; align-items:start; }
        .alert-row-title { font-size:13px; font-weight:800; color:#e9f2ff; line-height:1.3; }
        .alert-row-kicker { font-size:11px; color:#bdd0f3; text-transform:uppercase; letter-spacing:.04em; }
        .alert-row-meta { display:flex; flex-wrap:wrap; gap:10px; font-size:11px; color:#bdd0f3; }
        .alert-grid-col { display:grid; gap:4px; min-width:0; align-content:start; }
        .alert-grid-col-severity { justify-items:end; }
        .alert-grid-label { display:none; font-size:10px; color:#9eb6dd; text-transform:uppercase; letter-spacing:.05em; }
        .alert-grid-value { font-size:12px; line-height:1.35; color:#e9f2ff; overflow-wrap:anywhere; }
        .alert-grid-footer { grid-column:1 / -1; display:flex; flex-wrap:wrap; gap:12px; padding-top:6px; font-size:11px; color:#bdd0f3; border-top:1px solid rgba(124, 156, 216, 0.14); }
        .alert-row-empty { color:#bdd0f3; font-size:12px; }
        @media (max-width: 1400px) {
          .alert-grid-head, .alert-grid-row { grid-template-columns:1fr 0.9fr 0.95fr 1.15fr 1.5fr 1fr 100px; }
        }
        @media (max-width: 1100px) {
          .alert-grid-head { display:none; }
          .alert-grid-row { grid-template-columns:1fr 1fr; padding:12px; }
          .alert-row-main { grid-template-columns:1fr; }
          .alert-grid-col-severity { justify-items:start; }
          .alert-grid-label { display:block; }
          .alert-grid-value { font-size:13px; }
        }
        @media (max-width: 720px) {
          .alert-grid-row { grid-template-columns:1fr; }
        }
        .corridor-metrics { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:8px 12px; color:#d9e7ff; font-size:13px; }
        .corridor-strip { display:grid; gap:8px; padding-top:4px; }
        .corridor-strip-head { display:flex; align-items:center; justify-content:space-between; gap:10px; color:#c9d9f8; font-size:12px; font-weight:700; }
        .corridor-segments { display:flex; align-items:stretch; gap:8px; overflow-x:auto; padding-bottom:4px; }
        .corridor-segment { min-width:116px; border-radius:10px; border:1px solid rgba(124, 156, 216, 0.3); background:#10284f; padding:8px 10px; display:grid; gap:5px; }
        .corridor-segment.green { background:linear-gradient(180deg, rgba(17, 192, 122, 0.14), rgba(16, 40, 79, 0.96)); box-shadow: inset 0 0 0 1px rgba(17, 192, 122, 0.24); }
        .corridor-segment.light_green { background:linear-gradient(180deg, rgba(105, 216, 168, 0.14), rgba(16, 40, 79, 0.96)); box-shadow: inset 0 0 0 1px rgba(139, 214, 108, 0.26); }
        .corridor-segment.amber { background:linear-gradient(180deg, rgba(255, 180, 84, 0.18), rgba(16, 40, 79, 0.96)); box-shadow: inset 0 0 0 1px rgba(255, 180, 84, 0.36); }
        .corridor-segment.red, .corridor-segment.critical_red { background:linear-gradient(180deg, rgba(255, 107, 107, 0.2), rgba(16, 40, 79, 0.98)); box-shadow: inset 0 0 0 1px rgba(255, 107, 107, 0.42); }
        .corridor-segment-line { align-self:center; min-width:24px; height:2px; background:linear-gradient(90deg, rgba(120, 150, 210, 0.2), rgba(120, 150, 210, 0.7)); margin:0 -2px; }
        .corridor-segment-top { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; }
        .corridor-segment-label { font-size:11px; font-weight:800; color:#e6efff; line-height:1.25; }
        .corridor-segment-meta { font-size:11px; color:#bdd0f3; }
        .corridor-segment-delta { font-size:11px; font-weight:800; color:#ffffff; }
        .collapsible-summary-row { display:flex; align-items:center; justify-content:space-between; gap:12px; width:100%; }
        .collapsible-summary-meta { font-size:12px; color:#d8e6ff; font-weight:700; }
        .rail-member-list { display:grid; gap:8px; padding-top:4px; }
        .rail-member { border:1px solid rgba(124, 156, 216, 0.28); border-radius:10px; padding:10px; background:#10284f; display:grid; gap:6px; }
        .rail-member-head { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; }
        .rail-member-title { font-size:12px; font-weight:800; color:#e6efff; }
        .rail-member-meta { font-size:11px; color:#bdd0f3; }
        .rail-member-reason { font-size:12px; color:#dbe8ff; }
        .airline-mix-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(280px, 1fr)); gap:12px; padding-top:4px; }
        .airline-mix-split { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:8px; }
        .rail-service-list { display:grid; gap:4px; }
        .rail-service-item { font-size:11px; color:#cbd9f6; }
        .journey-type {
          display: inline-block;
          margin-top: 6px;
          margin-bottom: 4px;
          padding: 3px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .02em;
          border: 1px solid transparent;
          text-transform: uppercase;
        }
        .journey-type.departure {
          color: #d8f6ff;
          background: rgba(79, 196, 255, 0.18);
          border-color: rgba(79, 196, 255, 0.45);
        }
        .journey-type.arrival {
          color: #ffe9c4;
          background: rgba(255, 180, 84, 0.18);
          border-color: rgba(255, 180, 84, 0.45);
        }
        .journey-type.return {
          color: #d8ffe7;
          background: rgba(46, 204, 113, 0.18);
          border-color: rgba(46, 204, 113, 0.45);
        }
        .journey-type.unknown {
          color: #d0dcf3;
          background: rgba(126, 143, 173, 0.2);
          border-color: rgba(126, 143, 173, 0.45);
        }
        .detail-collapsible {
          margin: 0 0 10px;
          border: 1px solid #2b4f84;
          border-radius: 10px;
          background: rgba(14, 40, 80, 0.45);
          padding: 0 10px 8px;
        }
        .detail-collapsible > summary {
          cursor: pointer;
          list-style: none;
          padding: 8px 0;
          font-weight: 800;
          color: #d8e9ff;
        }
        .detail-collapsible > summary::-webkit-details-marker { display: none; }
        .detail-collapsible > summary::before {
          content: "▸";
          margin-right: 8px;
          color: #8fb9f4;
        }
        .detail-collapsible[open] > summary::before { content: "▾"; }
        .road-run-list { display: grid; gap: 8px; }
        .road-run-item {
          border: 1px solid #264675;
          border-radius: 8px;
          background: rgba(9, 28, 58, 0.55);
          padding: 8px;
        }
        .road-run-head { margin-bottom: 6px; }
        .road-line {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 8px;
          padding: 5px 0;
          border-top: 1px dashed rgba(87, 129, 187, 0.35);
        }
        .road-line:first-of-type { border-top: 0; }
        .road-name {
          display: inline-block;
          min-width: 40px;
          font-weight: 900;
          color: #dff2ff;
          letter-spacing: .02em;
        }
        .section-title { font-size:15px; font-weight:800; letter-spacing:.03em; text-transform:uppercase; color:#cfe2ff; margin: 0 0 8px; }
        .collapsible {
          margin-top: 14px;
          border: 1px solid #3f6ca8;
          border-radius: 12px;
          background: linear-gradient(180deg, #153a70 0%, #0f2a54 100%);
          box-shadow: 0 0 0 1px rgba(118, 173, 255, 0.25) inset;
        }
        .collapsible summary {
          cursor: pointer;
          list-style: none;
          font-size: 16px;
          font-weight: 900;
          color: #ffffff;
          letter-spacing: .02em;
          padding: 12px 14px;
          border-radius: 12px;
          text-shadow: 0 1px 0 rgba(0,0,0,.35);
          background: linear-gradient(90deg, rgba(87, 160, 255, 0.30) 0%, rgba(87, 160, 255, 0.12) 100%);
          border-bottom: 1px solid rgba(118, 173, 255, 0.35);
        }
        .collapsible summary::-webkit-details-marker { display: none; }
        .collapsible summary::before {
          content: "▸";
          display: inline-block;
          margin-right: 8px;
          color: #9fd0ff;
          transform: translateY(-1px);
        }
        .collapsible[open] summary::before { content: "▾"; }
        .dashboard-sections {
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        .alert-center-section {
          order: 30;
        }
        .monitoring-tables-section {
          order: 40;
        }
      `}</style>

      {phase !== "dashboard" && (
          <div className="card">
          <h1 className="title" style={{ fontSize: 34, marginBottom: 14 }}>Journey Monitor Admin</h1>
          {phase === "email" && (
            <form onSubmit={submitEmail}>
              <label className="label" htmlFor="admin-email">Admin Email</label>
              <input
                id="admin-email"
                className="input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <div style={{ marginTop: 12 }}>
                <button className="btn primary" type="submit" disabled={busy}>{busy ? "Sending..." : "Send Code"}</button>
              </div>
            </form>
          )}
          {phase === "code" && (
            <form onSubmit={submitCode}>
              <label className="label" htmlFor="admin-code">Verification Code</label>
              <input
                id="admin-code"
                className="input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
              <div style={{ marginTop: 12 }}>
                <button className="btn secondary" type="button" onClick={() => setPhase("email")} disabled={busy}>Back</button>
                <button className="btn primary" type="submit" disabled={busy}>{busy ? "Verifying..." : "Verify"}</button>
              </div>
            </form>
          )}
          {msg && <div className="msg">{msg}</div>}
        </div>
      )}

      {phase === "dashboard" && (
        <>
          <div className="top">
            <div>
              <h1 className="title">{airportCode === "LHR" ? "Heathrow Monitoring Dashboard" : "EMA Monitoring Dashboard"}</h1>
              <div className="meta">
                Airport: {airportCode} • Last dashboard refresh: {formatDt(generatedAt)} • Last monitor run: {formatDt(latestMonitorRunAt)} • Current: {currentRows.length} • Verified: {verifiedCurrentRows.length} • Unverified: {unverifiedCurrentRows.length} • Past: {pastRows.length} • Filtered rows: {filteredRows.length}/{rows.length}
              </div>
              <div className="meta">Auto-refresh: 5m</div>
            </div>
            <button className="btn secondary" type="button" onClick={logout}>Logout</button>
          </div>

          <div className="filters">
            <input
              className="input search"
              placeholder="Search email / flight / postcode / journey id"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className="chip"><input type="checkbox" checked={amberRedOnly} onChange={(e) => setAmberRedOnly(e.target.checked)} />Only Amber/Red</label>
            <label className="chip"><input type="checkbox" checked={dueSoonOnly} onChange={(e) => setDueSoonOnly(e.target.checked)} />Due in 60m</label>
            <label className="chip"><input type="checkbox" checked={hyperOnly} onChange={(e) => setHyperOnly(e.target.checked)} />Hyper only</label>
            <label className="chip"><input type="checkbox" checked={railRiskOnly} onChange={(e) => setRailRiskOnly(e.target.checked)} />Rail risk only</label>
            <label className="chip">
              Airport:
              <select
                className="input"
                style={{ marginLeft: 6, padding: "4px 8px", fontSize: 12, width: 110 }}
                value={airportCode}
                onChange={(e) => setAirportCode(String(e.target.value || "EMA").toUpperCase())}
              >
                <option value="EMA">EMA</option>
                <option value="LHR">LHR</option>
              </select>
            </label>
            <label className="chip">
              Notify:
              <select
                className="input"
                style={{ marginLeft: 6, padding: "4px 8px", fontSize: 12, width: 140 }}
                value={notifyTelemetry}
                onChange={(e) => setNotifyTelemetry(e.target.value)}
              >
                <option value="all">All</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="deduped">Deduped</option>
                <option value="policy">Policy</option>
              </select>
            </label>
            <label className="chip"><input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} />Hide unverified section</label>
          </div>

          <div className="dashboard-sections">
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-label">Active journeys</div>
              <div className="summary-value">{summary.active}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Require notification</div>
              <div className="summary-value">{summary.requireNotification}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">In hyper mode</div>
              <div className="summary-value">{summary.hyper}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Scheduler errors</div>
              <div className="summary-value">{summary.schedulerErrors}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Notify Reliability (24h)</div>
              <div className="summary-value">{notifyReliability.total || 0}</div>
              <div className="muted">
                S:{notifyReliability.sent || 0} • F:{notifyReliability.failed || 0} • D:{notifyReliability.deduped || 0} • P:{notifyReliability.policy || 0}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Motorway corridors</div>
              <div className="summary-value">{roadCorridorSummary.count || 0}</div>
              <div className="muted">
                Worst: {roadCorridorSummary.worst ? `${roadCorridorSummary.worst.corridor_key} ${String(roadCorridorSummary.worst.status_label || "unknown").toUpperCase()}` : "N/A"}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Road alerts open</div>
              <div className="summary-value">{roadAlertSummary.open_count || 0}</div>
              <div className="muted">
                Red: {roadAlertSummary.red_open_count || 0} • Amber: {roadAlertSummary.amber_open_count || 0}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">NH alerts open</div>
              <div className="summary-value">{roadNhAlertSummary.open_count || 0}</div>
              <div className="muted">
                Red: {roadNhAlertSummary.red_open_count || 0} • Amber: {roadNhAlertSummary.amber_open_count || 0}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Rail corridors</div>
              <div className="summary-value">{railCorridorSummary.count || 0}</div>
              <div className="muted">
                Worst: {railCorridorSummary.worst ? `${railCorridorSummary.worst.corridor_key} ${String(railCorridorSummary.worst.status_label || "unknown").toUpperCase()}` : "N/A"}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Rail alerts open</div>
              <div className="summary-value">{railAlertSummary.open_count || 0}</div>
              <div className="muted">
                Red: {railAlertSummary.red_open_count || 0} • Amber: {railAlertSummary.amber_open_count || 0} • Light: {railAlertSummary.light_green_open_count || 0}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Airport ops alerts</div>
              <div className="summary-value">{airportOpsIsAvailable(airportOpsAlertSummary) ? airportOpsAlertSummary.open_count || 0 : "N/A"}</div>
              <div className="muted">
                {airportOpsAvailabilityText(airportOpsAlertSummary, airportOpsAlertError) ||
                  `Red: ${airportOpsAlertSummary.red_open_count || 0} • Amber: ${airportOpsAlertSummary.amber_open_count || 0}`}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Weather alerts</div>
              <div className="summary-value">{weatherAlertSummary.last_evaluated_at ? weatherAlertSummary.open_count || 0 : "N/A"}</div>
              <div className="muted">
                {(weatherAlertError || !weatherAlertSummary.last_evaluated_at)
                  ? (weatherAlertError || "Weather alert feed unavailable.")
                  : `Red: ${weatherAlertSummary.red_open_count || 0} • Amber: ${weatherAlertSummary.amber_open_count || 0}`}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Manual alerts</div>
              <div className="summary-value">{manualAlertSummary.open_count || 0}</div>
              <div className="muted">
                Red: {manualAlertSummary.red_open_count || 0} • Amber: {manualAlertSummary.amber_open_count || 0}
              </div>
            </div>
          </div>
          <div className="meta" style={{ marginBottom: 10 }}>
            Projection health: active={projectionHealth?.active_verified || 0} • missing={projectionHealth?.missing_projection || 0} • stale={projectionHealth?.stale_projection || 0}
          </div>

          <details className="collapsible alert-center-section" open={alertCenterOpen} onToggle={(e) => setAlertCenterOpen(e.currentTarget.open)}>
            <summary>
                <span className="collapsible-summary-row">
                  <span>Active Alerts</span>
                  <span className="collapsible-summary-meta">
                  {totalOpenAlerts} open • Road {roadAlertSummary.open_count || 0} • NH {roadNhAlertSummary.open_count || 0} • Rail {railAlertSummary.open_count || 0} • Ops {airportOpsIsAvailable(airportOpsAlertSummary) ? airportOpsAlertSummary.open_count || 0 : "N/A"} • Weather {weatherAlertSummary.last_evaluated_at ? weatherAlertSummary.open_count || 0 : "N/A"} • Manual {manualAlertSummary.open_count || 0}
                </span>
              </span>
            </summary>
            <div className="table-card" style={{ marginBottom: 16 }}>
              <div className="alert-panel">
                <div className="alert-panel-head">
                  <div>
                    <div className="corridor-title">Deterministic alert review</div>
                    <div className="muted">
                      Road evaluated: {formatDt(roadAlertSummary.last_evaluated_at)} • NH evaluated: {formatDt(roadNhAlertSummary.last_evaluated_at)} • Rail evaluated: {formatDt(railAlertSummary.last_evaluated_at)} • Ops evaluated: {airportOpsIsAvailable(airportOpsAlertSummary) ? formatDt(airportOpsAlertSummary.last_evaluated_at) : "Unavailable"} • Weather evaluated: {weatherAlertSummary.last_evaluated_at ? formatDt(weatherAlertSummary.last_evaluated_at) : "Unavailable"} • Manual updated: {manualAlertSummary.last_evaluated_at ? formatDt(manualAlertSummary.last_evaluated_at) : "N/A"}
                    </div>
                  </div>
                  <div className="muted">
                    Open {totalOpenAlerts} • Recent events {(roadAlertEvents.length || 0) + (roadNhAlertEvents.length || 0) + (railAlertEvents.length || 0) + (airportOpsAlertEvents.length || 0) + (weatherAlertEvents.length || 0) + (manualAlertEvents.length || 0)}
                  </div>
                </div>
                {roadAlertError ? <div className="muted">{roadAlertError}</div> : null}
                <div className="alert-drawer-list">
                  <details className="detail-collapsible">
                    <summary>{`Road alerts (${activeRoadAlertsSorted.length})`}</summary>
                    {activeRoadAlertsSorted.length ? (
                      <div className="alert-row-list">
                        <div className="alert-grid-head">
                          <span>Time-start</span>
                          <span>System</span>
                          <span>Specific</span>
                          <span>Description</span>
                          <span>Reason</span>
                          <span>Cause</span>
                          <span>Severity</span>
                        </div>
                        {activeRoadAlertsSorted.map((item) => {
                          const evidence = item?.last_evidence_json || {};
                          const nhMatches = Array.isArray(evidence?.nh_matches) ? evidence.nh_matches : [];
                          const linkedManual = manualLinksByTarget.get(`road:${item.target_key}`) || [];
                          return (
                            <AlertGridRow
                              key={item.target_key}
                              openedAt={item.opened_at}
                              system={`Road (${shortSourceLabel(item.source)})`}
                              specific={item.road_name || `${item.from_junction || "?"} → ${item.to_junction || "?"}`}
                              description={roadAlertDescription(item)}
                              reason={roadAlertExactReason(item)}
                              cause={linkedManual[0]?.title || nhMatches[0]?.title || "Road flow variance"}
                              severity={item.current_severity}
                              footer={[
                                `Affected ${item.route_label || `${item.from_junction || "?"} to ${item.to_junction || "?"}`}`,
                                `Last checked ${formatDt(item.last_observed_at_utc)}`,
                                `Next check approx ${formatDt(alertNextCheckAt(item, roadAlertSummary.last_evaluated_at))}`,
                              ]}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div className="alert-row-empty">No open road alerts.</div>
                    )}
                    <details className="detail-collapsible" style={{ marginTop: 8 }}>
                      <summary>{`View older alerts / events (${roadAlertEvents.length})`}</summary>
                      {roadAlertEvents.length ? (
                        <div className="alert-row-list">
                          {roadAlertEvents.map((event) => (
                            <div key={`road-event-${event.event_id}`} className={`alert-row ${fmtLevel(event.severity_after)}`}>
                              <div className="alert-row-main">
                                <div>
                                  <div className="alert-row-title">{event.title || event.display_name}</div>
                                  <div className="alert-row-kicker">{`${event.corridor_key || event.target_key || "road"} • ${event.segment_key || event.road_name || "segment"}`}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Affected area</div>
                                  <div>{event.display_name || event.road_name || event.target_key || "Road corridor"}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Reason</div>
                                  <div>{Number.isFinite(Number(event.metric_value_mins)) ? `Journey time +${Math.round(Number(event.metric_value_mins))}m versus baseline` : formatDt(event.observed_at_utc)}</div>
                                </div>
                                <span className={pillClass(event.severity_after)}>{String(event.event_type || "event").toUpperCase()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="alert-row-empty">No older road alert events.</div>
                      )}
                    </details>
                  </details>

                  <details className="detail-collapsible">
                    <summary>{`NH alerts (${activeNhAlertsSorted.length})`}</summary>
                    {activeNhAlertsSorted.length ? (
                      <div className="alert-row-list">
                        <div className="alert-grid-head">
                          <span>Time-start</span>
                          <span>System</span>
                          <span>Specific</span>
                          <span>Description</span>
                          <span>Reason</span>
                          <span>Cause</span>
                          <span>Severity</span>
                        </div>
                        {activeNhAlertsSorted.map((item) => {
                          const evidence = item?.last_evidence_json || {};
                          const nhMatches = Array.isArray(evidence?.nh_matches) ? evidence.nh_matches : [];
                          const topMatch = nhMatches[0] || {};
                          const linkedManual = manualLinksByTarget.get(`nh:${item.target_key}`) || [];
                          return (
                            <AlertGridRow
                              key={item.target_key}
                              openedAt={item.opened_at}
                              system="National Highways"
                              specific={`${item.road_name || "Road"} ${item.from_junction || "?"}–${item.to_junction || "?"}`}
                              description={nhAlertDescription(item)}
                              reason={nhAlertExactReason(item)}
                              cause={linkedManual[0]?.title || humanizeToken(topMatch?.time_status || "incident")}
                              severity={item.current_severity}
                              footer={[
                                `${String(topMatch?.time_status || "unknown").toUpperCase()} • match ${String(topMatch?.match_confidence || "unknown").toUpperCase()}`,
                                `Impact ${item.max_traffic_delay_mins == null ? "n/a" : `+${Number(item.max_traffic_delay_mins).toFixed(1)}m`}`,
                                `Last checked ${formatDt(item.last_observed_at_utc)}`,
                                `Next check approx ${formatDt(alertNextCheckAt(item, roadNhAlertSummary.last_evaluated_at))}`,
                              ]}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div className="alert-row-empty">No open NH alerts.</div>
                    )}
                    <details className="detail-collapsible" style={{ marginTop: 8 }}>
                      <summary>{`View older alerts / events (${roadNhAlertEvents.length})`}</summary>
                      {roadNhAlertEvents.length ? (
                        <div className="alert-row-list">
                          {roadNhAlertEvents.map((event) => (
                            <div key={`nh-event-${event.event_id}`} className={`alert-row ${fmtLevel(event.severity_after)}`}>
                              <div className="alert-row-main">
                                <div>
                                  <div className="alert-row-title">{event.title || event.display_name}</div>
                                  <div className="alert-row-kicker">{`${event.corridor_key || event.target_key || "NH"} • ${event.segment_key || event.road_name || "segment"}`}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Affected area</div>
                                  <div>{event.display_name || event.road_name || "National Highways corridor"}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Reason</div>
                                  <div>{event.title || formatDt(event.observed_at_utc)}</div>
                                </div>
                                <span className={pillClass(event.severity_after)}>{String(event.event_type || "event").toUpperCase()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="alert-row-empty">No older NH alert events.</div>
                      )}
                    </details>
                  </details>

                  <details className="detail-collapsible">
                    <summary>{`Rail alerts (${activeRailAlertsSorted.length})`}</summary>
                    {activeRailAlertsSorted.length ? (
                      <div className="alert-row-list">
                        <div className="alert-grid-head">
                          <span>Time-start</span>
                          <span>System</span>
                          <span>Specific</span>
                          <span>Description</span>
                          <span>Reason</span>
                          <span>Cause</span>
                          <span>Severity</span>
                        </div>
                        {activeRailAlertsSorted.map((item) => {
                          const linkedManual = manualLinksByTarget.get(`rail:${item.target_key}`) || [];
                          return (
                          <AlertGridRow
                            key={item.target_key}
                            openedAt={item.opened_at}
                            system="Rail"
                            specific={item.display_name}
                            description={railAlertDescription(item)}
                            reason={railAlertExactReason(item) || `${item.cancelled_services_count || 0} cancelled • ${item.delayed_services_count || 0} delayed`}
                            cause={linkedManual[0]?.title || humanizeToken(item.alert_profile || item.target_kind || "rail")}
                            severity={item.current_severity}
                            footer={[
                              `Profile ${humanizeToken(item.alert_profile || item.target_kind || "rail")}`,
                              `Max delay ${item.max_delay_mins == null ? "n/a" : `+${Number(item.max_delay_mins).toFixed(1)}m`}`,
                              `Incidents ${item.incident_count || 0}`,
                              `Last checked ${formatDt(item.last_observed_at_utc)}`,
                              `Next check approx ${formatDt(alertNextCheckAt(item, railAlertSummary.last_evaluated_at))}`,
                            ]}
                          />
                        )})}
                      </div>
                    ) : (
                      <div className="alert-row-empty">No open rail alerts.</div>
                    )}
                    <details className="detail-collapsible" style={{ marginTop: 8 }}>
                      <summary>{`View older alerts / events (${railAlertEvents.length})`}</summary>
                      {railAlertEvents.length ? (
                        <div className="alert-row-list">
                          {railAlertEvents.map((event) => (
                            <div key={`rail-event-${event.event_id}`} className={`alert-row ${fmtLevel(event.severity_after)}`}>
                              <div className="alert-row-main">
                                <div>
                                  <div className="alert-row-title">{event.title || event.display_name}</div>
                                  <div className="alert-row-kicker">{`${humanizeToken(event.alert_profile || event.target_kind || "rail")} • ${event.crs || event.line_id || "network"}`}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Affected area</div>
                                  <div>{event.display_name || event.target_key || "Rail target"}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Reason</div>
                                  <div>{event.title || formatDt(event.observed_at_utc)}</div>
                                </div>
                                <span className={pillClass(event.severity_after)}>{String(event.event_type || "event").toUpperCase()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="alert-row-empty">No older rail alert events.</div>
                      )}
                    </details>
                  </details>

                  <details className="detail-collapsible">
                    <summary>{`Airport ops alerts (${airportOpsIsAvailable(airportOpsAlertSummary) ? activeAirportOpsAlertsSorted.length : "N/A"})`}</summary>
                    {activeAirportOpsAlertsSorted.length ? (
                      <div className="alert-row-list">
                        <div className="alert-grid-head">
                          <span>Time-start</span>
                          <span>System</span>
                          <span>Specific</span>
                          <span>Description</span>
                          <span>Reason</span>
                          <span>Cause</span>
                          <span>Severity</span>
                        </div>
                        {activeAirportOpsAlertsSorted.map((item) => {
                          const linkedManual = manualLinksByTarget.get(`airport_ops:${item.target_key}`) || [];
                          return (
                          <AlertGridRow
                            key={item.target_key}
                            openedAt={item.opened_at}
                            system="Airport ops"
                            specific={item.entity_label || item.display_name || airportCode}
                            description={airportOpsDescription(item)}
                            reason={airportOpsExactReason(item)}
                            cause={airportOpsCauseText(item, linkedManual[0])}
                            severity={item.current_severity}
                            stale={item?.last_observed_at_utc ? Date.now() - new Date(item.last_observed_at_utc).getTime() > 6 * 60 * 60 * 1000 : false}
                            footer={[
                              airportOpsMetricText(item),
                              `Category ${humanizeToken(item.category || "ops")}`,
                              `Last checked ${formatDt(item.last_observed_at_utc)}`,
                              `Next check approx ${formatDt(alertNextCheckAt(item, airportOpsAlertSummary.last_evaluated_at))}`,
                            ]}
                          />
                        )})}
                      </div>
                    ) : (
                      <div className="alert-row-empty">{airportOpsAvailabilityText(airportOpsAlertSummary, airportOpsAlertError) || "No open airport ops alerts."}</div>
                    )}
                    <details className="detail-collapsible" style={{ marginTop: 8 }}>
                      <summary>{`View older alerts / events (${airportOpsAlertEvents.length})`}</summary>
                      {airportOpsAlertEvents.length ? (
                        <div className="alert-row-list">
                          {airportOpsAlertEvents.map((event) => (
                            <div key={`ops-event-${event.event_id}`} className={`alert-row ${fmtLevel(event.severity_after)}`}>
                              <div className="alert-row-main">
                                <div>
                                  <div className="alert-row-title">{event.title || event.display_name}</div>
                                  <div className="alert-row-kicker">{`${humanizeToken(event.category || "ops")} • ${event.entity_label || event.entity_key || event.airport_code || airportCode}`}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Affected area</div>
                                  <div>{event.entity_label || event.entity_key || event.airport_code || airportCode}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Reason</div>
                                  <div>{event.title || formatDt(event.observed_at_utc)}</div>
                                </div>
                                <span className={pillClass(event.severity_after)}>{String(event.event_type || "event").toUpperCase()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="alert-row-empty">No older airport ops alert events.</div>
                      )}
                    </details>
                  </details>

                  <details className="detail-collapsible">
                    <summary>{`Weather alerts (${weatherAlertSummary.last_evaluated_at ? activeWeatherAlertsSorted.length : "N/A"})`}</summary>
                    {activeWeatherAlertsSorted.length ? (
                      <div className="alert-row-list">
                        <div className="alert-grid-head">
                          <span>Time-start</span>
                          <span>System</span>
                          <span>Specific</span>
                          <span>Description</span>
                          <span>Reason</span>
                          <span>Cause</span>
                          <span>Severity</span>
                        </div>
                        {activeWeatherAlertsSorted.map((item) => {
                          const linkedManual = manualLinksByTarget.get(`weather:${item.target_key}`) || [];
                          return (
                          <AlertGridRow
                            key={item.target_key}
                            openedAt={item.opened_at}
                            system="Weather"
                            specific={item.airport_code || airportCode}
                            description="Observed weather condition above threshold"
                            reason={weatherAlertExactReason(item)}
                            cause={weatherCauseText(item, linkedManual[0])}
                            severity={item.current_severity}
                            footer={[
                              weatherAlertMetricText(item),
                              `Last checked ${formatDt(item.last_observed_at_utc)}`,
                              `Next check approx ${formatDt(alertNextCheckAt(item, weatherAlertSummary.last_evaluated_at))}`,
                            ]}
                          />
                        )})}
                      </div>
                    ) : (
                      <div className="alert-row-empty">{weatherAlertError || (weatherAlertSummary.last_evaluated_at ? "No open weather alerts." : "Weather alert feed unavailable.")}</div>
                    )}
                    <details className="detail-collapsible" style={{ marginTop: 8 }}>
                      <summary>{`View older alerts / events (${weatherAlertEvents.length})`}</summary>
                      {weatherAlertEvents.length ? (
                        <div className="alert-row-list">
                          {weatherAlertEvents.map((event) => (
                            <div key={`weather-event-${event.event_id}`} className={`alert-row ${fmtLevel(event.severity_after)}`}>
                              <div className="alert-row-main">
                                <div>
                                  <div className="alert-row-title">{event.title || event.display_name}</div>
                                  <div className="alert-row-kicker">{`Weather • ${event.airport_code || airportCode}`}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Affected area</div>
                                  <div>{event.airport_code || airportCode}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Reason</div>
                                  <div>{event.title || formatDt(event.observed_at_utc)}</div>
                                </div>
                                <span className={pillClass(event.severity_after)}>{String(event.event_type || "event").toUpperCase()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="alert-row-empty">No older weather alert events.</div>
                      )}
                    </details>
                  </details>

                  <details className="detail-collapsible">
                    <summary>{`Manual / external alerts (${activeManualAlertsSorted.length})`}</summary>
                    <form onSubmit={submitManualAlert} style={{ display: "grid", gap: 8, marginBottom: 10 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr 0.8fr", gap: 8 }}>
                        <input className="input" placeholder="Title e.g. Fuel shortage" value={manualAlertForm.title} onChange={(e) => setManualAlertForm((p) => ({ ...p, title: e.target.value }))} />
                        <select className="input" value={manualAlertForm.category} onChange={(e) => setManualAlertForm((p) => ({ ...p, category: e.target.value }))}>
                          <option value="external">External</option>
                          <option value="airport_incident">Airport incident</option>
                          <option value="terminal_issue">Terminal issue</option>
                          <option value="security_issue">Security issue</option>
                          <option value="fuel">Fuel</option>
                          <option value="airspace">Airspace</option>
                          <option value="other">Other</option>
                        </select>
                        <select className="input" value={manualAlertForm.severity} onChange={(e) => setManualAlertForm((p) => ({ ...p, severity: e.target.value }))}>
                          <option value="light_green">Light green</option>
                          <option value="amber">Amber</option>
                          <option value="red">Red</option>
                        </select>
                      </div>
                      <textarea className="input" rows={2} placeholder="Reason / exact description" value={manualAlertForm.reason_text} onChange={(e) => setManualAlertForm((p) => ({ ...p, reason_text: e.target.value }))} />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 0.9fr 1.1fr 0.9fr", gap: 8 }}>
                        <input className="input" placeholder="Source URL (optional)" value={manualAlertForm.source_url} onChange={(e) => setManualAlertForm((p) => ({ ...p, source_url: e.target.value }))} />
                        <select className="input" value={manualAlertForm.linked_system} onChange={(e) => setManualAlertForm((p) => ({ ...p, linked_system: e.target.value }))}>
                          <option value="airport_ops">Airport ops</option>
                          <option value="rail">Rail</option>
                          <option value="road">Road</option>
                          <option value="nh">NH</option>
                          <option value="weather">Weather</option>
                        </select>
                        <input className="input" placeholder="Linked target key (optional)" value={manualAlertForm.linked_target_key} onChange={(e) => setManualAlertForm((p) => ({ ...p, linked_target_key: e.target.value }))} />
                        <select className="input" value={manualAlertForm.link_type} onChange={(e) => setManualAlertForm((p) => ({ ...p, link_type: e.target.value }))}>
                          <option value="possible_cause">Possible cause</option>
                          <option value="confirmed_cause">Confirmed cause</option>
                          <option value="related">Related</option>
                        </select>
                      </div>
                      <div>
                        <button className="btn tiny" type="submit" disabled={manualActionBusy}>{manualActionBusy ? "Saving..." : "Add manual alert"}</button>
                      </div>
                    </form>
                    {manualAlertError ? <div className="muted" style={{ marginBottom: 8 }}>{manualAlertError}</div> : null}
                    {activeManualAlertsSorted.length ? (
                      <div className="alert-row-list">
                        <div className="alert-grid-head">
                          <span>Time-start</span>
                          <span>System</span>
                          <span>Specific</span>
                          <span>Description</span>
                          <span>Reason</span>
                          <span>Cause</span>
                          <span>Severity</span>
                        </div>
                        {activeManualAlertsSorted.map((item) => (
                          <div key={item.manual_alert_id} style={{ display: "grid", gap: 8 }}>
                            <AlertGridRow
                              openedAt={item.opened_at}
                              system={humanizeToken(item.category || "manual")}
                              specific={item.airport_code || "Global"}
                              description="Manual or external factor added by operator"
                              reason={manualAlertReasonText(item)}
                              cause={manualCauseText(item)}
                              severity={item.severity}
                              footer={[
                                manualAlertLinksText(item),
                                item.source_url || null,
                                `Updated ${formatDt(item.updated_at || item.last_updated_at || item.opened_at)}`,
                              ]}
                            />
                            <div className="alert-row-meta" style={{ paddingLeft: 12 }}>
                              <button className="btn tiny" type="button" disabled={manualActionBusy} onClick={() => resolveManualAlertAction(item.manual_alert_id)}>Resolve</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="alert-row-empty">No open manual or external alerts.</div>
                    )}
                    <details className="detail-collapsible" style={{ marginTop: 8 }}>
                      <summary>{`View older alerts / events (${manualAlertEvents.length})`}</summary>
                      {manualAlertEvents.length ? (
                        <div className="alert-row-list">
                          {manualAlertEvents.map((event, idx) => (
                            <div key={`manual-event-${event.event_id || event.manual_alert_id || idx}`} className={`alert-row ${fmtLevel(event.severity_after || event.severity)}`}>
                              <div className="alert-row-main">
                                <div>
                                  <div className="alert-row-title">{event.title || event.display_name || event.reason_text || "Manual alert event"}</div>
                                  <div className="alert-row-kicker">{`${humanizeToken(event.category || "manual")} • ${event.airport_code || "Global"}`}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Affected area</div>
                                  <div>{event.airport_code || "Global / network-wide"}</div>
                                </div>
                                <div>
                                  <div className="alert-row-kicker">Reason</div>
                                  <div>{event.reason_text || event.title || formatDt(event.observed_at_utc || event.updated_at || event.resolved_at || event.opened_at)}</div>
                                </div>
                                <span className={pillClass(event.severity_after || event.severity)}>{String(event.event_type || event.status || "event").toUpperCase()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="alert-row-empty">No older manual or external alert events.</div>
                      )}
                    </details>
                  </details>
                </div>
              </div>
            </div>
          </details>

          <details className="collapsible" open={roadOpen} onToggle={(e) => setRoadOpen(e.currentTarget.open)}>
            <summary>
              <span className="collapsible-summary-row">
                <span>Motorway Corridors {airportCode === "LHR" ? "(M25 / M4)" : "(M1)"}</span>
                <span className="collapsible-summary-meta">
                  {roadCorridorSummary.count || 0} live • Worst: {roadCorridorSummary.worst ? `${roadCorridorSummary.worst.corridor_key} ${String(roadCorridorSummary.worst.status_label || "unknown").toUpperCase()}` : "N/A"}
                </span>
              </span>
            </summary>
            <div className="table-card" style={{ marginBottom: 16 }}>
              {roadLoading ? (
                <div className="muted">Loading motorway corridor status...</div>
              ) : roadError ? (
                <div className="muted">{roadError}</div>
              ) : roadCorridors.length ? (
                <div className="corridor-grid">
                  {roadCorridors.map((corridor) => {
                    const detail = roadDetailByKey[corridor.corridor_key];
                    const segments = Array.isArray(detail?.segments) ? detail.segments : [];
                    const endpoints = segmentEndpointsFromRows(segments);
                    return (
                      <div key={corridor.corridor_key} className="corridor-card">
                        <div className="corridor-head">
                          <div>
                            <div className="corridor-title">{corridor.display_name}</div>
                            <div className="muted">
                              {endpoints.start} → {endpoints.end} • {String(corridor.direction || "unknown").toUpperCase()}
                            </div>
                          </div>
                          <span className={pillClass(corridor.status_label)}>{String(corridor.status_label || "unknown").toUpperCase()}</span>
                        </div>
                        <div className="corridor-metrics">
                          <div><strong>Current:</strong> {formatDurationMins(corridor.route_duration_live_sec)}</div>
                          <div><strong>Baseline:</strong> {formatDurationMins(corridor.route_duration_static_sec)}</div>
                          <div><strong>Delta:</strong> {formatDeltaMins(corridor.route_delta_sec)}</div>
                          <div><strong>Distance:</strong> {formatMiles(corridor.route_distance_m)}</div>
                        </div>
                        {segments.length ? (
                          <div className="corridor-strip">
                            <div className="corridor-strip-head">
                              <span>{endpoints.start}</span>
                              <span>{endpoints.end}</span>
                            </div>
                            <div className="corridor-segments">
                              {segments.map((segment, index) => (
                                <React.Fragment key={segment.id || `${corridor.corridor_key}-${segment.segment_key}`}>
                                  <div className={`corridor-segment ${fmtLevel(segment.status_label)}`}>
                                    <div className="corridor-segment-top">
                                      <div className="corridor-segment-label">{segment.display_label}</div>
                                      <span className={pillClass(segment.status_label)}>{String(segment.status_label || "unknown").toUpperCase()}</span>
                                    </div>
                                    <div className="corridor-segment-meta">{formatDurationMins(segment.duration_live_sec)} • {formatMiles(segment.distance_m)}</div>
                                    <div className="corridor-segment-delta">{formatDeltaMins(segment.delta_sec)}</div>
                                  </div>
                                  {index < segments.length - 1 && <div className="corridor-segment-line" aria-hidden="true" />}
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="muted">Segment detail unavailable.</div>
                        )}
                        <div className="muted">
                          Updated: {formatDt(corridor.observed_at_utc)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="muted">No motorway corridor status available for {airportCode}.</div>
              )}
            </div>
          </details>

          <details className="collapsible" open={railOpen} onToggle={(e) => setRailOpen(e.currentTarget.open)}>
            <summary>
              <span className="collapsible-summary-row">
                <span>Rail Corridors {airportCode === "LHR" ? "(Elizabeth / Piccadilly / Heathrow rail)" : "(EMA station / gateways)"}</span>
                <span className="collapsible-summary-meta">
                  {railCorridorSummary.count || 0} live • Worst: {railCorridorSummary.worst ? `${railCorridorSummary.worst.corridor_key} ${String(railCorridorSummary.worst.status_label || "unknown").toUpperCase()}` : "N/A"}
                </span>
              </span>
          </summary>
          <div className="table-card" style={{ marginBottom: 16 }}>
            {railLoading ? (
              <div className="muted">Loading rail status...</div>
              ) : railError ? (
                <div className="muted">{railError}</div>
              ) : railAirportBoards ? (
                <div className="corridor-grid">
                  {(railAirportBoards.station_boards || []).map((station) => {
                    const code = station?.target?.crs || station?.target?.target_key;
                    const isCallingBoard = code === "HXX";
                    const stationLabel = stationDisplayWithCode(station?.target?.display_name, code);
                    return (
                      <div key={station?.target?.target_key} className="corridor-card">
                        <div className="corridor-head">
                            <div>
                              <div className="corridor-title">{station?.target?.display_name}</div>
                              <div className="muted">
                              {isCallingBoard ? "Calls at Heathrow T2/T3" : `${code} arrivals / departures`}
                              </div>
                            </div>
                          <span className={pillClass(station?.board?.status_label || station?.target?.effective_status_label)}>
                            {String(station?.board?.status_label || station?.target?.effective_status_label || "unknown").toUpperCase()}
                          </span>
                        </div>
                        <div className="corridor-metrics">
                          <div><strong>Services:</strong> {station?.board?.service_count || 0}</div>
                          <div><strong>Platform data:</strong> {station?.target?.board_mode ? "Live" : "N/A"}</div>
                          <div><strong>Updated:</strong> {formatDt(station?.board?.observed_at_utc || station?.target?.effective_observed_at_utc)}</div>
                          <div><strong>Status:</strong> {station?.reason_text || "N/A"}</div>
                        </div>
                        {isCallingBoard ? (
                          <div className="rail-member-list">
                            <div className="rail-member">
                              <div className="rail-member-head">
                                <div>
                                  <div className="rail-member-title">Next trains calling at T2/T3</div>
                                  <div className="rail-member-meta">Scheduled calls at {stationLabel}</div>
                                </div>
                              </div>
                              <div className="rail-service-list">
                                {(station?.relevant_services || []).slice(0, 5).map((service) => (
                                  <div key={service.id} className="rail-service-item">
                                    {service.arrival_scheduled_local || "Live"} arr / {service.departure_scheduled_local || "Live"} dep • Plat {service.platform || "?"} • {service.origin_name || service.origin_crs || "?"} → {service.destination_name || service.destination_crs || "?"} • {service.operator_name || "Rail"} • {service.status_text || service.status_label || "unknown"}
                                    {service.context_summary ? ` • ${service.context_summary}` : ""}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="rail-member-list">
                            <div className="rail-member">
                              <div className="rail-member-head">
                                <div>
                                  <div className="rail-member-title">Arrivals</div>
                                  <div className="rail-member-meta">Next trains into {stationLabel}</div>
                                </div>
                              </div>
                              <div className="rail-service-list">
                                {(station?.arrivals || []).slice(0, 5).map((service) => (
                                  <div key={service.id} className="rail-service-item">
                                    {railBoardScheduledTime(service, "arrival") || "Live"} • {service.origin_name || service.origin_crs || "?"} • Plat {service.platform || "?"} • {railBoardDisplayTime(service, "arrival")}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="rail-member">
                              <div className="rail-member-head">
                                <div>
                                  <div className="rail-member-title">Departures</div>
                                  <div className="rail-member-meta">Next trains leaving {stationLabel}</div>
                                </div>
                              </div>
                              <div className="rail-service-list">
                                {(station?.departures || []).slice(0, 5).map((service) => (
                                  <div key={service.id} className="rail-service-item">
                                    {railBoardScheduledTime(service, "departure") || "Live"} • {service.destination_name || service.destination_crs || "?"} • Plat {service.platform || "?"} • {railBoardDisplayTime(service, "departure")}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {(railAirportBoards.gateway_boards || []).map((gateway) => (
                    <div key={gateway?.target?.target_key} className="corridor-card">
                      <div className="corridor-head">
                        <div>
                          <div className="corridor-title">{gateway?.target?.display_name}</div>
                          <div className="muted">
                            {String(gateway?.target?.target_key || "") === "stp_board"
                              ? "Status-only gateway context"
                              : `${airportCode === "LHR" ? "Heathrow" : "EMA"}-related gateway board`}
                          </div>
                        </div>
                        <span className={pillClass(gateway?.board?.status_label || gateway?.target?.effective_status_label)}>
                          {String(gateway?.board?.status_label || gateway?.target?.effective_status_label || "unknown").toUpperCase()}
                        </span>
                      </div>
                      <div className="corridor-metrics">
                        <div><strong>Relevant:</strong> {gateway?.board?.service_count || 0}</div>
                        <div><strong>Updated:</strong> {formatDt(gateway?.board?.observed_at_utc || gateway?.target?.effective_observed_at_utc)}</div>
                        <div><strong>Status:</strong> {gateway?.reason_text || "N/A"}</div>
                        <div><strong>Code:</strong> {gateway?.target?.crs || "N/A"}</div>
                      </div>
                      {String(gateway?.target?.target_key || "") === "stp_board" ? (
                        <div className="muted">
                          {airportCode === "LHR"
                            ? "No direct Heathrow National Rail board services. Use Piccadilly line status and Heathrow Underground live arrivals below."
                            : "No direct EMA airport station board services from St Pancras. Use this as a National Rail gateway context."}
                        </div>
                      ) : (
                        <div className="rail-member-list">
                          <div className="rail-member">
                            <div className="rail-member-head">
                              <div>
                                <div className="rail-member-title">{airportCode === "LHR" ? "Heathrow-related arrivals" : "EMA-related arrivals"}</div>
                                <div className="rail-member-meta">Services arriving into {gateway?.target?.display_name}</div>
                              </div>
                            </div>
                            <div className="rail-service-list">
                              {(gateway?.arrivals || []).slice(0, 5).map((service) => (
                                <div key={service.id} className="rail-service-item">
                                  {railBoardScheduledTime(service, "arrival") || "Live"} • {service.origin_name || service.origin_crs || "?"} • Plat {service.platform || "?"} • {railBoardDisplayTime(service, "arrival")}
                                </div>
                              ))}
                              {!gateway?.arrivals?.length ? (
                                <div className="rail-service-item">
                                  No {airportCode === "LHR" ? "Heathrow" : "EMA"}-linked arrivals in latest board.
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div className="rail-member">
                            <div className="rail-member-head">
                              <div>
                                <div className="rail-member-title">{airportCode === "LHR" ? "Heathrow-related departures" : "EMA-related departures"}</div>
                                <div className="rail-member-meta">Services toward {airportCode === "LHR" ? "Heathrow" : "EMA"} from {gateway?.target?.display_name}</div>
                              </div>
                            </div>
                            <div className="rail-service-list">
                              {(gateway?.departures || []).slice(0, 5).map((service) => (
                                <div key={service.id} className="rail-service-item">
                                  {railBoardScheduledTime(service, "departure") || "Live"} • {service.destination_name || service.destination_crs || "?"} • Plat {service.platform || "?"} • {railBoardDisplayTime(service, "departure")}
                                </div>
                              ))}
                              {!gateway?.departures?.length ? (
                                <div className="rail-service-item">
                                  No {airportCode === "LHR" ? "Heathrow" : "EMA"}-linked departures in latest board.
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {airportCode === "LHR" && (railAirportBoards.tfl_station_boards || []).map((board) => (
                    <div key={board?.stop_point_id || board?.target?.display_name || board?.target?.target_key} className="corridor-card">
                      <div className="corridor-head">
                        <div>
                          <div className="corridor-title">{board?.target?.display_name || board?.station_label || "Heathrow Underground"}</div>
                          <div className="muted">Live Piccadilly trains at {board?.station_label || board?.target?.display_name || "Heathrow Underground"}</div>
                        </div>
                        <span className={pillClass(board?.board?.status_label || board?.target?.effective_status_label)}>
                          {String(board?.board?.status_label || board?.target?.effective_status_label || "unknown").toUpperCase()}
                        </span>
                      </div>
                      <div className="corridor-metrics">
                        <div><strong>Trains:</strong> {board?.board?.service_count || 0}</div>
                        <div><strong>Updated:</strong> {formatDt(board?.board?.observed_at_utc || board?.target?.effective_observed_at_utc)}</div>
                        <div><strong>Status:</strong> {board?.reason_text || "N/A"}</div>
                        <div><strong>Code:</strong> {board?.target?.atco_code || board?.target?.crs || "ZHJ"}</div>
                      </div>
                      {railReasonDetail(board) ? <div className="muted">{railReasonDetail(board)}</div> : null}
                      <div className="rail-member-list">
                        <div className="rail-member">
                          <div className="rail-member-head">
                            <div>
                              <div className="rail-member-title">Next Piccadilly trains</div>
                              <div className="rail-member-meta">Expected trains at {board?.station_label || board?.target?.display_name || "Heathrow Underground"}</div>
                            </div>
                          </div>
                          <div className="rail-service-list">
                            {(board?.services || []).slice(0, 6).map((service) => (
                              <div key={service.id} className="rail-service-item">
                                {service.expected_time_local || "Live"} • {service.destination_name || service.towards || "Piccadilly"} • Plat {service.platform || "?"}
                                {service.time_to_station_mins != null ? ` • ${service.time_to_station_mins}m` : ""}
                                {service.current_location ? ` • ${service.current_location}` : ""}
                              </div>
                            ))}
                            {!board?.services?.length ? (
                              <div className="rail-service-item">No live Piccadilly arrivals returned by TfL for this stop right now.</div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {(railAirportBoards.line_statuses || []).map((status) => (
                    <div key={status?.target?.target_key} className="corridor-card">
                      <div className="corridor-head">
                        <div>
                          <div className="corridor-title">{status?.target?.display_name}</div>
                          <div className="muted">Line status</div>
                        </div>
                        <span className={pillClass(status?.target?.effective_status_label)}>
                          {String(status?.target?.effective_status_label || "unknown").toUpperCase()}
                        </span>
                      </div>
                      <div className="corridor-metrics">
                        <div><strong>Status:</strong> {status?.reason_text || "N/A"}</div>
                        <div><strong>Updated:</strong> {formatDt(status?.target?.effective_observed_at_utc)}</div>
                      </div>
                      {railReasonDetail(status) ? <div className="muted">{railReasonDetail(status)}</div> : null}
                    </div>
                  ))}

                  {(railAirportBoards.operator_statuses || []).map((operator) => (
                    <div key={operator.operator_name} className="corridor-card">
                      <div className="corridor-head">
                        <div>
                          <div className="corridor-title">{operator.operator_name}</div>
                          <div className="muted">Operator status</div>
                        </div>
                        <span className={pillClass(operator.status_label)}>
                          {String(operator.status_label || "unknown").toUpperCase()}
                        </span>
                      </div>
                      <div className="corridor-metrics">
                        <div><strong>Services:</strong> {operator.service_count || 0}</div>
                        <div><strong>Status:</strong> {operator.reason_text || "N/A"}</div>
                        <div><strong>Airport:</strong> {airportCode === "LHR" ? "Heathrow" : "EMA"}</div>
                      </div>
                      <div className="rail-service-list">
                        {(operator.services || []).slice(0, 5).map((service) => (
                          <div key={service.id} className="rail-service-item">
                            {service.departure_scheduled_local || service.arrival_scheduled_local || "Live"} • {service.origin_name || service.origin_crs || "?"} → {service.destination_name || service.destination_crs || "?"} • Plat {service.platform || "?"} • {service.status_text || service.status_label || "unknown"}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : railCorridors.length ? (
                <div className="corridor-grid">
                  {railCorridors.map((corridor) => {
                    const detail = railDetailByKey[corridor.corridor_key];
                    const members = Array.isArray(detail?.members) ? detail.members : [];
                    const reasonText = corridor?.summary?.reason_text || detail?.worst_member?.reason_text || null;
                    return (
                      <div key={corridor.corridor_key} className="corridor-card">
                        <div className="corridor-head">
                          <div>
                            <div className="corridor-title">{corridor.display_name}</div>
                            <div className="muted">
                              {corridor.origin_label || "Origin"} → {corridor.destination_label || "Destination"} • {String(corridor.corridor_mode || "mixed").toUpperCase()}
                            </div>
                          </div>
                          <span className={pillClass(corridor.status_label)}>{String(corridor.status_label || "unknown").toUpperCase()}</span>
                        </div>
                        <div className="corridor-metrics">
                          <div><strong>Delayed:</strong> {corridor.delayed_services_count || 0}</div>
                          <div><strong>Cancelled:</strong> {corridor.cancelled_services_count || 0}</div>
                          <div><strong>Worst target:</strong> {corridor.worst_target_name || "N/A"}</div>
                          <div><strong>Updated:</strong> {formatDt(corridor.observed_at_utc)}</div>
                        </div>
                        {reasonText ? <div className="corridor-segment-delta">{reasonText}</div> : null}
                        {members.length ? (
                          <div className="rail-member-list">
                            {members.map((member) => (
                              <div key={`${corridor.corridor_key}-${member.sequence_no}-${member.target_key}`} className={`rail-member ${fmtLevel(member.effective_status_label)}`}>
                                <div className="rail-member-head">
                                  <div>
                                    <div className="rail-member-title">{member.member_label || member.display_name}</div>
                                    <div className="rail-member-meta">
                                      {String(member.role || "member").replaceAll("_", " ").toUpperCase()} • {member.display_name}
                                    </div>
                                  </div>
                                  <span className={pillClass(member.effective_status_label)}>{String(member.effective_status_label || "unknown").toUpperCase()}</span>
                                </div>
                                {member.reason_text ? <div className="rail-member-reason">{member.reason_text}</div> : null}
                                {Array.isArray(member.service_samples) && member.service_samples.length ? (
                                  <div className="rail-service-list">
                                    {member.service_samples.slice(0, 3).map((service) => (
                                      <div key={service.id} className="rail-service-item">
                                        {service.std_local || service.expected_time_local || "Live"} • {service.origin_crs || service.origin_name || "?"} → {service.destination_crs || service.destination_name || "?"} • {service.status_text || service.status_label || "unknown"}
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="muted">Rail member detail unavailable.</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="muted">No rail corridor status available for {airportCode}.</div>
              )}
            </div>
          </details>

          {airportCode === "LHR" && (railAirportBoards?.tfl_journey_paths || []).length ? (
            <details className="collapsible">
              <summary>
                <span className="collapsible-summary-row">
                  <span>TfL / HEX / ELZ Journey Timings</span>
                  <span className="collapsible-summary-meta">
                    {(railAirportBoards.tfl_journey_paths || []).length} monitored • Latest {String(railAirportBoards.tfl_journey_paths?.[0]?.status_label || "unknown").toUpperCase()}
                  </span>
                </span>
              </summary>
              <div className="table-card" style={{ marginBottom: 16 }}>
                <div className="corridor-grid">
                  {(railAirportBoards.tfl_journey_paths || []).map((path) => (
                    <div key={path?.target?.target_key} className="corridor-card">
                      <div className="corridor-head">
                        <div>
                          <div className="corridor-title">{path?.target?.display_name || "TfL journey snapshot"}</div>
                          <div className="muted">
                            {path?.query?.from_label || "Origin"} → {path?.query?.to_label || "Heathrow"} • {path?.selected_journey?.operator_family ? String(path.selected_journey.operator_family).replaceAll("_", " ") : "selected route"}
                          </div>
                        </div>
                        <span className={pillClass(path?.status_label || path?.target?.effective_status_label)}>
                          {String(path?.status_label || path?.target?.effective_status_label || "unknown").toUpperCase()}
                        </span>
                      </div>
                      <div className="corridor-metrics">
                        <div><strong>Total:</strong> {path?.selected_journey?.total_duration_mins != null ? `${path.selected_journey.total_duration_mins}m` : "N/A"}</div>
                        <div><strong>Walk:</strong> {path?.selected_journey?.walking_mins != null ? `${path.selected_journey.walking_mins}m` : "0m"}</div>
                        <div><strong>Interchanges:</strong> {path?.selected_journey?.interchanges != null ? path.selected_journey.interchanges : 0}</div>
                        <div><strong>Updated:</strong> {formatDt(path?.query?.observed_at_utc || path?.target?.effective_observed_at_utc)}</div>
                      </div>
                      {path?.reason_text ? <div className="muted">{path.reason_text}</div> : null}
                      <div className="rail-member-list">
                        <div className="rail-member">
                          <div className="rail-member-head">
                            <div>
                              <div className="rail-member-title">Selected journey legs</div>
                              <div className="rail-member-meta">Best preserved TfL route for this Heathrow path</div>
                            </div>
                          </div>
                          <div className="rail-service-list">
                            {(Array.isArray(path?.journeys) ? path.journeys.find((journey) => journey.is_selected) : null)?.legs?.slice(0, 6).map((leg) => (
                              <div key={`${path?.target?.target_key}-${leg.sequence_no}`} className="rail-service-item">
                                {leg.duration_mins != null ? `${leg.duration_mins}m` : "Live"} • {leg.line_name || leg.mode_name || "Leg"} • {leg.departure_label || "?"} → {leg.arrival_label || "?"}
                              </div>
                            ))}
                            {!((Array.isArray(path?.journeys) ? path.journeys.find((journey) => journey.is_selected) : null)?.legs || []).length ? (
                              <div className="rail-service-item">No normalized leg detail returned for this snapshot.</div>
                            ) : null}
                          </div>
                        </div>
                        <div className="rail-member">
                          <div className="rail-member-head">
                            <div>
                              <div className="rail-member-title">Alternatives captured</div>
                              <div className="rail-member-meta">Top TfL journey options preserved for later comparison</div>
                            </div>
                          </div>
                          <div className="rail-service-list">
                            {(path?.journeys || []).slice(0, 3).map((journey) => (
                              <div key={`${path?.target?.target_key}-journey-${journey.journey_index}`} className="rail-service-item">
                                {journey.is_selected ? "Selected" : `Alt ${journey.journey_index + 1}`} • {journey.total_duration_mins != null ? `${journey.total_duration_mins}m` : "N/A"} • {journey.summary_text || journey.operator_family || "TfL journey"}
                              </div>
                            ))}
                            {!path?.journeys?.length ? (
                              <div className="rail-service-item">No alternative journey options preserved in latest snapshot.</div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </details>
          ) : null}

          {airportCode === "EMA" ? (
            <details className="collapsible" open={flightOpsOpen} onToggle={(e) => setFlightOpsOpen(e.currentTarget.open)}>
              <summary>
                <span className="collapsible-summary-row">
                  <span>Flights (EMA)</span>
                  <span className="collapsible-summary-meta">
                    {flightOps?.summary?.arrivals?.status_label ? `Arrivals ${String(flightOps.summary.arrivals.status_label).toUpperCase()}` : "Arrivals N/A"} •{" "}
                    {flightOps?.summary?.departures?.status_label ? `Departures ${String(flightOps.summary.departures.status_label).toUpperCase()}` : "Departures N/A"}
                  </span>
                </span>
              </summary>
              <div className="table-card" style={{ marginBottom: 16 }}>
                {flightOpsLoading ? (
                  <div className="muted">Loading EMA flight snapshot...</div>
                ) : flightOpsError ? (
                  <div className="muted">{flightOpsError}</div>
                ) : flightOps ? (
                  <>
                    <div className="summary-grid" style={{ marginTop: 0 }}>
                      <div className="summary-card">
                        <div className="summary-label">Arrivals</div>
                        <div className="summary-value">{String(flightOps?.summary?.arrivals?.status_label || "unknown").toUpperCase()}</div>
                        <div className="muted">
                          On time: {flightOps?.summary?.arrivals?.on_time || 0} • Delayed: {flightOps?.summary?.arrivals?.delayed || 0} • Cancelled: {flightOps?.summary?.arrivals?.cancelled || 0}
                        </div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">Departures</div>
                        <div className="summary-value">{String(flightOps?.summary?.departures?.status_label || "unknown").toUpperCase()}</div>
                        <div className="muted">
                          On time: {flightOps?.summary?.departures?.on_time || 0} • Delayed: {flightOps?.summary?.departures?.delayed || 0} • Cancelled: {flightOps?.summary?.departures?.cancelled || 0}
                        </div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">Worst terminal</div>
                        <div className="summary-value">{flightOps?.summary?.worst_terminal?.terminal || "N/A"}</div>
                        <div className="muted">
                          {flightOps?.summary?.worst_terminal
                            ? `${String(flightOps.summary.worst_terminal.status_label || "unknown").toUpperCase()} • Delayed ${flightOps.summary.worst_terminal.delayed || 0} • Cancelled ${flightOps.summary.worst_terminal.cancelled || 0}`
                            : "No terminal issues"}
                        </div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">Updated</div>
                        <div className="summary-value">{flightOps?.updated_at ? formatHm(flightOps.updated_at) : "N/A"}</div>
                        <div className="muted">{formatDt(flightOps?.updated_at)}</div>
                      </div>
                    </div>
                    <div className="corridor-grid" style={{ marginTop: 12 }}>
                      <div className="corridor-card">
                        <div className="corridor-head">
                          <div>
                            <div className="corridor-title">Airport weather</div>
                            <div className="muted">Current, worst today, last 24h</div>
                          </div>
                          <span className={pillClass(flightOps?.weather?.status_label)}>
                            {String(flightOps?.weather?.status_label || "unknown").toUpperCase()}
                          </span>
                        </div>
                        <div className="rail-service-list">
                          <div className="rail-service-item">
                            Current • {weatherSummaryText(flightOps?.weather)}
                            {flightOps?.weather?.current?.observed_at ? ` • ${formatHm(flightOps.weather.current.observed_at)}` : ""}
                          </div>
                          <div className="rail-service-item">
                            Worst today • {flightOps?.weather?.worst_today?.weather_text || "N/A"}
                            {flightOps?.weather?.worst_today?.temp_c != null ? ` • ${flightOps.weather.worst_today.temp_c}C` : ""}
                            {flightOps?.weather?.worst_today?.wind_mps != null ? ` • wind ${flightOps.weather.worst_today.wind_mps}m/s` : ""}
                          </div>
                          <div className="rail-service-item">
                            Last 24h • {flightOps?.weather?.trend_24h?.min_temp_c != null ? `min ${flightOps.weather.trend_24h.min_temp_c}C` : "min N/A"}
                            {flightOps?.weather?.trend_24h?.max_temp_c != null ? ` • max ${flightOps.weather.trend_24h.max_temp_c}C` : ""}
                            {flightOps?.weather?.trend_24h?.peak_wind_mps != null ? ` • peak wind ${flightOps.weather.trend_24h.peak_wind_mps}m/s` : ""}
                          </div>
                          <div className="rail-service-item">
                            Severe flags • {(flightOps?.weather?.severe_flags || []).length ? flightOps.weather.severe_flags.join(" • ") : "None"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="corridor-grid">
                      <div className="corridor-card">
                        <div className="corridor-head">
                          <div>
                            <div className="corridor-title">Latest arrivals</div>
                            <div className="muted">Closest live arrivals at EMA right now</div>
                          </div>
                        </div>
                        <div className="rail-service-list">
                          {(flightOps?.latest_arrivals || []).map((flight) => (
                            <div key={flight.id} className="rail-service-item">
                              {formatHm(flight.anchor_utc || flight.scheduled_utc)} • {flight.flight_number} • {flight.airline_name || flight.airline_code || "Airline"} • {flight.origin_airport_code || flight.origin_airport_name || "Inbound"}
                              {flight.terminal ? ` • T${flight.terminal}` : ""}
                              {flight.baggage_carousel_number ? ` • Belt ${flight.baggage_carousel_number}` : ""}
                              {flight.status_text ? ` • ${flight.status_text}` : ""}
                              {flight.delay_mins != null && flight.delay_mins > 0 ? ` • +${flight.delay_mins}m` : ""}
                              {flightTimeSummary(flight) ? ` • ${flightTimeSummary(flight)}` : ""}
                            </div>
                          ))}
                          {!flightOps?.latest_arrivals?.length ? <div className="rail-service-item">No current EMA arrivals in snapshot.</div> : null}
                        </div>
                      </div>

                      <div className="corridor-card">
                        <div className="corridor-head">
                          <div>
                            <div className="corridor-title">Latest departures</div>
                            <div className="muted">Closest live departures from EMA right now</div>
                          </div>
                        </div>
                        <div className="rail-service-list">
                          {(flightOps?.latest_departures || []).map((flight) => (
                            <div key={flight.id} className="rail-service-item">
                              {formatHm(flight.anchor_utc || flight.scheduled_utc)} • {flight.flight_number} • {flight.airline_name || flight.airline_code || "Airline"} • {flight.destination_airport_code || flight.destination_airport_name || "Outbound"}
                              {flight.terminal ? ` • T${flight.terminal}` : ""}
                              {flight.gate_number ? ` • Gate ${flight.gate_number}` : ""}
                              {flight.status_text ? ` • ${flight.status_text}` : ""}
                              {flight.delay_mins != null && flight.delay_mins > 0 ? ` • +${flight.delay_mins}m` : ""}
                              {flightTimeSummary(flight) ? ` • ${flightTimeSummary(flight)}` : ""}
                            </div>
                          ))}
                          {!flightOps?.latest_departures?.length ? <div className="rail-service-item">No current EMA departures in snapshot.</div> : null}
                        </div>
                      </div>

                      <div className="corridor-card">
                        <div className="corridor-head">
                          <div>
                            <div className="corridor-title">Passenger flow outlook</div>
                            <div className="muted">Busiest scheduled hour blocks today</div>
                          </div>
                          <span className={pillClass(flightOps?.passenger_flow?.overall_status_label)}>
                            {String(flightOps?.passenger_flow?.overall_status_label || "unknown").toUpperCase()}
                          </span>
                        </div>
                        <div className="rail-member-list">
                          <div className="rail-member">
                            <div className="rail-member-head">
                              <div>
                                <div className="rail-member-title">Arrivals peak</div>
                                <div className="rail-member-meta">
                                  {flightOps?.passenger_flow?.arrivals?.block_label || "N/A"} • {flightOps?.passenger_flow?.arrivals?.severity_label || "Quiet"} • {flightOps?.passenger_flow?.arrivals?.flight_count || 0} flights
                                </div>
                              </div>
                              <span className={pillClass(flightOps?.passenger_flow?.arrivals?.status_label)}>
                                {String(flightOps?.passenger_flow?.arrivals?.status_label || "unknown").toUpperCase()}
                              </span>
                            </div>
                            <div className="muted">{flightOps?.passenger_flow?.arrival_note}</div>
                          </div>
                          <div className="rail-member">
                            <div className="rail-member-head">
                              <div>
                                <div className="rail-member-title">Departures peak</div>
                                <div className="rail-member-meta">
                                  {flightOps?.passenger_flow?.departures?.block_label || "N/A"} • {flightOps?.passenger_flow?.departures?.severity_label || "Quiet"} • {flightOps?.passenger_flow?.departures?.flight_count || 0} flights
                                </div>
                              </div>
                              <span className={pillClass(flightOps?.passenger_flow?.departures?.status_label)}>
                                {String(flightOps?.passenger_flow?.departures?.status_label || "unknown").toUpperCase()}
                              </span>
                            </div>
                            <div className="muted">{flightOps?.passenger_flow?.departure_note}</div>
                          </div>
                        </div>
                      </div>

                      {(flightOps?.insights || []).map((insight) => (
                        <div key={insight.title} className="corridor-card">
                          <div className="corridor-head">
                            <div>
                              <div className="corridor-title">{insight.title}</div>
                              <div className="muted">Operational insight</div>
                            </div>
                            <span className={pillClass(insight.status_label)}>{String(insight.status_label || "unknown").toUpperCase()}</span>
                          </div>
                          <div className="corridor-metrics">
                            <div><strong>Value:</strong> {insight.value}</div>
                          </div>
                          {insight.note ? <div className="muted">{insight.note}</div> : null}
                          {Array.isArray(insight.details) && insight.details.length ? (
                            <div className="rail-service-list">
                              {insight.details.map((detail) => (
                                <div key={`${insight.title}-${detail.flight_number}-${detail.delay_mins}`} className="rail-service-item">
                                  {detail.flight_number} • {detail.airline_name} • +{detail.delay_mins}m • {detail.status_text}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ))}

                    </div>

                    <div className="corridor-card" style={{ marginTop: 16 }}>
                      <div className="corridor-head">
                        <div>
                          <div className="corridor-title">Airline mix</div>
                          <div className="muted">Top airlines by unique flight number count, split by arrivals and departures</div>
                        </div>
                      </div>
                      <div className="airline-mix-grid">
                        {Object.entries(flightOps?.airline_mix || {}).map(([key, bucket]) => (
                          <div key={key} className="rail-member">
                            <div className="rail-member-head">
                              <div>
                                <div className="rail-member-title">{bucket?.title || key}</div>
                                <div className="rail-member-meta">{bucket?.source || "Snapshot"}</div>
                              </div>
                            </div>
                            <div className="airline-mix-split">
                              <div className="rail-member">
                                <div className="rail-member-head">
                                  <div>
                                    <div className="rail-member-title">Arrivals</div>
                                  </div>
                                </div>
                                <div className="rail-service-list">
                                  {(bucket?.arrivals || []).map((entry) => (
                                    <div key={`${key}-arr-${entry.airline_name}`} className="rail-service-item">
                                      {entry.airline_name} • {entry.flight_count}
                                    </div>
                                  ))}
                                  {!bucket?.arrivals?.length ? <div className="rail-service-item">No arrival mix available.</div> : null}
                                </div>
                              </div>
                              <div className="rail-member">
                                <div className="rail-member-head">
                                  <div>
                                    <div className="rail-member-title">Departures</div>
                                  </div>
                                </div>
                                <div className="rail-service-list">
                                  {(bucket?.departures || []).map((entry) => (
                                    <div key={`${key}-dep-${entry.airline_name}`} className="rail-service-item">
                                      {entry.airline_name} • {entry.flight_count}
                                    </div>
                                  ))}
                                  {!bucket?.departures?.length ? <div className="rail-service-item">No departure mix available.</div> : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="muted">No EMA flight snapshot available.</div>
                )}
              </div>
            </details>
          ) : null}

          {airportCode === "LHR" ? (
            <details className="collapsible" open={flightOpsOpen} onToggle={(e) => setFlightOpsOpen(e.currentTarget.open)}>
              <summary>
                <span className="collapsible-summary-row">
                  <span>Flights (LHR)</span>
                  <span className="collapsible-summary-meta">
                    {flightOps?.summary?.arrivals?.status_label ? `Arrivals ${String(flightOps.summary.arrivals.status_label).toUpperCase()}` : "Arrivals N/A"} •{" "}
                    {flightOps?.summary?.departures?.status_label ? `Departures ${String(flightOps.summary.departures.status_label).toUpperCase()}` : "Departures N/A"}
                  </span>
                </span>
              </summary>
              <div className="table-card" style={{ marginBottom: 16 }}>
                {flightOpsLoading ? (
                  <div className="muted">Loading Heathrow flight snapshot...</div>
                ) : flightOpsError ? (
                  <div className="muted">{flightOpsError}</div>
                ) : flightOps ? (
                  <>
                    <div className="summary-grid" style={{ marginTop: 0 }}>
                      <div className="summary-card">
                        <div className="summary-label">Arrivals</div>
                        <div className="summary-value">{String(flightOps?.summary?.arrivals?.status_label || "unknown").toUpperCase()}</div>
                        <div className="muted">
                          On time: {flightOps?.summary?.arrivals?.on_time || 0} • Delayed: {flightOps?.summary?.arrivals?.delayed || 0} • Cancelled: {flightOps?.summary?.arrivals?.cancelled || 0}
                        </div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">Departures</div>
                        <div className="summary-value">{String(flightOps?.summary?.departures?.status_label || "unknown").toUpperCase()}</div>
                        <div className="muted">
                          On time: {flightOps?.summary?.departures?.on_time || 0} • Delayed: {flightOps?.summary?.departures?.delayed || 0} • Cancelled: {flightOps?.summary?.departures?.cancelled || 0}
                        </div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">Terminals live</div>
                        <div className="summary-value">{(flightOps?.terminals || []).length}</div>
                        <div className="muted">T2 / T3 / T4 / T5 snapshot</div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">Updated</div>
                        <div className="summary-value">{flightOps?.updated_at ? formatHm(flightOps.updated_at) : "N/A"}</div>
                        <div className="muted">{formatDt(flightOps?.updated_at)}</div>
                      </div>
                    </div>
                    <div className="corridor-grid" style={{ marginTop: 12 }}>
                      <div className="corridor-card">
                        <div className="corridor-head">
                          <div>
                            <div className="corridor-title">Airport weather</div>
                            <div className="muted">Current, worst today, last 24h</div>
                          </div>
                          <span className={pillClass(flightOps?.weather?.status_label)}>
                            {String(flightOps?.weather?.status_label || "unknown").toUpperCase()}
                          </span>
                        </div>
                        <div className="rail-service-list">
                          <div className="rail-service-item">
                            Current • {weatherSummaryText(flightOps?.weather)}
                            {flightOps?.weather?.current?.observed_at ? ` • ${formatHm(flightOps.weather.current.observed_at)}` : ""}
                          </div>
                          <div className="rail-service-item">
                            Worst today • {flightOps?.weather?.worst_today?.weather_text || "N/A"}
                            {flightOps?.weather?.worst_today?.temp_c != null ? ` • ${flightOps.weather.worst_today.temp_c}C` : ""}
                            {flightOps?.weather?.worst_today?.wind_mps != null ? ` • wind ${flightOps.weather.worst_today.wind_mps}m/s` : ""}
                          </div>
                          <div className="rail-service-item">
                            Last 24h • {flightOps?.weather?.trend_24h?.min_temp_c != null ? `min ${flightOps.weather.trend_24h.min_temp_c}C` : "min N/A"}
                            {flightOps?.weather?.trend_24h?.max_temp_c != null ? ` • max ${flightOps.weather.trend_24h.max_temp_c}C` : ""}
                            {flightOps?.weather?.trend_24h?.peak_wind_mps != null ? ` • peak wind ${flightOps.weather.trend_24h.peak_wind_mps}m/s` : ""}
                          </div>
                          <div className="rail-service-item">
                            Severe flags • {(flightOps?.weather?.severe_flags || []).length ? flightOps.weather.severe_flags.join(" • ") : "None"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="corridor-grid">
                      <div className="corridor-card">
                        <div className="corridor-head">
                          <div>
                            <div className="corridor-title">Airport snapshot</div>
                            <div className="muted">Today and next 60 minutes</div>
                          </div>
                        </div>
                        <div className="rail-member-list">
                          <div className="rail-member">
                            <div className="rail-member-head">
                              <div>
                                <div className="rail-member-title">Top arrival delays</div>
                                <div className="rail-member-meta">Worst delayed arrivals today</div>
                              </div>
                            </div>
                            <div className="rail-service-list">
                              {(flightOps?.airport_insights?.top_arrival_delays || []).map((item) => (
                                <div key={`lhr-arr-delay-${item.flight_number}-${item.terminal}`} className="rail-service-item">
                                  T{item.terminal || "?"} • {item.flight_number} • {item.airline_name || "Airline"} • {item.route_label} • +{item.delay_mins}m
                                </div>
                              ))}
                              {!flightOps?.airport_insights?.top_arrival_delays?.length ? <div className="rail-service-item">No material arrival delays right now.</div> : null}
                            </div>
                          </div>
                          <div className="rail-member">
                            <div className="rail-member-head">
                              <div>
                                <div className="rail-member-title">Top departure delays</div>
                                <div className="rail-member-meta">Worst delayed departures today</div>
                              </div>
                            </div>
                            <div className="rail-service-list">
                              {(flightOps?.airport_insights?.top_departure_delays || []).map((item) => (
                                <div key={`lhr-dep-delay-${item.flight_number}-${item.terminal}`} className="rail-service-item">
                                  T{item.terminal || "?"} • {item.flight_number} • {item.airline_name || "Airline"} • {item.route_label} • +{item.delay_mins}m
                                </div>
                              ))}
                              {!flightOps?.airport_insights?.top_departure_delays?.length ? <div className="rail-service-item">No material departure delays right now.</div> : null}
                            </div>
                          </div>
                          <div className="rail-member">
                            <div className="rail-member-head">
                              <div>
                                <div className="rail-member-title">Cancellations</div>
                                <div className="rail-member-meta">
                                  Last 60m: A {flightOps?.airport_insights?.last_hour?.arrivals?.flights || 0} • D {flightOps?.airport_insights?.last_hour?.departures?.flights || 0}
                                </div>
                              </div>
                            </div>
                            <div className="rail-service-list">
                              {(flightOps?.airport_insights?.arrival_cancellations || []).map((item) => (
                                <div key={`lhr-arr-cancel-${item.flight_number}-${item.terminal}`} className="rail-service-item">
                                  Arrival • T{item.terminal || "?"} • {item.flight_number} • {item.route_label} • {item.reason}
                                </div>
                              ))}
                              {(flightOps?.airport_insights?.departure_cancellations || []).map((item) => (
                                <div key={`lhr-dep-cancel-${item.flight_number}-${item.terminal}`} className="rail-service-item">
                                  Departure • T{item.terminal || "?"} • {item.flight_number} • {item.route_label} • {item.reason}
                                </div>
                              ))}
                              {!flightOps?.airport_insights?.arrival_cancellations?.length && !flightOps?.airport_insights?.departure_cancellations?.length ? (
                                <div className="rail-service-item">No cancellations in the latest Heathrow snapshot.</div>
                              ) : null}
                              <div className="rail-service-item">
                                Next 60m risk • A {flightOps?.airport_insights?.next_hour?.arrivals?.flights || 0} flights, {flightOps?.airport_insights?.next_hour?.arrivals?.cancelled || 0} cancelled, {flightOps?.airport_insights?.next_hour?.arrivals?.severe_delays || 0} severe delays
                              </div>
                              <div className="rail-service-item">
                                Next 60m risk • D {flightOps?.airport_insights?.next_hour?.departures?.flights || 0} flights, {flightOps?.airport_insights?.next_hour?.departures?.cancelled || 0} cancelled, {flightOps?.airport_insights?.next_hour?.departures?.severe_delays || 0} severe delays
                              </div>
                              <div className="rail-service-item">
                                Last 60m ops • A severe delays {flightOps?.airport_insights?.last_hour?.arrivals?.severe_delays || 0} • D severe delays {flightOps?.airport_insights?.last_hour?.departures?.severe_delays || 0}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="corridor-card">
                        <div className="corridor-head">
                          <div>
                            <div className="corridor-title">Airport queues</div>
                            <div className="muted">Latest Heathrow queue summary</div>
                          </div>
                        </div>
                        <div className="rail-member-list">
                          <div className="rail-member">
                            <div className="rail-member-head">
                              <div>
                                <div className="rail-member-title">Current queue picture</div>
                              </div>
                            </div>
                            <div className="rail-service-list">
                              <div className="rail-service-item">
                                Security • T{flightOps?.airport_queues?.security?.worst_terminal || "?"} • {flightOps?.airport_queues?.security?.wait_message || "N/A"}
                                {flightOps?.airport_queues?.security?.max_wait_minutes != null ? ` • up to ${flightOps.airport_queues.security.max_wait_minutes}m` : ""}
                              </div>
                              <div className="rail-service-item">
                                Immigration EEA • T{flightOps?.airport_queues?.immigration_eea?.worst_terminal || "?"} • {flightOps?.airport_queues?.immigration_eea?.wait_message || "N/A"}
                                {flightOps?.airport_queues?.immigration_eea?.max_wait_minutes != null ? ` • up to ${flightOps.airport_queues.immigration_eea.max_wait_minutes}m` : ""}
                              </div>
                              <div className="rail-service-item">
                                Immigration Non-EEA • T{flightOps?.airport_queues?.immigration_noneea?.worst_terminal || "?"} • {flightOps?.airport_queues?.immigration_noneea?.wait_message || "N/A"}
                                {flightOps?.airport_queues?.immigration_noneea?.max_wait_minutes != null ? ` • up to ${flightOps.airport_queues.immigration_noneea.max_wait_minutes}m` : ""}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="corridor-card">
                        <div className="corridor-head">
                          <div>
                            <div className="corridor-title">Day mix</div>
                            <div className="muted">Yesterday / today / tomorrow airline and terminal mix</div>
                          </div>
                        </div>
                        <div className="airline-mix-grid">
                          {["yesterday", "today", "tomorrow"].map((bucketKey) => {
                            const bucket = flightOps?.day_mix?.[bucketKey];
                            return (
                              <div key={`lhr-${bucketKey}`} className="rail-member">
                                <div className="rail-member-head">
                                  <div>
                                    <div className="rail-member-title">{bucket?.label || bucketKey}</div>
                                  </div>
                                </div>
                                <div className="airline-mix-split">
                                  <div className="rail-service-list">
                                    <div className="rail-service-item"><strong>Arrivals</strong></div>
                                    {(bucket?.airlines?.arrivals || []).map((entry) => (
                                      <div key={`lhr-${bucketKey}-arr-${entry.airline_name}`} className="rail-service-item">
                                        {entry.airline_name} • {entry.flight_count}
                                      </div>
                                    ))}
                                    {!bucket?.airlines?.arrivals?.length ? <div className="rail-service-item">No arrival mix.</div> : null}
                                  </div>
                                  <div className="rail-service-list">
                                    <div className="rail-service-item"><strong>Departures</strong></div>
                                    {(bucket?.airlines?.departures || []).map((entry) => (
                                      <div key={`lhr-${bucketKey}-dep-${entry.airline_name}`} className="rail-service-item">
                                        {entry.airline_name} • {entry.flight_count}
                                      </div>
                                    ))}
                                    {!bucket?.airlines?.departures?.length ? <div className="rail-service-item">No departure mix.</div> : null}
                                  </div>
                                </div>
                                <div className="muted" style={{ marginTop: 8 }}>
                                  T2 A {bucket?.terminals?.["2"]?.arrivals || 0} • D {bucket?.terminals?.["2"]?.departures || 0}
                                  {" "}• T3 A {bucket?.terminals?.["3"]?.arrivals || 0} • D {bucket?.terminals?.["3"]?.departures || 0}
                                  {" "}• T4 A {bucket?.terminals?.["4"]?.arrivals || 0} • D {bucket?.terminals?.["4"]?.departures || 0}
                                  {" "}• T5 A {bucket?.terminals?.["5"]?.arrivals || 0} • D {bucket?.terminals?.["5"]?.departures || 0}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {(flightOps?.terminals || []).map((terminalCard) => (
                        <div key={terminalCard.terminal} className="corridor-card">
                          <div className="corridor-head">
                            <div>
                              <div className="corridor-title">Terminal {terminalCard.terminal}</div>
                              <div className="muted">
                                Today: A {flightOps?.terminal_mix?.today?.[terminalCard.terminal]?.arrivals || 0} • D {flightOps?.terminal_mix?.today?.[terminalCard.terminal]?.departures || 0}
                                {" "}• Tomorrow: A {flightOps?.terminal_mix?.tomorrow?.[terminalCard.terminal]?.arrivals || 0} • D {flightOps?.terminal_mix?.tomorrow?.[terminalCard.terminal]?.departures || 0}
                              </div>
                            </div>
                            <span className={pillClass(terminalCard?.terminal_pressure?.status_label)}>
                              {String(terminalCard?.terminal_pressure?.status_label || "unknown").toUpperCase()}
                            </span>
                          </div>
                          <div className="muted">{terminalCard?.terminal_pressure?.text || "Terminal pressure unavailable."}</div>
                          <div className="rail-member-list">
                            <div className="rail-member">
                              <div className="rail-member-head">
                                <div>
                                  <div className="rail-member-title">Terminal status</div>
                                  <div className="rail-member-meta">Latest queue snapshots</div>
                                </div>
                              </div>
                              <div className="rail-service-list">
                                <div className="rail-service-item">
                                  Security • {terminalCard?.queues?.security?.wait_message || "N/A"}
                                  {terminalCard?.queues?.security?.max_wait_minutes != null ? ` • up to ${terminalCard.queues.security.max_wait_minutes}m` : ""}
                                  {terminalCard?.queues?.security?.last_updated_raw ? ` • updated ${formatHm(terminalCard.queues.security.last_updated_raw)}` : ""}
                                  {terminalCard?.queue_trends?.security ? ` • trend ${queueTrendText(terminalCard.queue_trends.security)}` : ""}
                                </div>
                                <div className="rail-service-item">
                                  Immigration EEA • {terminalCard?.queues?.immigration_eea?.wait_message || "N/A"}
                                  {terminalCard?.queues?.immigration_eea?.max_wait_minutes != null ? ` • up to ${terminalCard.queues.immigration_eea.max_wait_minutes}m` : ""}
                                  {terminalCard?.queues?.immigration_eea?.last_updated_raw ? ` • updated ${formatHm(terminalCard.queues.immigration_eea.last_updated_raw)}` : ""}
                                  {terminalCard?.queue_trends?.immigration_eea ? ` • trend ${queueTrendText(terminalCard.queue_trends.immigration_eea)}` : ""}
                                </div>
                                <div className="rail-service-item">
                                  Immigration Non-EEA • {terminalCard?.queues?.immigration_noneea?.wait_message || "N/A"}
                                  {terminalCard?.queues?.immigration_noneea?.max_wait_minutes != null ? ` • up to ${terminalCard.queues.immigration_noneea.max_wait_minutes}m` : ""}
                                  {terminalCard?.queues?.immigration_noneea?.last_updated_raw ? ` • updated ${formatHm(terminalCard.queues.immigration_noneea.last_updated_raw)}` : ""}
                                  {terminalCard?.queue_trends?.immigration_noneea ? ` • trend ${queueTrendText(terminalCard.queue_trends.immigration_noneea)}` : ""}
                                </div>
                                {terminalCard?.queue_pressure_outlook?.arrivals ? (
                                  <div className="rail-service-item">
                                    Arrivals pressure • {terminalCard.queue_pressure_outlook.arrivals.text}
                                    {terminalCard?.queue_pressure_outlook?.arrivals?.current_wait_message ? ` • current ${terminalCard.queue_pressure_outlook.arrivals.current_wait_message}` : ""}
                                  </div>
                                ) : null}
                                {terminalCard?.queue_pressure_outlook?.departures ? (
                                  <div className="rail-service-item">
                                    Departures pressure • {terminalCard.queue_pressure_outlook.departures.text}
                                    {terminalCard?.queue_pressure_outlook?.departures?.current_wait_message ? ` • current ${terminalCard.queue_pressure_outlook.departures.current_wait_message}` : ""}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div className="rail-member">
                              <div className="rail-member-head">
                              <div>
                                <div className="rail-member-title">Arrivals</div>
                                <div className="rail-member-meta">
                                  On time {terminalCard?.summary?.arrivals?.on_time || 0} • Delayed {terminalCard?.summary?.arrivals?.delayed || 0} • Cancelled {terminalCard?.summary?.arrivals?.cancelled || 0}
                                </div>
                              </div>
                            </div>
                              <div className="muted">
                                Delay bands: &lt;15m {terminalCard?.delay_bands?.arrivals?.lt15 || 0} • 15-30m {terminalCard?.delay_bands?.arrivals?.m15to30 || 0} • 30-60m {terminalCard?.delay_bands?.arrivals?.m30to60 || 0} • &gt;60m {terminalCard?.delay_bands?.arrivals?.gt60 || 0}
                              </div>
                              {(terminalCard?.delay_examples?.arrivals?.length || terminalCard?.cancellation_examples?.arrivals?.length) ? (
                                <div className="rail-service-list">
                                  {(terminalCard?.delay_examples?.arrivals || []).map((item) => (
                                    <div key={`t${terminalCard.terminal}-arr-delay-${item.flight_number}`} className="rail-service-item">
                                      Delay • {item.flight_number} • {item.route_label} • +{item.delay_mins}m
                                    </div>
                                  ))}
                                  {(terminalCard?.cancellation_examples?.arrivals || []).map((item) => (
                                    <div key={`t${terminalCard.terminal}-arr-cancel-${item.flight_number}`} className="rail-service-item">
                                      Cancelled • {item.flight_number} • {item.route_label}
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                              <div className="muted">
                                Last 60m arrivals {terminalCard?.last_hour?.arrivals?.flights || 0} • cancelled {terminalCard?.last_hour?.arrivals?.cancelled || 0} • severe delays {terminalCard?.last_hour?.arrivals?.severe_delays || 0}
                              </div>
                              <div className="muted">
                                Next 60m arrivals {terminalCard?.next_hour?.arrivals?.flights || 0}
                                {" "}• risk cancelled {terminalCard?.next_hour?.arrivals?.cancelled || 0} • severe delays {terminalCard?.next_hour?.arrivals?.severe_delays || 0}
                                {terminalCard?.busiest_hour?.arrivals?.block_label ? ` • Busiest hour today ${terminalCard.busiest_hour.arrivals.block_label} (${terminalCard.busiest_hour.arrivals.flight_count})` : ""}
                              </div>
                              <div className="rail-service-list">
                                {(terminalCard?.arrivals || []).map((flight) => (
                                  <div key={flight.id} className="rail-service-item">
                                    {formatHm(flight.scheduled_utc)} • {flight.flight_number} • {flight.airline_name || "Airline"} • {flight.origin_airport_iata || flight.origin_city || "Inbound"}
                                    {flight.stand_number ? ` • Stand ${flight.stand_number}` : ""}
                                    {flight.status_text ? ` • ${flight.status_text}` : ""}
                                    {flight.delay_mins != null && flight.delay_mins > 0 ? ` • +${flight.delay_mins}m` : ""}
                                    {lhrFlightTimeSummary(flight) ? ` • ${lhrFlightTimeSummary(flight)}` : ""}
                                  </div>
                                ))}
                                {!terminalCard?.arrivals?.length ? <div className="rail-service-item">No current arrivals listed for this terminal.</div> : null}
                              </div>
                            </div>
                            <div className="rail-member">
                              <div className="rail-member-head">
                                <div>
                                  <div className="rail-member-title">Departures</div>
                                  <div className="rail-member-meta">
                                    On time {terminalCard?.summary?.departures?.on_time || 0} • Delayed {terminalCard?.summary?.departures?.delayed || 0} • Cancelled {terminalCard?.summary?.departures?.cancelled || 0}
                                  </div>
                                </div>
                              </div>
                              <div className="muted">
                                Delay bands: &lt;15m {terminalCard?.delay_bands?.departures?.lt15 || 0} • 15-30m {terminalCard?.delay_bands?.departures?.m15to30 || 0} • 30-60m {terminalCard?.delay_bands?.departures?.m30to60 || 0} • &gt;60m {terminalCard?.delay_bands?.departures?.gt60 || 0}
                              </div>
                              {(terminalCard?.delay_examples?.departures?.length || terminalCard?.cancellation_examples?.departures?.length) ? (
                                <div className="rail-service-list">
                                  {(terminalCard?.delay_examples?.departures || []).map((item) => (
                                    <div key={`t${terminalCard.terminal}-dep-delay-${item.flight_number}`} className="rail-service-item">
                                      Delay • {item.flight_number} • {item.route_label} • +{item.delay_mins}m
                                    </div>
                                  ))}
                                  {(terminalCard?.cancellation_examples?.departures || []).map((item) => (
                                    <div key={`t${terminalCard.terminal}-dep-cancel-${item.flight_number}`} className="rail-service-item">
                                      Cancelled • {item.flight_number} • {item.route_label}
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                              <div className="muted">
                                Last 60m departures {terminalCard?.last_hour?.departures?.flights || 0} • cancelled {terminalCard?.last_hour?.departures?.cancelled || 0} • severe delays {terminalCard?.last_hour?.departures?.severe_delays || 0}
                              </div>
                              <div className="muted">
                                Next 60m departures {terminalCard?.next_hour?.departures?.flights || 0}
                                {" "}• risk cancelled {terminalCard?.next_hour?.departures?.cancelled || 0} • severe delays {terminalCard?.next_hour?.departures?.severe_delays || 0}
                                {terminalCard?.busiest_hour?.departures?.block_label ? ` • Busiest hour today ${terminalCard.busiest_hour.departures.block_label} (${terminalCard.busiest_hour.departures.flight_count})` : ""}
                              </div>
                              <div className="rail-service-list">
                                {(terminalCard?.departures || []).map((flight) => (
                                  <div key={flight.id} className="rail-service-item">
                                    {formatHm(flight.scheduled_utc)} • {flight.flight_number} • {flight.airline_name || "Airline"} • {flight.destination_airport_iata || flight.destination_city || "Outbound"}
                                    {flight.gate_number ? ` • Gate ${flight.gate_number}` : ""}
                                    {flight.checkin_zone ? ` • Zone ${flight.checkin_zone}` : ""}
                                    {flight.status_text ? ` • ${flight.status_text}` : ""}
                                    {flight.delay_mins != null && flight.delay_mins > 0 ? ` • +${flight.delay_mins}m` : ""}
                                    {lhrFlightTimeSummary(flight) ? ` • ${lhrFlightTimeSummary(flight)}` : ""}
                                  </div>
                                ))}
                                {!terminalCard?.departures?.length ? <div className="rail-service-item">No current departures listed for this terminal.</div> : null}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="muted">No Heathrow flight snapshot available.</div>
                )}
              </div>
            </details>
          ) : null}

          {airportCode === "LHR" && flightOps?.tracking_review ? (
            <details className="collapsible">
              <summary>
                <span className="collapsible-summary-row">
                  <span>Flight Tracking Review (LHR)</span>
                  <span className="collapsible-summary-meta">
                    {flightOps.tracking_review.snapshot_date
                      ? `Snapshot ${flightOps.tracking_review.snapshot_date}`
                      : "Tracking review"} •{" "}
                    {flightOps.tracking_review.updated_at
                      ? `Refreshed ${formatHm(flightOps.tracking_review.updated_at)}`
                      : "Live diagnostic layer"}
                  </span>
                </span>
              </summary>
              <div className="table-card" style={{ marginBottom: 16 }}>
                {(() => {
                  const tracking = flightOps.tracking_review || {};
                  const operationalNow = Array.isArray(tracking.operational_now) ? tracking.operational_now : [];
                  const lateEnRoute = Array.isArray(tracking.late_departures_en_route) ? tracking.late_departures_en_route : [];
                  const trackingBacklog = Array.isArray(tracking.historical_tracking_backed) ? tracking.historical_tracking_backed : [];
                  const statusOnlyBacklog = Array.isArray(tracking.historical_status_only) ? tracking.historical_status_only : [];
                  const consistency = Array.isArray(tracking.latest_fresh_status_consistency) ? tracking.latest_fresh_status_consistency : [];
                  const trackedNow = [...operationalNow, ...lateEnRoute];
                  const towardHeathrow = trackedNow.filter((item) => trackingDirectionBucket(item) === "toward");
                  const awayFromHeathrow = trackedNow.filter((item) => trackingDirectionBucket(item) === "away");
                  const actionableNow = operationalNow.filter((item) => trackingDirectionBucket(item) === "unknown");
                  const delayedSignalNow = trackedNow.filter((item) => String(item?.timing_signal || "").toLowerCase() === "delayed_signal");
                  const consistentNow = trackedNow.filter((item) => String(item?.status_consistency || "").toLowerCase() === "consistent");
                  const insufficientNow = trackedNow.filter((item) => String(item?.status_consistency || "").toLowerCase() === "insufficient_tracking");
                  const consistencyLead = consistency[0] || null;
                  const consistencyTail = consistency.slice(1, 3);
                  const liveTrackingCardCount =
                    (actionableNow.length ? 1 : 0) +
                    (lateEnRoute.length ? 1 : 0) +
                    (towardHeathrow.length ? 1 : 0);
                  return (
                    <>
                <div className="summary-grid" style={{ marginTop: 0 }}>
                  <div className="summary-card">
                    <div className="summary-label">Tracked live now</div>
                    <div className="summary-value">{trackedNow.length}</div>
                    <div className="muted">{`${towardHeathrow.length} toward Heathrow • ${awayFromHeathrow.length} away from Heathrow`}</div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Late arr en route</div>
                    <div className="summary-value">{towardHeathrow.length}</div>
                    <div className="muted">Inbound flights still showing live delay toward Heathrow</div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Late dep en route</div>
                    <div className="summary-value">{lateEnRoute.length}</div>
                    <div className="muted">Left Heathrow late and now already airborne</div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Delayed signals live</div>
                    <div className="summary-value">
                      {delayedSignalNow.length}
                    </div>
                    <div className="muted">
                      {`${consistentNow.length} consistent • ${insufficientNow.length} insufficient tracking`}
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Status consistency</div>
                    <div className="summary-value">
                      {consistencyLead ? `${humanizeToken(consistencyLead.key)} ${consistencyLead.flights}` : "N/A"}
                    </div>
                    <div className="muted">
                      {consistencyTail.length
                        ? consistencyTail.map((entry) => `${humanizeToken(entry.key)} ${entry.flights}`).join(" • ")
                        : "No latest fresh consistency summary"}
                    </div>
                  </div>
                </div>

                <div
                  className="corridor-grid"
                  style={{
                    marginTop: 12,
                    gridTemplateColumns:
                      liveTrackingCardCount <= 1
                        ? "minmax(320px, 980px)"
                        : "repeat(2, minmax(320px, 1fr))",
                  }}
                >
                  {actionableNow.length ? (
                  <div className="corridor-card">
                    <div className="corridor-head">
                      <div>
                        <div className="corridor-title">Operational now</div>
                        <div className="muted">Actionable late flights still near departure or arrival decision points</div>
                      </div>
                    </div>
                    <div className="rail-service-list">
                      {actionableNow.map((item) => (
                        <div key={`trk-now-${item.heathrow_flight_key}`} className="rail-service-item">
                          <div>
                            <strong>{item.iata_flight_identifier || "Flight"}</strong> • {item.route_label} • {item.flight_phase || "N/A"} • {formatTrackingDelta(item.delay_reference_mins)}
                          </div>
                          <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <span className={trackingSignalPillClass(item.timing_signal)}>{humanizeToken(item.timing_signal)}</span>
                            <span className={trackingConsistencyPillClass(item.status_consistency)}>{humanizeToken(item.status_consistency)}</span>
                          </div>
                          <div className="muted" style={{ marginTop: 4 }}>
                            {humanizeToken(item.timing_signal_basis)} • {item.heathrow_status_code || "N/A"} {item.heathrow_status_message || ""} • {item.coverage_quality || "N/A"} {item.coverage_score != null ? `(${item.coverage_score})` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  ) : null}

                  {lateEnRoute.length ? (
                  <div className="corridor-card">
                    <div className="corridor-head">
                      <div>
                        <div className="corridor-title">Late departures en route</div>
                        <div className="muted">Flights that left Heathrow late and are already beyond the departure window</div>
                      </div>
                    </div>
                    <div className="rail-service-list">
                      {lateEnRoute.map((item) => (
                        <div key={`trk-enroute-${item.heathrow_flight_key}`} className="rail-service-item">
                          <div>
                            <strong>{item.iata_flight_identifier || "Flight"}</strong> • {item.route_label} • {item.flight_phase || "N/A"} • {formatTrackingDelta(item.delay_reference_mins)}
                          </div>
                          <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <span className={trackingSignalPillClass(item.timing_signal)}>{humanizeToken(item.timing_signal)}</span>
                            <span className={trackingConsistencyPillClass(item.status_consistency)}>{humanizeToken(item.status_consistency)}</span>
                          </div>
                          <div className="muted" style={{ marginTop: 4 }}>
                            {humanizeToken(item.timing_signal_basis)} • {item.heathrow_status_code || "N/A"} {item.heathrow_status_message || ""} • observed {item.last_observed_at ? formatHm(item.last_observed_at) : "N/A"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  ) : null}

                  {towardHeathrow.length ? (
                  <div className="corridor-card">
                    <div className="corridor-head">
                      <div>
                        <div className="corridor-title">Late arrivals en route</div>
                        <div className="muted">Inbound flights still showing live delay while approaching Heathrow</div>
                      </div>
                    </div>
                    <div className="rail-service-list">
                      {towardHeathrow.map((item) => (
                        <div key={`trk-toward-${item.heathrow_flight_key}`} className="rail-service-item">
                          <div>
                            <strong>{item.iata_flight_identifier || "Flight"}</strong> • {item.route_label} • {item.flight_phase || "N/A"} • {formatTrackingDelta(item.delay_reference_mins)}
                          </div>
                          <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <span className={trackingSignalPillClass(item.timing_signal)}>{humanizeToken(item.timing_signal)}</span>
                            <span className={trackingConsistencyPillClass(item.status_consistency)}>{humanizeToken(item.status_consistency)}</span>
                          </div>
                          <div className="muted" style={{ marginTop: 4 }}>
                            {humanizeToken(item.timing_signal_basis)} • {item.heathrow_status_code || "N/A"} {item.heathrow_status_message || ""} • observed {item.last_observed_at ? formatHm(item.last_observed_at) : "N/A"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  ) : null}
                </div>

                <details className="detail-collapsible" style={{ marginTop: 14 }}>
                  <summary>{`View historical backlog details (${trackingBacklog.length + statusOnlyBacklog.length})`}</summary>
                  <div className="corridor-grid" style={{ marginTop: 8 }}>
                    <div className="corridor-card">
                      <div className="corridor-head">
                        <div>
                          <div className="corridor-title">Tracking-backed backlog</div>
                          <div className="muted">Historical delayed rows supported by stronger tracking evidence</div>
                        </div>
                      </div>
                      <div className="rail-service-list">
                      {trackingBacklog.map((item) => (
                        <div key={`trk-hist-${item.heathrow_flight_key}`} className="rail-service-item">
                          <div>
                            <strong>{item.snapshot_date || "Date"}</strong> • {item.iata_flight_identifier || "Flight"} • {item.route_label} • {formatTrackingDelta(item.delay_reference_mins)}
                          </div>
                          <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <span className={trackingSignalPillClass(item.timing_signal)}>{humanizeToken(item.timing_signal)}</span>
                            <span className={trackingConsistencyPillClass(item.status_consistency)}>{humanizeToken(item.status_consistency)}</span>
                          </div>
                          <div className="muted" style={{ marginTop: 4 }}>
                            {item.flight_phase || "N/A"} • {humanizeToken(item.timing_signal_basis)} • {item.heathrow_status_code || "N/A"} {item.heathrow_status_message || ""}
                          </div>
                        </div>
                      ))}
                      {!trackingBacklog.length ? (
                        <div className="rail-service-item">No tracking-backed delayed backlog rows returned.</div>
                      ) : null}
                    </div>
                    </div>
                    <div className="corridor-card">
                      <div className="corridor-head">
                        <div>
                          <div className="corridor-title">Status-only backlog</div>
                          <div className="muted">Older inferred rows, kept for later analysis rather than live operations</div>
                        </div>
                      </div>
                      <div className="rail-service-list">
                      {statusOnlyBacklog.map((item) => (
                        <div key={`trk-status-${item.heathrow_flight_key}`} className="rail-service-item">
                          <div>
                            <strong>{item.snapshot_date || "Date"}</strong> • {item.iata_flight_identifier || "Flight"} • {item.route_label} • {formatTrackingDelta(item.delay_reference_mins)}
                          </div>
                          <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <span className={trackingSignalPillClass(item.timing_signal)}>{humanizeToken(item.timing_signal)}</span>
                            <span className={trackingConsistencyPillClass(item.status_consistency)}>{humanizeToken(item.status_consistency)}</span>
                          </div>
                          <div className="muted" style={{ marginTop: 4 }}>
                            {item.flight_phase || "N/A"} • {humanizeToken(item.timing_signal_basis)} • {item.heathrow_status_code || "N/A"} {item.heathrow_status_message || ""}
                          </div>
                        </div>
                      ))}
                      {!statusOnlyBacklog.length ? (
                        <div className="rail-service-item">No status-only delayed backlog rows returned.</div>
                      ) : null}
                    </div>
                  </div>
                  </div>
                </details>
                    </>
                  );
                })()}
              </div>
            </details>
          ) : null}

          <div className="monitoring-tables-section">
          <h2 className="section-title">Verified Current Flight Checks ({verifiedCurrentRows.length})</h2>
          <div className="table-card">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Journey</th>
                  <th>Overall</th>
                  <th>Risk / Action</th>
                  <th>Road</th>
                  <th>Rail</th>
                  <th>TfL</th>
                  <th>Weather</th>
                  <th>Flight</th>
                  <th>Terminal</th>
                  <th>Next Check</th>
                  <th>Next Notify</th>
                  <th>Phase / Progress</th>
                  <th>Targets</th>
                  <th>Details</th>
                  <th>Last Change</th>
                </tr>
              </thead>
              <tbody>
                {currentTable}
                {!currentTable.length && (
                  <tr>
                    <td colSpan={16}>
                      <div className="muted">No current flight checks for the selected filters.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <details className="collapsible" open={unverifiedOpen} onToggle={(e) => setUnverifiedOpen(e.currentTarget.open)}>
            <summary>Unverified journeys ({visibleUnverifiedCurrentRows.length})</summary>
            <div className="table-card">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Journey</th>
                    <th>Overall</th>
                    <th>Cache</th>
                    <th>Next Check</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {unverifiedTable}
                  {!unverifiedTable.length && (
                    <tr>
                      <td colSpan={6}>
                        <div className="muted">No unverified journeys for the selected filters.</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </details>

          <details className="collapsible" open={pastOpen} onToggle={(e) => setPastOpen(e.currentTarget.open)}>
            <summary>Verified past / post-flight checks ({verifiedPastRows.length})</summary>
            <div className="table-card">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Journey</th>
                    <th>Overall</th>
                    <th>Risk / Action</th>
                    <th>Road</th>
                    <th>Rail</th>
                    <th>TfL</th>
                    <th>Weather</th>
                    <th>Flight</th>
                    <th>Terminal</th>
                    <th>Next Check</th>
                    <th>Next Notify</th>
                    <th>Phase / Progress</th>
                    <th>Targets</th>
                    <th>Details</th>
                    <th>Last Change</th>
                  </tr>
                </thead>
                <tbody>
                  {pastTable}
                  {!pastTable.length && (
                    <tr>
                      <td colSpan={16}>
                        <div className="muted">No post-flight checks for the selected filters.</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </details>
          </div>
          </div>
        </>
      )}
    </div>
  );
}
